import React, { useState, useEffect, useRef, useMemo, Suspense } from "react";
import { createPortal } from "react-dom";
import "./App.css";
import { useSound, BGM_TRACKS, setBgmTrack, getBgmTrack } from "./hooks/useSound";
import { RELEASE_NOTES } from "./releaseNotes";
import { auth, db, storage } from "./firebase";
import { collection, getDocs, addDoc, serverTimestamp, query, where, doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { useUserData } from "./useUserData";
import { useProgress } from "./useProgress";
import { addToStat, StatDoc } from "./scoreStats";
import MindMap from "./MindMap";
import { exportQuestions } from "./utils/export";
import { speak, stopSpeaking, ttsSupported, TTS_RATES, LS_TTS_RATE, LS_TTS_AUTO, getRate } from "./utils/tts";
import { pickBalanced } from "./utils/pickQuestions";
import { parseBatch, planBatch, isValid, mediaWarnings, FIELD_LABEL, Update as BatchUpdate } from "./utils/batchImport";
import { makeSubjectNameOf, ALL_SUB_ID, WRONGS_SUB_ID } from "./subjectRef";
import type { SubjectGroup } from "./GroupEditor";
import { toast, confirmDialog, promptDialog } from "./feedback";
import { explanationOf, optionsAreShuffled } from "./explainRefs";
import { announceUpdate, hasPendingUpdate, StaleChunkNotice } from "./updateBanner";
import { openDict } from "./DictHost";
import { openImage } from "./ImageViewer";
import { contentHash, isVerified, verifiedFields } from "./verify";
import RichText, { hasTable, tableToSpeech } from "./RichText";
import { dayKey, setStudyUid, logStudy } from "./activityLog";
import { AnswerMap, QRef as AnsRef, loadAnswers, applyAnswer, saveAnswer, seedFromWrongs } from "./answerLog";
import { seriesColor } from "./palette";
import { Daily, GOAL_OPTIONS, DEFAULT_GOAL, TASK_SIZE, dateKey, loadDaily, bumpDaily, saveGoal, saveTaskDone, pickDailyTask } from "./daily";
import { pctClr as pctClr_, srCardId, sm2Update, shuffle as shuffle_, SRCard as SRCardType } from "./utils";
import {
  onAuthStateChanged, User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  signOut,
  sendPasswordResetEmail,
} from "firebase/auth";

// ── 需要時才下載的頁面（後台、排行榜、學習分析、筆順）：不佔首次載入的大小 ──
// 部署新版後舊的檔名會消失，載入失敗時不自動重新整理（會中斷作答），改成顯示「有新版本，請更新」並跳出更新提示
function lazyPage<T extends React.ComponentType<any>>(loader: () => Promise<{ default: T }>) {
  const C = React.lazy(() => loader().catch(() => {
    if (!hasPendingUpdate()) announceUpdate(() => window.location.reload());
    return { default: StaleChunkNotice as unknown as T };
  })) as React.ComponentType<any>;
  return (props: React.ComponentProps<T>) => (
    <Suspense fallback={<div className="loading-spinner" style={{margin:'40px auto'}} />}><C {...props} /></Suspense>
  );
}
const SubjectEditor  = lazyPage(() => import("./SubjectEditor"));
const GroupEditor    = lazyPage(() => import("./GroupEditor"));
const Analysis       = lazyPage(() => import("./Analysis"));
const Leaderboard    = lazyPage(() => import("./Leaderboard"));
const StorageCleanup = lazyPage(() => import("./StorageCleanup"));
const AdminNotes     = lazyPage(() => import("./AdminNotes"));
const ReportsPanel   = lazyPage(() => import("./ReportsPanel"));
const StrokeOrder    = lazyPage(() => import("./StrokeOrder"));

// 載入中的版面輪廓（比轉圈圈更不覺得久）
const SkeletonList = ({ n = 6, h = 56 }: { n?: number; h?: number }) => (
  <div aria-busy="true" aria-label="載入中" style={{display:'flex',flexDirection:'column',gap:10}}>
    {Array.from({ length: n }, (_, i) => <div key={i} className="skeleton" style={{height:h,borderRadius:14}} />)}
  </div>
);

// ── Types ─────────────────────────────────────────────────────────────────────
type Subject  = {id:string,n:string,i:string,c:string,b:string,g?:string,lb?:string,o?:number,ga?:boolean};  // ga：開放訪客使用（預設不開放）  // g：群組 ID、lb：首頁卡片短名、o：群組內順序
type QRef     = {s:string, t:string, id:string};  // 科目 ID、主題、題目文件 ID
type QItem    = {t:string,u?:string,q?:string,o?:string[],a?:number,e?:string,p?:string[][],img?:string,audio?:string,passage?:string,tag?:string,year?:number,num?:number,
                 ref?:QRef, noImgNeeded?:boolean,
                 verified?:boolean, verifiedHash?:string, verifiedAt?:string, verifiedBy?:string};  // 已驗證旗標（內容雜湊對不上即失效，見 verify.ts）  // ref：題目在 Firestore 的位置（錯題本、回報以此關聯）；noImgNeeded：後台缺圖檢查略過
type NoteItem =
  | {t:'article'; title:string; points:string[]; imgs?:string[]; u?:string; _id?:string}
  | {t:'card';    front:string; back:string;     img?:string;    u?:string; _id?:string}
  | {t:'map';     title:string; text:string;                     u?:string; _id?:string};   // 心智圖：text 為縮排大綱

// Admin: fahsiang@gmail.com — replace with actual Firebase UID from Firebase Console
const ADMIN_UIDS: string[] = ['KrzoMLH711c74gkgNF5JT80amvL2'];

const APP_VERSION = '1.9.1';
// 建置資訊（每次部署自動產生，見 scripts/write-build-info.mjs）；開發模式沒有時顯示 dev
const BUILD_INFO = process.env.REACT_APP_BUILD_TIME ? `${process.env.REACT_APP_BUILD_TIME} · ${process.env.REACT_APP_COMMIT}` : 'dev';

const POS_MAP: Record<string,string> = {
  'n.':'noun','v.':'verb','adj.':'adjective','adv.':'adverb',
  'prep.':'preposition','conj.':'conjunction','pron.':'pronoun','interj.':'interjection',
};
function linkifyEnglish(text: string): React.ReactNode {
  // 含撇號的字（don't、o'clock、Mother's）視為一個字，不從撇號拆開
  const regex = /adj\.|adv\.|prep\.|conj\.|pron\.|interj\.|[nv]\.|[A-Za-z]+(?:['’][A-Za-z]+)+|[A-Za-z]{2,}/g;
  const result: React.ReactNode[] = [];
  let last = 0;
  let hasMatch = false;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    hasMatch = true;
    if (m.index > last) result.push(text.slice(last, m.index));
    const w = m[0];
    const lookup = POS_MAP[w.toLowerCase()] ?? w.toLowerCase();
    result.push(
      <a key={m.index}
         href={`https://dictionary.cambridge.org/dictionary/english-chinese-traditional/${encodeURIComponent(lookup)}`}
         target="_blank" rel="noopener noreferrer"
         onClick={(e: React.MouseEvent) => {
           e.stopPropagation();
           // 一般英文字在 App 內查字典；詞性縮寫（n.、v.）與 Ctrl／⌘ 點擊維持開啟劍橋字典
           if (POS_MAP[w.toLowerCase()] || e.ctrlKey || e.metaKey || e.shiftKey) return;
           e.preventDefault(); openDict(w);
         }}
         style={{color:'var(--accent)',textDecoration:'underline',textUnderlineOffset:3,cursor:'pointer'}}>
        {w}
      </a>
    );
    last = m.index + w.length;
  }
  if (last < text.length) result.push(text.slice(last));
  return hasMatch ? result : text;
}
// 重點整理的行內 Markdown：**粗體**；其餘文字照舊加上英文查字典連結
function inlineMd(text: string): React.ReactNode {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  if (parts.length === 1) return linkifyEnglish(text);
  return parts.map((p, i) => !p ? null : i % 2
    ? <strong key={i} style={{fontWeight:800}}>{linkifyEnglish(p)}</strong>
    : <React.Fragment key={i}>{linkifyEnglish(p)}</React.Fragment>);
}
// 行首的清單符號（- 、* ）：外層已是清單，不重複顯示
const stripBullet = (s: string) => s.replace(/^\s*[-*•]\s+/, '');
// 朗讀用：去掉 Markdown 符號
const plainMd = (s: string) => stripBullet(s).replace(/\*\*(.+?)\*\*/g, '$1');

interface ThemeConfig { id: string; name: string; accent: string; dark: boolean }
const THEMES: ThemeConfig[] = [
  { id:'ocean',    name:'🌊 海洋藍', accent:'#2563eb', dark:false },
  { id:'night',    name:'🌙 夜深藍', accent:'#3b82f6', dark:true  },
  { id:'forest',   name:'🌿 森林綠', accent:'#16a34a', dark:false },
  { id:'carbon',   name:'🌲 碳黑',   accent:'#059669', dark:true  },
  { id:'grape',    name:'🍇 紫羅蘭', accent:'#7c3aed', dark:false },
  { id:'ember',    name:'🔥 活力橘', accent:'#ea580c', dark:false },
  { id:'dawn',     name:'☀️ 暖陽',   accent:'#b45309', dark:false },
  { id:'midnight', name:'🌃 午夜',   accent:'#6366f1', dark:true  },
];

// ── Constants ─────────────────────────────────────────────────────────────────
// 所有科目（含學習卡）都存在 Firestore customSubjects，可於後台編輯。
// 主題以 ID 關聯（題目、講義路徑與成績都存主題 ID），名稱存於 topicNames，只用於顯示。
// 作答與解說時間（可在個人設定調整）；秒數為 0 表示不限時／手動
type Timing = { normal:number; gsat:number; listen:number; correctDelay:number; wrongDelay:number };
const DEFAULT_TIMING: Timing = {
  normal: 80,        // 一般題
  gsat: 80,          // 會考歷屆題
  listen: 80,        // 聽力題
  correctDelay: 3,   // 答對後自動跳下一題；0 = 手動按繼續
  wrongDelay: 0,     // 答錯／逾時後停留看解說，按「繼續」才跳下一題
};
// 作答時間的選項（0 = 不限時）
const TIMING_OPTIONS = [40, 60, 80, 100, 120, 180, 0];
// 看解說停留時間的選項（0 = 手動按繼續）
const DELAY_OPTIONS = [3, 5, 10, 15, 20, 30, 0];
// 舊版把停留時間和作答時間設成同一個秒數（例如 80 秒），等於永遠要手動按繼續：改回新的預設
const migrateTiming = (t: Timing): Timing => (t.correctDelay > 30 || t.wrongDelay > 30)
  ? { ...t, correctDelay: DEFAULT_TIMING.correctDelay, wrongDelay: DEFAULT_TIMING.wrongDelay } : t;
// v2：預設改為 80 秒；舊版曾把舊預設值寫入 gsat_timing，改用新 key 才會套用新預設
const LS_TIMING = 'gsat_timing_v2';
const TIMER_SEC  = DEFAULT_TIMING.normal;
// 首頁、主題清單的答對率：作答次數少於這個數就不顯示（避免做一兩題就 100%）
const PCT_MIN_TRIED = 10;
// 後台可調整的 App 設定（Firestore config/app）；未設定的欄位用這裡的預設值
type AppConfig = {
  wrongClearStreak: number;  // 錯題連續答對幾次才移出
  quizSize: number;          // 每次測驗題數（歷屆考題整份出，不受限）
  boardMinQ: number;         // 排行榜上榜最低題數
  boardTopN: number;         // 排行榜顯示名次數
  unitSize: number;          // 單元自動切分：每個子單元題數
  timingDefault: number;     // 作答與停留時間預設秒數（0 = 不限時／手動）；使用者未自訂時套用
  topicFilterMin: number;    // 主題數超過此值才顯示篩選列
  analysisMinTried: number;  // 錯題分析：主題至少做過幾題才列入排名
  analysisTopN: number;      // 錯題分析：列出錯題率最高的前幾個主題
};
const DEFAULT_CONFIG: AppConfig = {
  wrongClearStreak: 3, quizSize: 20, boardMinQ: 15, boardTopN: 20, unitSize: 15,
  timingDefault: 80, topicFilterMin: 10, analysisMinTried: 5, analysisTopN: 5,
};

// 概念 → emoji 對照（單元卡片圖示）
const CONCEPT_EMOJI: Record<string,string> = {
  '力與運動':'🏃','能量與功':'⚙️','波動與光':'🌈','電與磁':'⚡','流體與壓力':'🌊',
  '細胞構造':'🔬','生理系統':'🫀','遺傳演化':'🧬','生態環境':'🌿',
  '物質性質':'🧪','化學反應':'💥','酸鹼鹽':'⚗️','有機化學':'🛢️',
  '天文':'🔭','大氣':'☁️','地質':'⛰️','海洋':'🌊',
  '一次方程式':'🟰','多項式運算':'✖️','指數對數':'📈','數列函數':'🔢',
  '三角形':'📐','圓與多邊形':'⭕','面積體積':'📦','座標幾何':'🗺️',
  '平均數中位數':'📊','機率':'🎲','圖表判讀':'📉','資料分析':'🔍',
  '日常生活':'🏠','學校學習':'🏫','情感性格':'😊','自然環境':'🌳','動作行為':'🏃',
  '時態':'⏰','詞類':'🔤','句型結構':'🧱','介系詞連接詞':'🔗',
  '主旨理解':'🎯','細節擷取':'🔎','推論判斷':'🧠','字彙語境':'📖',
  '字詞解釋':'📝','句義理解':'💬','文意判讀':'📜','文學常識':'🏛️',
  '詩意理解':'🌸','修辭技巧':'🖋️','作者背景':'👤','名句填空':'✏️',
  '譬喻類':'🎭','轉化擬人':'🦋','誇飾映襯':'🎪','排比層遞':'🪜','其他修辭':'🎨',
  '字音辨識':'🔊','字形辨識':'✍️','形近字':'👀','字義辨析':'💡',
  '成語意義':'💡','成語運用':'🎯','成語典故':'📚','誤用辨析':'⚖️',
  '台灣地理':'🏝️','世界地理':'🌍','地形氣候':'🌦️','產業人口':'🏭',
  '台灣史':'🏝️','中國史':'🏯','世界史':'🌍','近現代史':'📰',
  '法律':'⚖️','政治制度':'🏛️','經濟':'💰','社會文化':'🎎',
  '綜合練習':'📚',
};
const CN_NUM = ['一','二','三','四','五','六','七','八','九','十'];
const LS_NICK    = 'gsat_nickname';
const LS_PROG    = 'gsat_progress';
const soundRow: React.CSSProperties = {display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,fontSize:14,color:'var(--text)',marginBottom:10};
// 朗讀按鈕：裝置不支援語音合成時不顯示；點擊不會觸發外層（例如閃卡翻面）
const SpeakBtn = ({ active, onClick, size = 16 }: { active: boolean; onClick: () => void; size?: number }) =>
  ttsSupported() ? (
    <button onClick={e => { e.stopPropagation(); onClick(); }} aria-label={active ? '停止朗讀' : '朗讀'} title={active ? '停止朗讀' : '朗讀'}
      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: size, padding: '2px 4px', lineHeight: 1, flexShrink: 0, opacity: active ? 1 : 0.75 }}>
      {active ? '⏹' : '🔊'}
    </button>
  ) : null;
const topBtn: React.CSSProperties = {background:'none',fontSize:15,padding:'6px 7px',fontWeight:700,color:'var(--muted)',borderRadius:8};
const pillBtn: React.CSSProperties = {flex:'1 1 72px',background:'var(--card)',border:'1px solid var(--border)',borderRadius:10,padding:'10px 4px',fontSize:13,fontWeight:700,color:'var(--text)',whiteSpace:'nowrap'};
const CUSTOM_SUBS_CACHE_KEY = 'gsat_custom_subs_v7';  // v7：加入各主題題數 counts
const CUSTOM_SUBS_CACHE_TTL = 5 * 60 * 1000;
const pctClr = pctClr_;
const SR_KEY = 'gsat_sr_v1';
const DAILY_TASK_LABEL = '今日任務';
type SRCard = SRCardType;

// ── Helpers ───────────────────────────────────────────────────────────────────
const shuffle = shuffle_;
const lsGet = (k:string, fallback:any) => { try { return JSON.parse(localStorage.getItem(k)||'null')??fallback; } catch { return fallback; } };
const lsSet = (k:string, v:any) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };

// Fetch notes from Firestore
async function fetchNotes(sid:string, topic:string, unit?:string|null): Promise<NoteItem[]> {
  try {
    const snap = await getDocs(collection(db,'notes',sid,'topics',topic,'items'));
    const all = snap.docs.map(d => ({...d.data(), _id: d.id} as NoteItem));
    return unit ? all.filter(n => n.u === unit) : all;
  } catch { return []; }
}

// Firestore 題目文件 → QItem，附上題目位置 ref
const toQItem = (d: {id:string, data:()=>any}, s:string, t:string): QItem => ({...d.data(), ref:{s, t, id:d.id}});

// 題目讀取：同一主題讀過就留在記憶體（最多 10 分鐘），同時間重複的請求共用同一次讀取
const TOPIC_TTL = 10 * 60 * 1000;
const RANDOM_TOPICS = 15;   // 「隨機」跨全部科目時，每次隨機抽幾個主題來出題（不必把整個題庫讀下來）
const topicCache = new Map<string, { ts: number; p: Promise<QItem[]> }>();
function loadTopic(s: string, t: string): Promise<QItem[]> {
  const key = `${s}/${t}`;
  const hit = topicCache.get(key);
  if (hit && Date.now() - hit.ts < TOPIC_TTL) return hit.p;
  const p = getDocs(collection(db, 'questions', s, 'topics', t, 'items')).then(snap => snap.docs.map(d => toQItem(d, s, t)));
  topicCache.set(key, { ts: Date.now(), p });
  p.catch(() => topicCache.delete(key));   // 失敗的不留，下次重試
  return p;
}
// 後台改了題目後清掉該科目的暫存
const dropSubjectTopics = (s: string) => { Array.from(topicCache.keys()).filter(k => k.startsWith(`${s}/`)).forEach(k => topicCache.delete(k)); };

// Fetch questions from Firestore
async function fetchQs(sid:string, topic:string, topicsMap: Record<string,string[]>): Promise<{quiz:QItem[],match:any[]}> {
  const ids = sid==='all' ? Object.keys(topicsMap) : [sid];
  let pairs = ids.flatMap(s => (topic==='ALL' ? (topicsMap[s]||[]) : [topic]).map(t => [s, t] as const));
  if (sid==='all' && pairs.length > RANDOM_TOPICS) pairs = shuffle([...pairs]).slice(0, RANDOM_TOPICS);
  const all: QItem[] = (await Promise.all(pairs.map(([s, t]) => loadTopic(s, t).catch(() => [] as QItem[])))).flat();
  return {
    quiz:  all.filter(q=>q.t==='q'),
    // Firestore 儲存格式為 {l,r}，轉回 [left, right] 陣列
    match: all.filter(q=>q.t==='m').flatMap(q=>
      (q.p||[]).map((pair:any)=> Array.isArray(pair) ? pair : [pair.l, pair.r])
    ),
  };
}

// ── Button component ──────────────────────────────────────────────────────────
const Btn = ({onClick,style,children,disabled,className,title,'aria-label':ariaLabel}:{onClick?:()=>void,style?:React.CSSProperties,children?:React.ReactNode,disabled?:boolean,className?:string,title?:string,'aria-label'?:string}) => (
  <button onClick={onClick} disabled={disabled} aria-label={ariaLabel} title={title} className={className}
    style={{border:'none',cursor:disabled?'default':'pointer',fontFamily:'inherit',...style}}>
    {children}
  </button>
);

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  // Navigation
  const [page,setPage]   = useState('home');
  // 往下一層走時回到頂端；返回上一層（首頁、主題、單元等清單頁）時回到原本的捲動位置
  const PAGE_DEPTH: Record<string, number> = { home: 0, topic: 1, wrongs: 1, leaderboard: 1, stats: 1, unit: 2 };
  const scrollPos = useRef<Record<string, number>>({});
  const scrollPage = useRef(page);
  const scrollBack = useRef(false);
  // 換頁後的第一次 render 時畫面還是舊的，此時的捲動位置屬於上一頁
  if (scrollPage.current !== page) {
    const prev = scrollPage.current;
    scrollPos.current[prev] = window.scrollY;
    scrollPage.current = page;
    scrollBack.current = (PAGE_DEPTH[page] ?? 3) < (PAGE_DEPTH[prev] ?? 3);
  }
  useEffect(() => {
    const y = scrollBack.current ? (scrollPos.current[page] ?? 0) : 0;
    window.scrollTo(0, y);
    if (!y) return;
    const id = window.setTimeout(() => window.scrollTo(0, y), 200);   // 清單內容稍後才補上時再定位一次
    return () => window.clearTimeout(id);
  }, [page]);
  // Subject / topic / mode
  const [sub,setSub]     = useState<Subject|null>(null);
  const [topic,setTopic] = useState('');
  const [mode,setMode]   = useState('quiz');
  // 學習單元（依概念分組）：null = 全部；否則為選定單元的題目集合
  const [unitSel,setUnitSel] = useState<{label:string,items:QItem[]}|null>(null);
  const [unitItems,setUnitItems] = useState<QItem[]|null>(null); // 當前主題全部題目（unit 頁用）
  const [err,setErr]     = useState('');
  // Theme (replaces separate themeColor + darkMode)
  const [themeId, setThemeId] = useState<string>(()=>{
    const s = localStorage.getItem('gsat_theme') ?? '';
    return THEMES.find(t=>t.id===s) ? s : 'ocean';
  });
  const currentTheme = THEMES.find(t=>t.id===themeId) ?? THEMES[0];
  const darkMode = currentTheme.dark;
  // Font size
  const [fontSize,setFontSize] = useState(()=>{
    return parseInt(localStorage.getItem('gsat_fontsize') || '16');
  });
  // Quiz state
  const [qList,setQList]       = useState<QItem[]>([]);
  const [qi,setQi]             = useState(0);
  // 作答時先在背景下載下一題的圖片與音訊，切題時不必等
  useEffect(() => {
    if (page !== 'quiz') return;
    const next = qList[qi + 1];
    if (next?.img) { const im = new Image(); im.src = next.img; }
    if (next?.audio) { const a = new Audio(); a.preload = 'auto'; a.src = next.audio; }
  }, [page, qi, qList]);
  const [shuffledOpts,setShuffledOpts] = useState<{text:string,origIdx:number}[]>([]);
  const [sel,setSel]           = useState<number|null>(null);
  const [showExp,setShowExp]   = useState(false);
  const [score,setScore]       = useState(0);
  const [combo,setCombo]       = useState(0);
  const [maxC,setMaxC]         = useState(0);
  const [wrongs,setWrongs]     = useState<QItem[]>([]);
  const [paused,setPaused]     = useState(false);      // 功能一：答錯暫停
  // Timer (功能二)
  const [timeLeft,setTimeLeft]       = useState(TIMER_SEC);
  const [timerActive,setTimerActive] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval>|null>(null);
  // Match state
  const [lItems,setLItems]     = useState<any[]>([]);
  const [rItems,setRItems]     = useState<any[]>([]);
  const [selL,setSelL]         = useState<number|null>(null);
  const [matched,setMatched]   = useState<Set<number>>(new Set());
  const [flashL,setFlashL]     = useState<number|null>(null);
  const [flashR,setFlashR]     = useState<number|null>(null);
  const [mWrong,setMWrong]     = useState(0);
  // Nickname (功能三)
  const [nickname,setNickname] = useState<string>(()=>lsGet(LS_NICK,'')||'');
  // Global wrongs bank (功能五) — managed via useUserData below
  // Custom question bank (功能六)
  // Saved progress (功能四)
  const [savedProg,setSavedProg] = useState<any>(()=>lsGet(LS_PROG,null));
  const hasOwnTiming = useRef(localStorage.getItem(LS_TIMING) !== null);  // 使用者曾自訂時間：不套用後台預設
  const [timing, setTiming] = useState<Timing>(()=>migrateTiming({...DEFAULT_TIMING, ...lsGet(LS_TIMING,{})}));
  useEffect(()=>{ lsSet(LS_TIMING, timing); }, [timing]);
  // 題目的作答秒數：聽力 > 會考歷屆 > 一般；0 表示不限時
  const timeLimitFor = (q?: {year?: number, audio?: string}) =>
    (q?.audio ? timing.listen : q?.year != null ? timing.gsat : timing.normal);
  // 答題後自動跳下一題的計時器（手動換題或離開時取消，避免重複跳題）
  const advanceRef = useRef<ReturnType<typeof setTimeout>|null>(null);
  const advanceTickRef = useRef<ReturnType<typeof setInterval>|null>(null);
  const [advanceIn, setAdvanceIn] = useState<number|null>(null);   // 解說停留倒數秒數；null 表示不自動跳題
  const clearAdvance = () => {
    if (advanceRef.current) { clearTimeout(advanceRef.current); advanceRef.current = null; }
    if (advanceTickRef.current) { clearInterval(advanceTickRef.current); advanceTickRef.current = null; }
    setAdvanceIn(null);
  };
  // 解說停留 sec 秒後執行 go；sec 為 0 表示手動按繼續
  const scheduleAdvance = (sec: number, go: () => void) => {
    clearAdvance();
    if (sec <= 0) return;
    setAdvanceIn(sec);
    advanceTickRef.current = setInterval(() => setAdvanceIn(n => (n && n > 1 ? n - 1 : n)), 1000);
    advanceRef.current = setTimeout(() => { clearAdvance(); go(); }, sec * 1000);
  };
  // Mobile detection
  const [isMobile, setIsMobile] = useState(()=>window.innerWidth < 480);
  useEffect(()=>{
    const handle = ()=>setIsMobile(window.innerWidth<480);
    window.addEventListener('resize',handle);
    return ()=>window.removeEventListener('resize',handle);
  },[]);
  // Leaderboard
  const [lbTab,setLbTab] = useState<'global'|'mine'>('global');
  // Time tracking
  const startTimeRef = useRef<number>(0);

  useEffect(()=>{
    const t = THEMES.find(x=>x.id===themeId) ?? THEMES[0];
    document.documentElement.setAttribute('data-theme', t.dark ? 'dark' : 'light');
    document.documentElement.style.setProperty('--accent', t.accent);
    localStorage.setItem('gsat_theme', t.id);
  },[themeId]);

  useEffect(()=>{
    // 整頁放大：zoom 設在 #root，並把寬度縮為 1/zoom，放大後剛好等於螢幕寬度，版面依實際可用寬度排版
    //（zoom 設在 html 或 body 時 Chrome 不會依縮放後寬度重排，會超出螢幕）；--font-size 保留給使用它的 CSS
    //（寬度用 px：百分比寬度在 zoom 元素上會被再縮放一次）
    const zoom = fontSize / 16;
    const root = document.getElementById('root');
    const fit = () => {
      if (!root) return;
      (root.style as any).zoom = String(zoom);
      root.style.width = `${document.documentElement.clientWidth / zoom}px`;
    };
    fit();
    window.addEventListener('resize', fit);
    document.documentElement.style.setProperty('--font-size', `${fontSize}px`);
    localStorage.setItem('gsat_fontsize', String(fontSize));
    return () => window.removeEventListener('resize', fit);
  },[fontSize]);

  // ── Auth state ────────────────────────────────────────────────────────────
  const [authUser, setAuthUser]         = useState<User|null>(null);
  const [authLoading, setAuthLoading]   = useState(true);
  // 帳號核准狀態（users/{uid}.status）：新註冊為 pending，管理員核准後才能使用
  const [accountStatus, setAccountStatus] = useState<'pending'|'approved'|'disabled'|'deleted'>('pending');
  const [pendingCount, setPendingCount] = useState(0);   // 管理員：待核准帳號數
  const [reportCount, setReportCount] = useState(0);     // 管理員：待處理的題目錯誤回報數
  const [guestMode, setGuestMode]       = useState(false);
  const [authEmail, setAuthEmail]       = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authConfirm, setAuthConfirm]   = useState('');
  const [authError, setAuthError]       = useState('');
  const [authBusy, setAuthBusy]         = useState(false);
  const [fpEmail, setFpEmail] = useState('');
  const [fpMsg, setFpMsg]     = useState('');
  const [fpErr, setFpErr]     = useState(false);

  const [bgmTrack, setBgmTrackState] = useState(getBgmTrack);
  const { playSound, muted, setMuted, bgmOn, setBgmOn, startBgm, stopBgm, duck } = useSound();

  // Export state
  const [expFmt, setExpFmt]       = useState<'json'|'csv'|'zip'>('json');
  const [exporting, setExporting] = useState(false);

  // Admin state
  const [adminSub, setAdminSub]     = useState('ch');
  const [adminTopic, setAdminTopic] = useState('t01');
  const [adminQuery, setAdminQuery] = useState('');   // 題庫：搜尋題目或解說
  const [adminQFilters, setAdminQFilters] = useState<string[]>([]);   // 題庫：篩選（複選，同時符合）
  const [ioHelp, setIoHelp] = useState(false);        // 題庫：匯出匯入說明展開
  // 匯出、批次匯入沿用題庫分頁選定的科目與主題
  const batchSub = adminSub, batchTopic = adminTopic;
  const [adminItems, setAdminItems] = useState<QItem[]>([]);
  const [adminDocIds, setAdminDocIds] = useState<string[]>([]);
  const [adminEdit, setAdminEdit]   = useState<number|null>(null);
  const [adminForm, setAdminForm]   = useState<{t:string,q:string,o:string[],a:number,e:string,img:string,noImg?:boolean,ver?:boolean,verTouched?:boolean}>({t:'q',q:'',o:['','','',''],a:0,e:'',img:''});
  // 編輯已驗證的題目時，只要內容被改動，驗證就失效（取消勾選），確認新內容後需重新勾選
  useEffect(() => {
    if (adminEdit === null || adminEdit < 0 || !adminForm.ver || adminForm.verTouched) return;
    const orig = adminItems[adminEdit];
    if (!orig || !isVerified(orig)) return;
    const oo = [...adminForm.o]; while (oo.length && !oo[oo.length - 1]?.trim()) oo.pop();
    if (contentHash({ ...orig, t: adminForm.t, q: adminForm.q, o: oo, a: adminForm.a, e: adminForm.e, img: adminForm.img || undefined }) !== contentHash(orig))
      setAdminForm(f => ({ ...f, ver: false }));
  }, [adminForm.t, adminForm.q, adminForm.o, adminForm.a, adminForm.e, adminForm.img, adminForm.ver, adminForm.verTouched, adminEdit, adminItems]);
  // Batch import
  const [batchPreview, setBatchPreview] = useState<{
    toUpdate: BatchUpdate[];            // item 只含有變動的欄位
    toAdd:    Record<string, any>[];
    skipped:  number;
    invalid:  number;                   // 格式不完整而略過的筆數
    warnings: string[];                 // 圖片／音訊網址警告
    media:    { img: number; audio: number };
    uploads:  { path: string; blob: Blob }[];   // ZIP 內要上傳的檔案
    blocked:  boolean;                         // 有無法解析的圖片／音訊，不允許寫入（避免寫入壞連結）
  } | null>(null);
  const [zipBusy, setZipBusy] = useState('');   // ZIP 匯出／上傳進度文字
  const [batchImporting, setBatchImporting] = useState(false);
  const [adminTab, setAdminTab]     = useState<'questions'|'users'|'subjects'|'notes'|'reports'|'settings'>('subjects');
  const [adminUsers, setAdminUsers] = useState<Array<{uid:string,nickname:string,color?:string,avatarUrl?:string,status?:string,email?:string,emailVerified?:boolean}>>([]);
  const [adminUsersLoaded, setAdminUsersLoaded] = useState(false);
  const [userFilter, setUserFilter] = useState<'all'|'pending'|'approved'|'disabled'|'deleted'>('all');

  // Custom subjects state
  const [customSubs, setCustomSubs]     = useState<Subject[]>([]);
  const [customTopics, setCustomTopics] = useState<Record<string,string[]>>({});
  const [customTopicNames, setCustomTopicNames] = useState<Record<string,Record<string,string>>>({});
  const [customTopicCats, setCustomTopicCats]   = useState<Record<string,Record<string,string>>>({});  // 主題 ID → 分類
  const [customCounts, setCustomCounts]         = useState<Record<string,Record<string,number>>>({});  // 主題 ID → 題數（後台重算）
  const [topicCatSel, setTopicCatSel] = useState('');      // 主題頁篩選：分類（空字串為全部）
  const [topicQuery, setTopicQuery]   = useState('');      // 主題頁篩選：關鍵字
  // 「全部主題」：題數（'all' = 全部）與出題範圍（依目前分類篩選後的主題）
  const [allCount, setAllCount] = useState<string>(() => localStorage.getItem('gsat_all_count') || '50');
  const [allScope, setAllScope] = useState<string[] | null>(null);
  const [adminNewSub, setAdminNewSub]   = useState({id:'',name:'',icon:'📗',color:'#0891b2',bg:'#ecfeff',topicsText:''});
  const [adminSubSaving, setAdminSubSaving] = useState(false);
  const [editingSubId, setEditingSubId] = useState<string|null>(null);
  const [newSubOpen, setNewSubOpen] = useState(false);   // 後台：新增科目表單展開
  const [subjectGroupList, setSubjectGroupList] = useState<SubjectGroup[]>([]);

  // Notes（重點整理）state
  const [notesTopic,   setNotesTopic]   = useState('');
  const [notesUnit,    setNotesUnit]    = useState<string|null>(null);
  const [notesItems,   setNotesItems]   = useState<NoteItem[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesTab,     setNotesTab]     = useState<'article'|'card'|'map'|'match'>('article');
  const [notesMatchCount, setNotesMatchCount] = useState(0);   // 目前重點整理主題的配對題組數（0 則不顯示「配對」分頁）
  const [notesSearch,  setNotesSearch]  = useState('');
  const [srDueOnly,    setSrDueOnly]    = useState(false);
  const [srData,       setSrData]       = useState<Record<string,SRCard>>(() => {
    try { return JSON.parse(localStorage.getItem(SR_KEY) ?? '{}'); } catch { return {}; }
  });
  const [cardIndex,    setCardIndex]    = useState(0);
  const [cardFlipped,  setCardFlipped]  = useState(false);
  // ── 即時朗讀（重點、閃卡、題目）────────────────────────────────────────
  const [speakingKey, setSpeakingKey] = useState<string|null>(null);
  const [ttsRate, setTtsRate] = useState(getRate);
  const [ttsAuto, setTtsAuto] = useState(() => localStorage.getItem(LS_TTS_AUTO) !== 'false');   // 預設開啟
  const startSpeak = (key: string, text: string) => {
    setSpeakingKey(key); duck(true);
    speak(text, () => { setSpeakingKey(k => k === key ? null : k); duck(false); });
  };
  const toggleSpeak = (key: string, text: string) => {
    if (speakingKey === key) { stopSpeaking(); setSpeakingKey(null); duck(false); return; }
    startSpeak(key, text);
  };
  const cardSpeechRef = useRef<{ front: string; back: string } | null>(null);   // 目前閃卡內容（供自動朗讀）
  useEffect(() => {
    stopSpeaking(); setSpeakingKey(null); duck(false);
    // 閃卡自動朗讀：新卡念正面、翻面念背面
    if (ttsAuto && page === 'notes' && notesTab === 'card') {
      const c = cardSpeechRef.current;
      if (c) { const t = setTimeout(() => startSpeak(cardFlipped ? 'card-back' : 'card-front', cardFlipped ? c.back : c.front), 250); return () => clearTimeout(t); }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, qi, cardIndex, cardFlipped, notesTab]);
  // Admin Notes state

  const clr = currentTheme.accent;  // 主題色優先，忽略科目預設色
  const sbg = sub?.b||'#eff6ff';   // 科目背景淺色仍保留

  const [fpBusy, setFpBusy] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string|null>(null);
  const [uploading, setUploading] = useState(false);

  // ── 載入自訂科目（Firestore customSubjects collection）──────────────────────
  const loadCustomSubjects = () => {
    try {
      const cached = JSON.parse(localStorage.getItem(CUSTOM_SUBS_CACHE_KEY) || '{}');
      if (cached.ts && Date.now() - cached.ts < CUSTOM_SUBS_CACHE_TTL && cached.subs) {
        setCustomSubs(cached.subs);
        setCustomTopics(cached.tops ?? {});
        setCustomTopicNames(cached.names ?? {});
        setCustomTopicCats(cached.cats ?? {});
        setCustomCounts(cached.counts ?? {});
        setSubjectGroupList(cached.groups ?? []);
        return;
      }
    } catch {}

    Promise.all([getDocs(collection(db, 'customSubjects')), getDocs(collection(db, 'subjectGroups'))]).then(([snap, gsnap]) => {
      const subs: Subject[] = [];
      const tops: Record<string,string[]> = {};
      const names: Record<string,Record<string,string>> = {};
      const cats: Record<string,Record<string,string>> = {};
      const counts: Record<string,Record<string,number>> = {};
      snap.docs
        .sort((a,b) => (a.data().order??99) - (b.data().order??99))
        .forEach(d => {
          const s = d.data();
          const sid = s.id || d.id;
          subs.push({ id: sid, n: s.name, i: s.icon, c: s.color, b: s.bg, g: s.group, lb: s.label, o: s.order, ga: !!s.guestAllowed });
          tops[sid] = s.topics ?? [];          // 主題 ID
          names[sid] = s.topicNames ?? {};    // 主題 ID → 名稱
          cats[sid] = s.topicCats ?? {};      // 主題 ID → 分類
          counts[sid] = s.counts ?? {};       // 主題 ID → 題數
        });
      setCustomSubs(subs);
      setCustomTopics(tops);
      setCustomTopicNames(names);
      setCustomTopicCats(cats);
      setCustomCounts(counts);
      const groups: SubjectGroup[] = gsnap.docs
        .map(d => ({ id: d.id, name: d.data().name ?? d.id, order: d.data().order ?? 99, color: d.data().color, stroke: !!d.data().stroke }))
        .sort((a,b) => a.order - b.order);
      setSubjectGroupList(groups);
      try {
        localStorage.setItem(CUSTOM_SUBS_CACHE_KEY, JSON.stringify({ ts: Date.now(), subs, tops, names, cats, counts, groups }));
      } catch {}
    }).catch(()=>{});
  };
  useEffect(loadCustomSubjects, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 有群組主色的科目改用系列配色（依群組內位置深到淺）；否則沿用科目自己的顏色
  // 強制重新整理：移除 service worker 與快取、清除本機資料快取後重新載入
  const forceRefresh = async () => {
    try {
      if ('serviceWorker' in navigator)
        for (const r of await navigator.serviceWorker.getRegistrations()) await r.unregister();
      if ('caches' in window)
        for (const k of await caches.keys()) await caches.delete(k);
      Object.keys(localStorage).filter(k => k.startsWith('gsat_custom_subs') || k.startsWith('gsat_total_map'))
        .forEach(k => localStorage.removeItem(k));
    } catch {}
    window.location.reload();
  };

  const allSubjects = useMemo(() => customSubs.map(sb => {
    const g = subjectGroupList.find(x => x.id === sb.g);
    if (!g?.color) return sb;
    const idx = customSubs.filter(x => x.g === sb.g).indexOf(sb);
    return { ...sb, ...seriesColor(g.color, idx, darkMode) };
  })
    // 依群組順序、再依群組內順序排列（未設群組的排最後），各處下拉選單與清單一致
    .sort((a, b) => {
      const gi = (x: Subject) => { const i = subjectGroupList.findIndex(g => g.id === x.g); return i < 0 ? 999 : i; };
      return gi(a) - gi(b) || (a.o ?? 99) - (b.o ?? 99);
    }), [customSubs, subjectGroupList, darkMode]);
  const allTopics   = customTopics;
  const isGuest = !authUser && guestMode;   // 訪客模式：只能使用 guestAllowed 的科目（新科目預設不開放）
  // 主題 ID → 名稱（'ALL' 為所有主題；查不到時顯示原值）
  const topicNameOf = useMemo(() => {
    const all = customTopicNames;
    return (sid: string|undefined|null, tid: string|undefined|null): string =>
      tid === 'ALL' ? '所有主題' : (all[sid ?? '']?.[tid ?? ''] ?? tid ?? '');
  }, [customTopicNames]);

  // 首頁類別篩選（複選，依科目卡片短名；未選任何類別 = 全部），記住在本機
  const [homeKinds, setHomeKinds] = useState<string[]>(() => { try { return JSON.parse(localStorage.getItem('gsat_home_kinds') || '[]'); } catch { return []; } });
  const toggleHomeKind = (k: string) => setHomeKinds(ks => {
    const next = ks.includes(k) ? ks.filter(x => x !== k) : [...ks, k];
    try { localStorage.setItem('gsat_home_kinds', JSON.stringify(next)); } catch {}
    return next;
  });
  // 首頁依群組分區：群組依 subjectGroups.order，區內科目依 order；未設群組的放「其他」
  const subjectGroups = useMemo(() => {
    const visible = isGuest ? allSubjects.filter(sb => sb.ga) : allSubjects;
    const out = subjectGroupList.map(g => ({ id: g.id, name: g.name, subs: visible.filter(sb => sb.g === g.id) }));
    const rest = visible.filter(sb => !subjectGroupList.some(g => g.id === sb.g));
    if (rest.length) out.push({ id: '_other', name: '其他', subs: rest });
    return out.filter(g => g.subs.length);
  }, [allSubjects, subjectGroupList, isGuest]);
  const topicLabel = topicNameOf(sub?.id, topic);
  // 筆順：null 為關閉；字串為要帶入的文字（空字串＝沿用上次輸入的字）
  const [strokeText, setStrokeText] = useState<string|null>(null);
  // 科目所屬群組有開啟「筆順」才在題目、閃卡旁顯示 ✍️ 捷徑
  const strokeOn = (sid?: string) => !!subjectGroupList.find(g => g.id === allSubjects.find(x => x.id === sid)?.g)?.stroke;
  const strokeBtn = (text: string, before?: () => void) => (
    <button onClick={e => { e.stopPropagation(); before?.(); setStrokeText(text); }} aria-label="看筆順" title="看這段文字的筆順"
      style={{background:'none',border:'none',cursor:'pointer',fontSize:16,padding:'2px 4px',lineHeight:1,flexShrink:0,opacity:0.75}}>✍️</button>
  );
  const strokeLayer = strokeText !== null && createPortal(<StrokeOrder initial={strokeText} onClose={() => setStrokeText(null)} />, document.body);

  // ── 成績紀錄以科目 ID 關聯，顯示時用 ID 查目前名稱 ───────────────────────
  const subNameOf = useMemo(() => makeSubjectNameOf(allSubjects), [allSubjects]);

  // ── Per-user wrongs & records (Firestore or localStorage fallback) ─────────
  const { wrongs: globalWrongs, records: localRecords, addWrong, removeWrongs, markCorrect, addRecord, refreshWrongs } = useUserData(authUser?.uid ?? null);

  // ── 逐題作答紀錄（完成率、錯題率、曾錯過的題目）──────────────────────────
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [answersReady, setAnswersReady] = useState(false);
  useEffect(() => {
    let alive = true;
    setAnswersReady(false);
    loadAnswers(authUser?.uid ?? null)
      .then(m => { if (alive) { setAnswers(m); setAnswersReady(true); } })
      .catch(() => { if (alive) setAnswersReady(true); });
    return () => { alive = false; };
  }, [authUser?.uid]);
  // 上線時把錯題本現有題目補記為「曾答錯」（只補沒有紀錄的）
  useEffect(() => {
    if (!answersReady) return;
    const refs = globalWrongs.map(w => w.ref).filter(Boolean) as AnsRef[];
    if (!refs.some(r => !answers[`${r.s}__${r.t}__${r.id}`])) return;
    seedFromWrongs(authUser?.uid ?? null, answers, refs).then(setAnswers).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answersReady, globalWrongs]);
  // 公開統計（排行榜「完成率」用）：只含各科答對過的題數，作答變動後 3 秒同步
  useEffect(() => {
    if (!authUser || !answersReady) return;
    const t = setTimeout(() => {
      const mastered: Record<string, number> = {};
      Object.values(answers).forEach(a => { if (a.corrects > 0) mastered[a.s] = (mastered[a.s] ?? 0) + 1; });
      setDoc(doc(db, 'publicStats', authUser.uid), { nickname: nickname || '匿名', avatarUrl: avatarUrl ?? null, mastered, ts: serverTimestamp() }).catch(() => {});
    }, 3000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, answersReady, authUser?.uid, nickname, avatarUrl]);
  // 每日目標：今日題數、目標、今日任務是否完成（跨午夜自動換日）
  const [daily, setDaily] = useState<Daily>({ date: dateKey(), count: 0, taskDone: false, goal: DEFAULT_GOAL });
  const [goalEdit, setGoalEdit] = useState(false);
  useEffect(() => {
    if (authLoading) return;
    loadDaily(authUser?.uid ?? null).then(setDaily).catch(() => {});
  }, [authLoading, authUser?.uid]);
  const logAnswer = (q: QItem, correct: boolean) => {
    if (!q.ref) return;
    setAnswers(m => applyAnswer(m, q.ref!, correct));
    saveAnswer(authUser?.uid ?? null, q.ref, correct).catch(() => {});
    const today = dateKey();
    setDaily(d => d.date === today ? { ...d, count: d.count + 1 } : { ...d, date: today, count: 1, taskDone: false });
    bumpDaily(authUser?.uid ?? null, today).catch(() => {});
  };

  // ── 後台設定（config/app）────────────────────────────────────────────────
  const [appCfg, setAppCfg] = useState<AppConfig>(DEFAULT_CONFIG);
  useEffect(() => {
    getDoc(doc(db, 'config', 'app')).then(d => {
      const v = d.data() ?? {};
      const cfg = { ...DEFAULT_CONFIG };
      (Object.keys(DEFAULT_CONFIG) as (keyof AppConfig)[]).forEach(k => { if (typeof v[k] === 'number') cfg[k] = v[k]; });
      setAppCfg(cfg);
      // 使用者沒自訂過時間：套用後台預設秒數
      if (!hasOwnTiming.current) {
        const t = cfg.timingDefault;
        setTiming(cur => ({ ...cur, normal:t, gsat:t, listen:t }));   // 後台預設只設定作答時間
      }
    }).catch(() => {});
  }, []);
  const wrongClearStreak = appCfg.wrongClearStreak;
  const saveCfg = async (patch: Partial<AppConfig>) => {
    setAppCfg(c => ({ ...c, ...patch }));
    try { await setDoc(doc(db,'config','app'), patch, { merge:true }); }
    catch { toast('儲存失敗，請稍後再試'); }
  };
  // ── 錯題本：依範圍（科目／主題／單元）篩選與練習 ─────────────────────────
  const wrongsOf = (sid?: string, tid?: string, ids?: Set<string>) =>
    globalWrongs.filter(w => (!sid || w.ref?.s === sid) && (!tid || w.ref?.t === tid) && (!ids || (!!w.ref && ids.has(w.ref.id))));
  const practiceWrongs = async (list: typeof globalWrongs, label?: string) => {
    if (!list.length) return;
    setSub({id:WRONGS_SUB_ID,n:label ? `錯題複習 · ${label}` : '錯題複習',i:'🔁',c:'#dc2626',b:'#fff1f2'});
    setTopic('ALL');
    setMode('quiz');
    setUnitSel(null);
    // 以題目位置讀取最新內容，題目修正後錯題本同步更新
    const fresh = (await refreshWrongs(list) as QItem[]).filter(q => q.q || q.p);
    // await 之後才呼叫，透過 ref 取得已套用上述設定的最新 startGame
    if (fresh.length) startGameRef.current(fresh);
  };
  const clearWrongScope = async (list: typeof globalWrongs, label: string) => {
    if (!(await confirmDialog(`確定清除「${label}」的 ${list.length} 題錯題？此動作無法復原。`))) return;
    await removeWrongs(list);
  };
  // 曾錯過的題目（含已訂正），以題目位置組成可練習的清單
  const everWrongOf = (sid?: string, tid?: string, ids?: Set<string>) =>
    Object.values(answers)
      .filter(a => a.wrongs > 0 && (!sid || a.s === sid) && (!tid || a.t === tid) && (!ids || ids.has(a.id)))
      .map(a => ({ t:'q', ref:{ s:a.s, t:a.t, id:a.id } }) as typeof globalWrongs[number]);
  const practiceRefs = (refs: AnsRef[], label: string) =>
    practiceWrongs(refs.map(r => ({ t:'q', ref:r }) as typeof globalWrongs[number]), label);
  const [wrongMode, setWrongMode] = useState<'now'|'ever'>('now');   // 錯題本：目前錯題／曾錯過的題目
  const [analysisTarget, setAnalysisTarget] = useState<{uid:string|null,name:string,email?:string}|null>(null);
  // 未登入（含登入畫面、訪客）只計算開放科目的題數（其他科目資料庫不允許未登入讀取）
  // 登入狀態確認前不計算（避免先用未登入範圍算一輪再重算）
  const progressTopics = useMemo(() => authLoading ? {} : !authUser
    ? Object.fromEntries(Object.entries(allTopics).filter(([sid]) => allSubjects.find(x => x.id === sid)?.ga))
    : allTopics, [authLoading, authUser, allTopics, allSubjects]);
  const progress = useProgress(answers, progressTopics, authUser ? 'user' : 'guest', customCounts);

  // ── 答對率（首頁科目卡、主題清單）──────────────────────────────────────
  // 以逐題作答紀錄計算「答對次數 ÷ 作答次數」，每題照實計入；
  // 作答次數太少不顯示，避免只做一兩題就出現 100%。
  const { subAvgPct, topicAvgPct } = useMemo(() => {
    const agg: Record<string,[number,number]> = {};   // 科目／科目__主題 → [答對次數, 作答次數]
    Object.values(answers).forEach(a => {
      [a.s, `${a.s}__${a.t}`].forEach(k => {
        const cur = agg[k] ?? (agg[k] = [0, 0]);
        cur[0] += a.corrects; cur[1] += a.attempts;
      });
    });
    const subAvgPct: Record<string,number> = {};
    const topicAvgPct: Record<string,number> = {};
    Object.entries(agg).forEach(([k, [ok, n]]) => {
      if (n < PCT_MIN_TRIED) return;
      (k.includes('__') ? topicAvgPct : subAvgPct)[k] = Math.round(ok / n * 100);
    });
    return { subAvgPct, topicAvgPct };
  }, [answers]);


  const handleForgotPw = async () => {
    if (!fpEmail.trim()) { setFpErr(true); setFpMsg('請輸入 Email'); return; }
    setFpBusy(true);
    try {
      await sendPasswordResetEmail(auth, fpEmail.trim());
      setFpErr(false);
      // 已開啟帳號探測保護：信箱不存在時也不會報錯，因此訊息不能斷定已寄出
      setFpMsg('如果這個信箱已註冊，將會收到重設密碼的連結。請確認信箱拼字正確，並檢查垃圾郵件匣。');
    } catch(e:any) {
      setFpErr(true);
      const msg: Record<string,string> = {
        'auth/user-not-found': '查無此帳號',
        'auth/invalid-email': '請輸入有效的 Email格式',
      };
      setFpMsg(msg[e.code] || '寄送失敗，請稍後再試');
    } finally { setFpBusy(false); }
  };

  const doLogin = async () => {
    if (!authEmail || !authPassword) { setAuthError('請填寫電子郵件與密碼'); return; }
    setAuthBusy(true); setAuthError('');
    try {
      await signInWithEmailAndPassword(auth, authEmail, authPassword);
      setAuthEmail(''); setAuthPassword('');
      setPage('home');
    } catch (e: any) {
      const msg: Record<string,string> = {
        'auth/user-not-found': '找不到此帳號',
        'auth/wrong-password': '密碼錯誤',
        'auth/invalid-credential': '電子郵件或密碼錯誤',
        'auth/too-many-requests': '嘗試次數過多，請稍後再試',
      };
      setAuthError(msg[e.code] || '登入失敗，請再試一次');
    } finally { setAuthBusy(false); }
  };

  const doRegister = async () => {
    if (!authEmail || !authPassword) { setAuthError('請填寫所有欄位'); return; }
    if (authPassword !== authConfirm) { setAuthError('兩次密碼不一致'); return; }
    if (authPassword.length < 6) { setAuthError('密碼至少需要 6 位'); return; }
    setAuthBusy(true); setAuthError('');
    try {
      const cred = await createUserWithEmailAndPassword(auth, authEmail, authPassword);
      await sendEmailVerification(cred.user);
      setAuthEmail(''); setAuthPassword(''); setAuthConfirm('');
      setPage('home');
    } catch (e: any) {
      const msg: Record<string,string> = {
        'auth/email-already-in-use': '此電子郵件已被使用',
        'auth/invalid-email': '電子郵件格式錯誤',
        'auth/weak-password': '密碼強度不足',
      };
      setAuthError(msg[e.code] || '註冊失敗，請再試一次');
    } finally { setAuthBusy(false); }
  };

  // ── Count-up animation for result page ────────────────────────────────
  const [displayPct, setDisplayPct] = useState(0);
  useEffect(() => {
    if (page !== 'result') { setDisplayPct(0); return; }
    const total = mode==='quiz'?qList.length:lItems.length;
    const sc = mode==='quiz'?score:lItems.length;
    const targetPct = Math.round((sc/total)*100);
    let cur = 0;
    const step = Math.ceil(targetPct / 20);
    const iv = setInterval(() => {
      cur = Math.min(cur + step, targetPct);
      setDisplayPct(cur);
      if (cur >= targetPct) clearInterval(iv);
    }, 50);
    return () => clearInterval(iv);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // deepMode is now derived from themeId; its effect is handled in the theme useEffect above

  // ── Auth state listener ──────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setAuthUser(user);
      setStudyUid(user?.uid ?? null);   // 學習時長歸屬
      if (user) {
        try {
          const ref = doc(db, 'users', user.uid);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            const data = snap.data() as {nickname:string, color?:string, themeId?:string, avatarUrl?:string, status?:string, emailVerified?:boolean};
            setAccountStatus((data.status as any) ?? 'pending');
            // 同步電子郵件驗證狀態（管理員核准時需要；規則只允許照登入憑證寫入 true）
            if (!!data.emailVerified !== user.emailVerified) {
              if (user.emailVerified) await user.getIdToken(true);
              updateDoc(ref, { emailVerified: user.emailVerified, email: user.email ?? '' }).catch(() => {});
            }
            setNickname(data.nickname);
            if (data.themeId && THEMES.find(t=>t.id===data.themeId)) setThemeId(data.themeId);
            if ((data as any).timingV2) setTiming(migrateTiming({...DEFAULT_TIMING, ...(data as any).timingV2}));
            setAvatarUrl(data.avatarUrl ?? null);
          } else {
            const migrated = {
              nickname: lsGet(LS_NICK, '') || '',
              themeId: 'ocean',
              status: 'pending',                      // 新帳號：待管理員核准
              email: user.email ?? '',
              emailVerified: user.emailVerified,
            };
            await setDoc(ref, migrated);
            setAccountStatus('pending');
            setNickname(migrated.nickname);
          }
        } finally {
          setAuthLoading(false);
        }
      } else {
        setAuthLoading(false);
      }
    });
    return unsub;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 管理員：讀取待核准帳號數（首頁頭像與後台用戶分頁顯示）
  const refreshPendingCount = () => {
    if (!authUser || !ADMIN_UIDS.includes(authUser.uid)) return;
    getDocs(query(collection(db, 'users'), where('status', '==', 'pending')))
      .then(s => setPendingCount(s.size)).catch(() => {});
    getDocs(collection(db, 'reports')).then(s => setReportCount(s.size)).catch(() => {});   // 回報處理完會被刪除，所以文件數就是待處理數
  };
  useEffect(refreshPendingCount, [authUser?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 登出後導回 login ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!authLoading && authUser === null && !guestMode) {
      setPage('login');
    }
  }, [authUser, authLoading, guestMode]);

  // ── 瀏覽器返回鍵支援 ──────────────────────────────────────────────────────
  useEffect(() => {
    window.history.replaceState({ page }, '', window.location.pathname);
    const onPop = (e: PopStateEvent) => {
      const p = e.state?.page;
      if (p) setPage(p);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const noHistory = ['login','register','guest-nick','forgot-pw','loading'];
    if (!noHistory.includes(page)) {
      window.history.pushState({ page }, '', window.location.pathname);
    }
  }, [page]);

  // ── 計時器 (功能二) ────────────────────────────────────────────────────────
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!timerActive || paused || page!=='quiz' || !timeLimitFor(qList[qi])) return;
    timerRef.current = setInterval(()=>{
      setTimeLeft(t=>{
        if (t<=1) { clearInterval(timerRef.current!); handleTimeout(); return 0; }
        if (t-1<=10 && t-1>0 && sel===null) playSound('tick');
        return t-1;
      });
    },1000);
    return ()=>{ if(timerRef.current) clearInterval(timerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[timerActive, paused, qi, page, sel, playSound]);

  // ── 選項隨機排列（防短期記憶作弊）────────────────────────────────────────
  useEffect(()=>{
    if (page==='quiz' && qList.length>0) {
      const q = qList[qi];
      const opts = (q.o||[]).map((text:string,origIdx:number)=>({text,origIdx}));
      // 歷屆考題保留原題順序；選項為代號（A–D、甲乙丙丁）時打亂會與圖表對不上
      setShuffledOpts(optionsAreShuffled(q) ? shuffle(opts) : opts);
      setTimeLeft(timeLimitFor(q));
    }
  // 時間設定只能在作答流程外修改，換題時讀當下設定即可
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[qi,qList,page]);

  // ── 進度儲存 (功能四) ──────────────────────────────────────────────────────
  useEffect(()=>{
    if (page==='quiz' && qList.length>0) {
      lsSet(LS_PROG,{sub,topic,mode,qList,qi,score,combo,maxC,wrongs});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[qi,score,page]);

  // ── 暱稱儲存 ──────────────────────────────────────────────────────────────
  useEffect(()=>{ lsSet(LS_NICK,nickname); },[nickname]);
  // 個人設定變更後自動儲存到雲端（停止輸入 0.8 秒後寫入；只在個人設定頁觸發，避免載入時回寫）
  const [profileSaveState, setProfileSaveState] = useState<''|'saving'|'saved'|'error'>('');
  useEffect(()=>{
    if (page !== 'profile' || !authUser) return;
    setProfileSaveState(st => st ? 'saving' : st);   // 變更後、寫入前就顯示「儲存中」，避免沿用上次的「已儲存」
    const t = setTimeout(async ()=>{
      setProfileSaveState('saving');
      try {
        await setDoc(doc(db, 'users', authUser.uid), { nickname, themeId, timingV2: timing }, { merge: true });
        setProfileSaveState('saved');
      } catch { setProfileSaveState('error'); }
    }, 800);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nickname, themeId, timing]);

  // ── 中途離開（✕、🏠、瀏覽器返回鍵）：已作答的題目存成一筆成績 ─────────────
  const quizSnapRef = useRef<{ answered:number; score:number; sub:Subject|null; topic:string; mode:string; planned:number } | null>(null);
  if (page === 'quiz' && mode === 'quiz') {
    quizSnapRef.current = { answered: qi + (sel !== null ? 1 : 0), score, sub, topic, mode, planned: qList.length };
  }
  const prevPageRef = useRef(page);
  useEffect(() => {
    const prev = prevPageRef.current;
    prevPageRef.current = page;
    if (prev !== 'quiz' || page === 'quiz' || page === 'result' || page === 'loading') return;
    const snap = quizSnapRef.current;
    quizSnapRef.current = null;
    if (!snap || snap.answered < 1) return;
    saveScore(Math.round(snap.score / snap.answered * 100), snap.score, snap.answered,
      { sub: snap.sub, topic: snap.topic, mode: snap.mode, planned: snap.planned });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // ── 結果頁儲存成績（功能三，只觸發一次）─────────────────────────────────
  const scoreSavedRef = useRef(false);
  useEffect(()=>{
    if (page==='result' && !scoreSavedRef.current) {
      scoreSavedRef.current = true;
      const total = mode==='quiz'?qList.length:lItems.length;
      const sc    = mode==='quiz'?score:lItems.length;
      const pct   = Math.round((sc/total)*100);
      saveScore(pct, sc, total);
    }
    if (page!=='result') scoreSavedRef.current = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[page]);

  // ── Jump to next question / end ────────────────────────────────────────────
  const nextQuestion = (curQi:number) => {
    clearAdvance();
    setPaused(false);
    if (timerRef.current) clearInterval(timerRef.current);
    if (curQi+1>=qList.length) {
      finishQuiz();
    } else {
      setQi(i=>i+1);
      setSel(null);
      setShowExp(false);
      setTimeLeft(TIMER_SEC);
      setTimerActive(true);
    }
  };

  // ── 結束問答，儲存分數 ─────────────────────────────────────────────────────
  const finishQuiz = () => {
    setTimerActive(false);
    lsSet(LS_PROG,null); // 清除進度
    playSound('complete');
    if (sub?.n?.endsWith(DAILY_TASK_LABEL) && !daily.taskDone) {
      const d = dateKey();
      setDaily(v => ({ ...v, date: d, taskDone: true }));
      saveTaskDone(authUser?.uid ?? null, d).catch(() => {});
    }
    setPage('result');
  };

  // ── 答題處理 (含功能一暫停) ────────────────────────────────────────────────
  const handleQuizSel = (optI:number) => {
    if (sel!==null) return;
    const q = qList[qi];
    const ok = optI===q.a;
    setSel(optI); setShowExp(true);
    if (timerRef.current) clearInterval(timerRef.current);
    setTimerActive(false);
    playSound(ok ? 'correct' : 'wrong');
    if (ok) {
      setScore(s=>s+1);
      const nc=combo+1; setCombo(nc); setMaxC(m=>Math.max(m,nc));
      logAnswer(q, true);
      // 答對：錯題本中的題目累計連續答對，達後台設定次數才移出
      markCorrect({...q}, wrongClearStreak).then(left => { if (left) toast('🎉 這題學會了！已移出錯題本'); }).catch(() => {});
      // 答對：依設定秒數自動跳下一題（0 = 手動按繼續）
      scheduleAdvance(timing.correctDelay, ()=>nextQuestion(qi));
    } else {
      setCombo(0);
      logAnswer(q, false);
      const newWrong = {...q};
      setWrongs(w=>[...w,newWrong]);
      // 更新全域錯題本
      addWrong(newWrong);
      // 答錯：暫停，等使用者按繼續；有設定秒數則自動跳下一題
      setPaused(true);
      scheduleAdvance(timing.wrongDelay, ()=>nextQuestion(qi));
    }
  };

  // ── 逾時處理 ──────────────────────────────────────────────────────────────
  const handleTimeout = () => {
    if (sel!==null) return;
    playSound('timeout');
    const q = qList[qi];
    setSel(-1); setShowExp(true);
    setCombo(0);
    logAnswer(q, false);
    setWrongs(w=>[...w,{...q}]);
    addWrong({...q});
    setPaused(true);
    scheduleAdvance(timing.wrongDelay, ()=>nextQuestion(qi));
  };

  // ── Match handlers ─────────────────────────────────────────────────────────
  const initMatch = (pairs:any[]) => {
    setLItems(pairs.map((p,i)=>({txt:p[0],idx:i})));
    setRItems(shuffle(pairs.map((p,i)=>({txt:p[1],idx:i}))));
    setSelL(null); setMatched(new Set());
    setFlashL(null); setFlashR(null); setMWrong(0);
  };
  const handleMatchL = (i:number)=>{ if (!matched.has(lItems[i].idx)) setSelL(i); };
  const handleMatchR = (i:number)=>{
    if (matched.has(rItems[i].idx)||selL===null) return;
    if (lItems[selL].idx===rItems[i].idx) {
      const nm=new Set(matched); nm.add(lItems[selL].idx); setMatched(nm); setSelL(null);
      if (sub) logStudy(authUser?.uid ?? null, sub.id, 'match').catch(()=>{});
      playSound('match');
      if (nm.size===lItems.length) { lsSet(LS_PROG,null); setTimeout(()=>{ playSound('complete'); setPage('result'); },600); }
    } else {
      setMWrong(x=>x+1); setFlashL(selL); setFlashR(i);
      if (sub) logStudy(authUser?.uid ?? null, sub.id, 'mw').catch(()=>{});
      setTimeout(()=>{setSelL(null);setFlashL(null);setFlashR(null);},500);
    }
  };

  // ── 開始遊戲 ──────────────────────────────────────────────────────────────
  // 重點整理頁的「配對」分頁：載入此主題的配對題數（有快取）；沒有配對題就不顯示分頁
  useEffect(() => {
    if (page !== 'notes' || !sub || !notesTopic) return;
    let alive = true;
    setNotesMatchCount(0);
    loadTopic(sub.id, notesTopic)
      .then(items => { if (alive) setNotesMatchCount(items.filter(q => q.t === 'm').reduce((n, q) => n + (q.p?.length ?? 0), 0)); })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sub?.id, notesTopic]);
  const startGameRef = useRef<(customQList?:QItem[]) => Promise<void>>(async () => {});
  const startGame = async (customQList?:QItem[]) => {
    if (!sub) return;
    // 目前科目是虛擬科目（錯題複習、綜合）卻從主題／單元頁開始測驗（例如練完錯題後按返回鍵回到單元頁）：
    // 切回最後使用的真實科目再開始，成績才會記到正確的科目
    if (!customQList && (sub.id === WRONGS_SUB_ID || sub.id === ALL_SUB_ID) && (unitSel !== null || topic !== 'ALL') && lastRealSubRef.current) {
      setSub(lastRealSubRef.current);
      if (lastRealTopicRef.current) setTopic(lastRealTopicRef.current);
      setRestartTick(t => t + 1);   // 狀態更新後重新開始（用計數器，避免與 pendingStart 同值不觸發）
      return;
    }
    setErr('');
    setPaused(false);
    setPage('loading');
    try {
      let quiz:QItem[], matchPairs:any[];
      // 全部主題的範圍需屬於目前科目才套用
      const scope = topic === 'ALL' && allScope && sub.id !== ALL_SUB_ID && sub.id !== WRONGS_SUB_ID
        && allScope.every(t => (allTopics[sub.id] || []).includes(t)) ? allScope : null;
      if (customQList) {
        quiz = customQList; matchPairs = [];
      } else if (unitSel !== null && mode==='quiz') {
        // 單元題目已在選擇時備妥，免重抓
        quiz = unitSel.items; matchPairs = [];
      } else if (scope) {
        // 全部主題：只讀篩選範圍內的主題（已讀過的主題用暫存）
        const lists = await Promise.all(scope.map(t => loadTopic(sub.id, t)));
        const all = lists.flat();
        quiz = all.filter(q => q.t === 'q');
        matchPairs = all.filter(q => q.t === 'm').flatMap(q => (q.p || []).map((pair:any) => Array.isArray(pair) ? pair : [pair.l, pair.r]));
      } else {
        const res = await fetchQs(sub.id, topic, isGuest
          ? Object.fromEntries(Object.entries(allTopics).filter(([sid]) => allSubjects.find(x => x.id === sid)?.ga))
          : allTopics);
        quiz = res.quiz; matchPairs = res.match;
      }
      if (mode==='quiz') {
        if (!quiz.length) { failStart('此主題暫無題目，請稍後再試。'); return; }
        const sliced = unitSel !== null ? unitSel.items.filter(q=>q.t==='q') : quiz;
        // 歷屆考題：整份依題號出題，題組才不會被拆散
        const isPastExam = sliced.length > 0 && sliced.every(q => q.year != null);
        const byExamOrder = (list: QItem[]) => [...list].sort((a,b) => (a.year! - b.year!) || ((a.num ?? 0) - (b.num ?? 0)));
        let ql: QItem[];
        if (scope && unitSel === null && !customQList) {
          // 全部主題：各主題平均分配、優先還沒答對過的題目
          const n = allCount === 'all' ? Infinity : Number(allCount);
          const picked = pickBalanced(sliced, scope, n, q => !!q.ref && (answers[`${q.ref.s}__${q.ref.t}__${q.ref.id}`]?.corrects ?? 0) > 0);
          ql = isPastExam ? byExamOrder(picked) : picked;
        } else {
          ql = isPastExam ? byExamOrder(sliced) : shuffle(sliced).slice(0, appCfg.quizSize);
        }
        setQList(ql); setQi(0); setSel(null); setShowExp(false);
        setScore(0); setCombo(0); setMaxC(0); setWrongs([]);
        setTimeLeft(TIMER_SEC); setTimerActive(true);
        startTimeRef.current = Date.now();
        setPage('quiz');
      } else {
        if (!matchPairs.length) { failStart('此主題暫無配對題。'); return; }
        initMatch(shuffle(matchPairs).slice(0,8));
        startTimeRef.current = Date.now();
        setPage('match');
      }
    } catch(e) {
      failStart('載入題目失敗，請檢查網路後重試。');
    }
  };

  startGameRef.current = startGame;
  const lastRealSubRef = useRef<Subject|null>(null);
  const lastRealTopicRef = useRef<string>('');
  useEffect(() => {
    if (sub && sub.id !== WRONGS_SUB_ID && sub.id !== ALL_SUB_ID) {
      lastRealSubRef.current = sub;
      if (topic && topic !== 'ALL') lastRealTopicRef.current = topic;
    }
  }, [sub, topic]);
  // 上次學習的主題：開始作答（問答模式、真實科目、單一主題）時記下，首頁提供一鍵繼續
  const lastKey = `gsat_last_topic_${authUser?.uid ?? 'guest'}`;
  const [lastTopic, setLastTopic] = useState<{ s: string; t: string; u?: string; ids?: string[] } | null>(null);
  useEffect(() => {
    if (authLoading) return;
    try { setLastTopic(JSON.parse(localStorage.getItem(lastKey) || 'null')); } catch { setLastTopic(null); }
  }, [lastKey, authLoading]);
  useEffect(() => {
    if (page !== 'quiz' || mode !== 'quiz' || !sub || sub.id === WRONGS_SUB_ID || sub.id === ALL_SUB_ID || !topic || topic === 'ALL') return;
    const rec = { s: sub.id, t: topic, ...(unitSel ? { u: unitSel.label, ids: unitSel.items.flatMap(q => q.ref?.id ? [q.ref.id] : []) } : {}) };
    setLastTopic(rec);
    try { localStorage.setItem(lastKey, JSON.stringify(rec)); } catch { /* 無痕模式等 */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sub?.id, topic, unitSel?.label]);
  const resumeLast = async () => {
    const sb = lastTopic && allSubjects.find(x => x.id === lastTopic.s);
    if (!lastTopic || !sb) return;
    setSub(sb); setTopic(lastTopic.t); setUnitSel(null);
    if (lastTopic.u && lastTopic.ids?.length) {
      try {
        const ids = new Set(lastTopic.ids);
        const items = (await loadTopic(sb.id, lastTopic.t)).filter(q => q.t === 'q' && q.ref && ids.has(q.ref.id));
        if (items.length) setUnitSel({ label: lastTopic.u, items });
      } catch { /* 載入失敗就退回整個主題 */ }
    }
    quickStart('home');
  };
  const [restartTick, setRestartTick] = useState(0);
  useEffect(() => { if (restartTick) startGameRef.current(); }, [restartTick]);

  // ── 快速開始：點主題/單元直接以問答模式開始，不經遊戲設定頁 ──────────────
  // startFrom：載入失敗時要回到的頁面（null 表示從設定頁開始，錯誤顯示在設定頁）
  const [startFrom, setStartFrom] = useState<string|null>(null);
  const [pendingStart, setPendingStart] = useState(false);
  const quickStart = (from: string, m: 'quiz'|'match' = 'quiz') => { setMode(m); setErr(''); setStartFrom(from); setPendingStart(true); };
  useEffect(() => {
    if (!pendingStart) return;
    setPendingStart(false);
    startGameRef.current();
  }, [pendingStart]);
  // 載入失敗：快速開始時提示並回原頁；從設定頁開始則留在設定頁顯示錯誤
  const failStart = (msg: string) => {
    if (startFrom) { setStartFrom(null); toast(msg); setPage(startFrom); }
    else { setErr(msg); setPage('config'); }
  };

  // ── 儲存成績至 Firestore (功能三) ─────────────────────────────────────────
  const saveScore = async (pct:number, sc:number, total:number,
                           over?: { sub: Subject|null; topic: string; mode: string; planned: number }) => {
    const timeTaken = Math.round((Date.now()-startTimeRef.current)/1000);
    const dateStr = dayKey();   // 本地日期（凌晨不會被算成前一天，日曆以此歸日）
    const S = over ? over.sub : sub;
    const record: any = {
      nickname: nickname||'匿名',
      uid: authUser?.uid ?? null,
      avatarUrl: avatarUrl ?? null,
      sub: S?.id||'',         // 科目 ID（關聯用）
      subName: S?.n||'',      // 當下名稱（科目刪除後仍可顯示）
      topic: over ? over.topic : topic,   // 主題 ID（'ALL' 表所有主題）
      mode: over ? over.mode : mode,
      pct, score:sc, total, timeTaken,
      date: dateStr,
    };
    // 中途離開：只計已作答的題目，另記原本的題數
    if (over) { record.partial = true; record.planned = over.planned; }
    // 錯題複習不計入排行榜
    if (S?.id !== WRONGS_SUB_ID) {
      try {
        await addDoc(collection(db,'scores'), {...record, date: serverTimestamp()});
      } catch {}
      // 排行榜「全部期間」用的每人統計（訪客不上榜）
      if (authUser) {
        try {
          const { runTransaction } = await import('firebase/firestore');
          const ref = doc(db, 'scoreStats', authUser.uid);
          await runTransaction(db, async tx => {
            const prev = (await tx.get(ref)).data() as StatDoc | undefined;
            tx.set(ref, addToStat(prev, { sub: record.sub, pct, score: sc, total, timeTaken, ms: Date.now() },
              appCfg.boardMinQ, { nickname: record.nickname, avatarUrl: record.avatarUrl }));
          });
        } catch {}
      }
    }
    // 儲存至個人記錄（Firestore 或 localStorage fallback）
    try {
      await addRecord(record);
    } catch(e) {
      console.warn('Failed to save record locally:', e);
    }
  };

  // ── 載入排行榜 ─────────────────────────────────────────────────────────────

  // ── 恢復進度 (功能四) ─────────────────────────────────────────────────────
  const resumeProgress = () => {
    const p = savedProg;
    if (!p) return;
    setSub(p.sub); setTopic(p.topic); setMode(p.mode);
    setQList(p.qList); setQi(p.qi); setSel(null); setShowExp(false);
    setScore(p.score); setCombo(p.combo); setMaxC(p.maxC); setWrongs(p.wrongs||[]);
    setTimeLeft(TIMER_SEC); setTimerActive(true);
    startTimeRef.current = Date.now();
    setSavedProg(null);
    setPage('quiz');
  };

  const handleReport = async (item: QItem) => {
    if (!authUser) { toast('請先登入才能回報題目'); return; }
    const wasPaused = paused;
    setPaused(true);   // 回報時先暫停計時，寫完再繼續
    const reason = await promptDialog('請簡述這題的錯誤（例如：答案有誤、選項重複、圖片不對）：', { placeholder: '請寫下哪裡有問題', okText: '送出回報' });
    if (!wasPaused) setPaused(false);
    if (!reason) return;
    try {
      const { addDoc, collection: col, serverTimestamp } = await import('firebase/firestore');
      await addDoc(col(db, 'reports'), {
        uid: authUser.uid,
        nickname,
        sub: sub?.id ?? '',
        topic,
        ref: item.ref ?? null,   // 題目位置（科目 ID、主題、題目文件 ID）
        question: item.q ?? JSON.stringify(item.p ?? ''),
        reason,
        createdAt: serverTimestamp(),
      });
      toast('感謝回報！我們會盡快審查。');
    } catch {
      toast('回報失敗，請稍後再試。');
    }
  };

  const handleBatchFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    let parsed;
    let uploads: { path: string; blob: Blob }[] = [];
    let missing: string[] = [];
    try {
      if (file.name.toLowerCase().endsWith('.zip')) {
        const { readZip } = await import('./utils/zipIO');
        ({ incoming: parsed, uploads, missing } = await readZip(file));
      } else {
        parsed = parseBatch(await file.text(), file.name);
      }
    } catch (err) {
      toast('解析失敗：請確認檔案格式正確。' + (err instanceof Error ? `\n${err.message}` : ''));
      return;
    }
    const { getDocs, collection: col } = await import('firebase/firestore');
    const snap = await getDocs(col(db, 'questions', batchSub, 'topics', batchTopic, 'items'));
    const existing = new Map<string, any>(snap.docs.map(d => [d.id, d.data()]));
    // 更新既有題目時可只給部分欄位；新增的題目必須格式完整
    const incoming = parsed.filter(r => (r._id && existing.has(r._id)) || isValid(r.item));
    const invalid = parsed.length - incoming.length;
    if (!incoming.length) { toast('未找到有效題目（選擇題需有題目文字和至少 2 個選項；配對題需有配對內容）。'); return; }
    const plan = planBatch(incoming, existing);
    const media = {
      img: incoming.filter(r => r.item.img).length,
      audio: incoming.filter(r => r.item.audio).length,
    };
    // ZIP 內找得到的檔名會在寫入時上傳，不列為網址警告
    const inZip = new Set(uploads.map(u => u.path));
    const warnings = [
      ...missing.map(m => `ZIP 內找不到檔案：${m}`),
      ...mediaWarnings(incoming.map(r => ({ ...r, item: Object.fromEntries(Object.entries(r.item).filter(([k, v]) => !((k === 'img' || k === 'audio') && (inZip.has(v as string) || missing.includes(v as string))))) }))),
    ];
    setBatchPreview({ ...plan, invalid, warnings, media, uploads, blocked: warnings.length > 0 });
  };

  const confirmBatchImport = async () => {
    if (!batchPreview) return;
    setBatchImporting(true);
    try {
      const { setDoc, addDoc, doc, collection: col } = await import('firebase/firestore');
      const base = col(db, 'questions', batchSub, 'topics', batchTopic, 'items');
      if (batchPreview.uploads.length) {
        setZipBusy(`上傳 ${batchPreview.uploads.length} 個檔案中…`);
        const { uploadZipMedia } = await import('./utils/zipIO');
        const urls = await uploadZipMedia(batchPreview.uploads, batchSub, batchTopic);
        const swap = (it: Record<string, any>) => { ['img','audio'].forEach(k => { if (typeof it[k] === 'string' && urls[it[k]]) it[k] = urls[it[k]]; }); };
        batchPreview.toUpdate.forEach(u => swap(u.item));
        batchPreview.toAdd.forEach(swap);
      }
      await Promise.all([
        // 只寫入有變動的欄位，其他欄位（圖片、音訊等）保留原值
        ...batchPreview.toUpdate.map(({ id, item }) =>
          setDoc(doc(db, 'questions', batchSub, 'topics', batchTopic, 'items', id), item, { merge: true })
        ),
        ...batchPreview.toAdd.map(item => addDoc(base, item)),
      ]);
      toast(`✅ 完成！更新 ${batchPreview.toUpdate.length} 題，新增 ${batchPreview.toAdd.length} 題。`);
      setBatchPreview(null);
      loadAdminItems();   // 匯入後重新載入清單
      recountSubjects([batchSub]).catch(() => {});
    } catch {
      toast('寫入失敗，請稍後再試。');
    } finally {
      setZipBusy('');
      setBatchImporting(false);
    }
  };

  // ── Admin handlers ────────────────────────────────────────────────────────────
  const loadAdminItems = async () => {
    const { getDocs, collection: col } = await import('firebase/firestore');
    const snap = await getDocs(col(db, 'questions', adminSub, 'topics', adminTopic, 'items'));
    setAdminItems(snap.docs.map(d => d.data() as QItem));
    setAdminDocIds(snap.docs.map(d => d.id));
  };

  const [adminItemsLoading, setAdminItemsLoading] = useState(false);
  // 用戶：進入分頁自動載入
  useEffect(() => {
    if (page === 'admin' && adminTab === 'users' && !adminUsersLoaded) loadAdminUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, adminTab]);
  // 題庫：進入分頁或切換科目、主題時自動載入
  useEffect(() => {
    if (page !== 'admin' || adminTab !== 'questions' || !adminSub || !adminTopic) return;
    let alive = true;
    setAdminItemsLoading(true);
    getDocs(collection(db, 'questions', adminSub, 'topics', adminTopic, 'items')).then(snap => {
      if (!alive) return;
      setAdminItems(snap.docs.map(d => d.data() as QItem));
      setAdminDocIds(snap.docs.map(d => d.id));
    }).catch(() => {}).finally(() => { if (alive) setAdminItemsLoading(false); });
    return () => { alive = false; };
  }, [page, adminTab, adminSub, adminTopic]);

  // 從回報清單開啟被回報的題目：切到題庫分頁、載入該主題並打開編輯視窗
  const openReportedQuestion = async (ref: QRef) => {
    const { getDocs, collection: col } = await import('firebase/firestore');
    const snap = await getDocs(col(db, 'questions', ref.s, 'topics', ref.t, 'items'));
    const items = snap.docs.map(d => d.data() as QItem);
    const ids = snap.docs.map(d => d.id);
    setAdminSub(ref.s); setAdminTopic(ref.t);
    setAdminItems(items); setAdminDocIds(ids);
    setAdminTab('questions');
    const i = ids.indexOf(ref.id);
    if (i < 0) { toast('找不到這題，可能已被刪除。'); return; }
    const item = items[i];
    setAdminEdit(i);
    setAdminForm({ t: item.t, q: item.q??'', o: Array.isArray(item.o) ? item.o.map(String) : ['','','',''], a: item.a??0, e: item.e??'', img: item.img??'', noImg: !!item.noImgNeeded, ver: isVerified(item), verTouched: false });
  };

  const startEditItem = (i: number) => {
    const item = adminItems[i];
    setAdminEdit(i);
    setAdminForm({ t: item.t, q: item.q??'', o: Array.isArray(item.o) ? item.o.map(String) : ['','','',''], a: item.a??0, e: item.e??'', img: item.img??'', noImg: !!item.noImgNeeded, ver: isVerified(item), verTouched: false });
  };

  const [imgUploading, setImgUploading] = useState(false);
  const uploadQuestionImage = async (file: File) => {
    setImgUploading(true);
    try {
      const bitmap = await createImageBitmap(file);
      const scale = Math.min(1, 1200 / bitmap.width);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(bitmap.width * scale);
      canvas.height = Math.round(bitmap.height * scale);
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob>((res, rej) => canvas.toBlob(b => b ? res(b) : rej(new Error('壓縮失敗')), 'image/jpeg', 0.85));
      const { ref: sRef, uploadBytes, getDownloadURL } = await import('firebase/storage');
      const path = `question-images/${adminSub}/${Date.now()}_${Math.random().toString(36).slice(2,8)}.jpg`;
      const r = sRef(storage, path);
      await uploadBytes(r, blob, { contentType: 'image/jpeg' });
      const url = await getDownloadURL(r);
      setAdminForm(f => ({...f, img: url}));
    } catch (err: any) {
      toast('圖片上傳失敗：' + (err?.message ?? err));
    } finally {
      setImgUploading(false);
    }
  };

  const renderImgField = () => (
    <div style={{marginBottom:10}}>
      <label style={{fontSize:13,color:'var(--muted)',display:'block',marginBottom:4}}>圖片（選填）</label>
      {adminForm.img && (
        <div style={{position:'relative',marginBottom:6}}>
          <img src={adminForm.img} alt="題目圖片" style={{maxWidth:'100%',borderRadius:8,border:'1px solid var(--border)'}} />
          <Btn onClick={() => setAdminForm(f=>({...f,img:''}))}
               style={{position:'absolute',top:6,right:6,background:'#dc2626',color:'#fff',padding:'2px 8px',borderRadius:6,fontSize:12}}>移除</Btn>
        </div>
      )}
      <input type="file" accept="image/*" disabled={imgUploading}
             onChange={e => { const f = e.target.files?.[0]; if (f) uploadQuestionImage(f); e.target.value=''; }}
             style={{fontSize:13,color:'var(--text)'}} />
      {imgUploading && <span style={{fontSize:12,color:'var(--muted)',marginLeft:8}}>上傳中…</span>}
    </div>
  );

  const saveAdminItem = async () => {
    if (imgUploading) { toast('圖片還在上傳中'); return; }
    dropSubjectTopics(adminSub);
    const { setDoc, addDoc, doc, collection: col } = await import('firebase/firestore');
    // 保留表單以外的欄位（題組文章、章節標籤等）
    const isEdit = adminEdit !== null && adminEdit >= 0;
    const base: QItem = isEdit ? { ...adminItems[adminEdit!] } : { t: adminForm.t };
    delete base.img;
    // 去掉尾端空白選項（三選一題目）
    const o = [...adminForm.o]; while (o.length && !o[o.length - 1]?.trim()) o.pop();
    if (adminForm.t === 'q' && (!adminForm.q.trim() || o.length < 2)) { toast('請填寫題目與至少 2 個選項'); return; }
    if (adminForm.t === 'q' && adminForm.a >= o.length) { toast('請選擇正解'); return; }
    const data: QItem = { ...base, t: adminForm.t, q: adminForm.q, o, a: adminForm.a, e: adminForm.e };
    if (adminForm.img) data.img = adminForm.img;
    if (adminForm.noImg) data.noImgNeeded = true; else delete data.noImgNeeded;
    // 已驗證旗標：勾選時以「目前內容」重新蓋章；沒勾選就移除旗標
    delete data.verified; delete data.verifiedHash; delete data.verifiedAt; delete data.verifiedBy;
    if (adminForm.ver) {
      const keep = isEdit && isVerified(adminItems[adminEdit!]) && contentHash(adminItems[adminEdit!]) === contentHash(data);
      Object.assign(data, keep ? { verified: true, verifiedHash: adminItems[adminEdit!].verifiedHash, verifiedAt: adminItems[adminEdit!].verifiedAt, ...(adminItems[adminEdit!].verifiedBy ? { verifiedBy: adminItems[adminEdit!].verifiedBy } : {}) } : verifiedFields(data, '後台'));
    }
    if (isEdit && adminEdit !== null) {
      await setDoc(doc(db,'questions',adminSub,'topics',adminTopic,'items',adminDocIds[adminEdit]), data);
      const updated = [...adminItems]; updated[adminEdit] = data;
      setAdminItems(updated);
      setAdminEdit(null);
    } else {
      const ref = await addDoc(col(db,'questions',adminSub,'topics',adminTopic,'items'), data);
      setAdminItems(prev => [...prev, data]);
      setAdminDocIds(prev => [...prev, ref.id]);
      setAdminEdit(null);
      recountSubjects([adminSub]).catch(() => {});
    }
    setAdminForm({t:'q',q:'',o:['','','',''],a:0,e:'',img:''});
  };

  // 重算題數並存進科目資料（customSubjects.counts），首頁直接讀取、不必逐主題查詢
  // 後台新增、刪除、匯入後自動呼叫；用腳本直接寫入題庫後需按「重算題數」
  const [recounting, setRecounting] = useState(false);
  const recountSubjects = async (subIds: string[]) => {
    subIds.forEach(dropSubjectTopics);
    const { getCountFromServer } = await import('firebase/firestore');
    const next: Record<string,Record<string,number>> = {};
    for (const sid of subIds) {
      const entries = await Promise.all((allTopics[sid] || []).map(async t =>
        [t, (await getCountFromServer(collection(db, 'questions', sid, 'topics', t, 'items'))).data().count] as const));
      next[sid] = Object.fromEntries(entries);
      await updateDoc(doc(db, 'customSubjects', sid), { counts: next[sid] });
    }
    setCustomCounts(prev => ({ ...prev, ...next }));
    try { localStorage.removeItem(CUSTOM_SUBS_CACHE_KEY); } catch {}
  };

  const deleteAdminItem = async (i: number) => {
    if (!(await confirmDialog('確定刪除此題目？'))) return;
    const { deleteDoc, doc } = await import('firebase/firestore');
    await deleteDoc(doc(db,'questions',adminSub,'topics',adminTopic,'items',adminDocIds[i]));
    setAdminItems(prev => prev.filter((_,idx) => idx !== i));
    setAdminDocIds(prev => prev.filter((_,idx) => idx !== i));
    recountSubjects([adminSub]).catch(() => {});
  };

  // 核准／停用／恢復／刪除帳號
  const setUserStatus = async (uid: string, status: 'approved'|'disabled'|'pending') => {
    await updateDoc(doc(db, 'users', uid), { status });
    setAdminUsers(us => us.map(u => u.uid === uid ? { ...u, status } : u));
    refreshPendingCount();
  };
  const deleteUserData = async (u: {uid:string, nickname?:string, email?:string}) => {
    if (!(await confirmDialog(`確定刪除「${u.nickname || u.email || u.uid}」？\n會清除此人的成績、錯題、作答紀錄與排行榜資料，且無法復原；帳號將無法再使用。`))) return;
    const { deleteDoc: del, writeBatch } = await import('firebase/firestore');
    for (const sub of ['records', 'wrongs', 'answers', 'activity']) {
      const snap = await getDocs(collection(db, 'users', u.uid, sub));
      for (let i = 0; i < snap.docs.length; i += 400) {
        const b = writeBatch(db); snap.docs.slice(i, i + 400).forEach(d => b.delete(d.ref)); await b.commit();
      }
    }
    const sc = await getDocs(query(collection(db, 'scores'), where('uid', '==', u.uid)));
    await Promise.all(sc.docs.map(d => del(d.ref)));
    await del(doc(db, 'publicStats', u.uid)).catch(() => {});
    await del(doc(db, 'scoreStats', u.uid)).catch(() => {});
    await updateDoc(doc(db, 'users', u.uid), { status: 'deleted', avatarUrl: null });
    setAdminUsers(us => us.map(x => x.uid === u.uid ? { ...x, status: 'deleted' } : x));
    refreshPendingCount();
    toast('已刪除此帳號的資料。登入帳號本身需執行 scripts/delete-users.mjs 才會從 Firebase 移除。');
  };

  const loadAdminUsers = async () => {
    const { getDocs, collection: col } = await import('firebase/firestore');
    const snap = await getDocs(col(db, 'users'));
    setAdminUsers(snap.docs.map(d => ({ uid: d.id, ...(d.data() as any) })));
    setAdminUsersLoaded(true);
  };


  const saveCustomSubject = async (): Promise<boolean> => {
    const { id, name, icon, color, bg, topicsText } = adminNewSub;
    if (!id.trim() || !name.trim()) { toast('請填寫 ID 和名稱'); return false; }
    if (!/^[A-Za-z0-9_]+$/.test(id.trim())) { toast('科目 ID 只能使用英文字母、數字與底線'); return false; }
    if (allSubjects.some(s => s.id === id.trim())) { toast('ID 已存在，請使用不同 ID'); return false; }
    const topicList = topicsText.split('\n').map(s=>s.trim()).filter(Boolean);
    if (!topicList.length) { toast('請至少輸入一個主題'); return false; }
    // 主題 ID 依順序編為 t01、t02…；路徑與紀錄用 ID，名稱存於 topicNames
    const topics = topicList.map((_, i) => `t${String(i+1).padStart(2,'0')}`);
    const topicNames = Object.fromEntries(topics.map((t, i) => [t, topicList[i]]));
    setAdminSubSaving(true);
    try {
      const { setDoc: fsSetDoc, doc: fsDoc } = await import('firebase/firestore');
      await fsSetDoc(fsDoc(db, 'customSubjects', id.trim()), {
        id: id.trim(), name, icon, color, bg, topics, topicNames, order: Date.now(),
      });
      setAdminNewSub({id:'',name:'',icon:'📗',color:'#0891b2',bg:'#ecfeff',topicsText:''});
      try { localStorage.removeItem(CUSTOM_SUBS_CACHE_KEY); } catch {}
      loadCustomSubjects();
      return true;
    } catch(e) { toast('儲存失敗：' + String(e)); return false; }
    finally { setAdminSubSaving(false); }
  };


  // ══════════════════════════════════════════════════════════════════════════════
  // Pages
  // ══════════════════════════════════════════════════════════════════════════════

  // 等待 Firebase Auth 初始化
  if (authLoading) return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh'}}>
      <div className="loading-spinner" />
    </div>
  );

  // ── Login ─────────────────────────────────────────────────────────────────
  // ── 帳號尚未核准（或已停用）：只顯示等待畫面 ─────────────────────────────
  if (authUser && !ADMIN_UIDS.includes(authUser.uid) && accountStatus !== 'approved') {
    const recheck = async () => {
      await authUser.reload();
      const u = auth.currentUser!;
      if (u.emailVerified) await u.getIdToken(true);
      const ref = doc(db, 'users', u.uid);
      await updateDoc(ref, { emailVerified: u.emailVerified }).catch(() => {});
      const snap = await getDoc(ref);
      setAccountStatus(((snap.data() as any)?.status) ?? 'pending');
      setAuthUser(Object.assign(Object.create(Object.getPrototypeOf(u)), u));   // 觸發重新渲染（emailVerified 更新）
    };
    const off = accountStatus === 'disabled' || accountStatus === 'deleted';
    return (
      <div style={{minHeight:'100vh',background:'var(--bg)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
        <div style={{maxWidth:380,width:'100%',background:'var(--card)',borderRadius:16,padding:24,textAlign:'center',border:'1px solid var(--border)'}}>
          <div style={{fontSize:44,marginBottom:8}}>{off ? '⛔' : '⏳'}</div>
          <h2 style={{fontSize:18,fontWeight:800,color:'var(--text)',margin:'0 0 8px'}}>{off ? '帳號已停用' : '帳號審核中'}</h2>
          <p style={{fontSize:14,color:'var(--muted)',margin:'0 0 16px',lineHeight:1.6}}>
            {off ? '此帳號已被管理員停用，如有疑問請聯絡管理員。'
              : !authUser.emailVerified ? `請先到 ${authUser.email} 收信完成電子郵件驗證，驗證後管理員才能核准。`
              : '電子郵件已驗證，請等待管理員核准後即可使用。'}
          </p>
          {!off && (
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              <Btn onClick={recheck} style={{background:'var(--accent)',color:'#fff',borderRadius:10,padding:'10px',fontWeight:700}}>重新檢查</Btn>
              {!authUser.emailVerified && (
                <Btn onClick={async()=>{ try { await sendEmailVerification(authUser); toast('驗證信已寄出，請檢查信箱（含垃圾郵件）'); } catch { toast('寄送太頻繁，請稍後再試'); } }}
                  style={{background:'var(--bg2)',color:'var(--text)',borderRadius:10,padding:'10px',fontWeight:600}}>重寄驗證信</Btn>
              )}
            </div>
          )}
          <Btn onClick={async()=>{ await signOut(auth); setGuestMode(false); setAccountStatus('pending'); }}
            style={{background:'none',color:'#dc2626',fontWeight:600,fontSize:14,padding:'12px 0 0'}}>登出</Btn>
        </div>
      </div>
    );
  }

  if (page === 'login') return (
    <div style={{minHeight:'100vh',background:'var(--bg)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'var(--card)',borderRadius:20,padding:36,width:'100%',maxWidth:400,boxShadow:'0 4px 20px rgba(0,0,0,.08)'}}>
        <div style={{textAlign:'center',marginBottom:28}}>
          <div style={{fontSize:48,marginBottom:8}}>🎓</div>
          <h1 style={{fontSize:22,fontWeight:800,color:'var(--text)',margin:0}}>考題怪</h1>
          <p style={{color:'var(--muted)',fontSize:14,marginTop:6,marginBottom:0}}>登入以儲存個人設定與成績</p>
        </div>

        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          <input className="auth-input" type="email" placeholder="電子郵件" value={authEmail}
            onChange={e=>setAuthEmail(e.target.value)} autoComplete="email" />
          <input className="auth-input" type="password" placeholder="密碼" value={authPassword}
            onChange={e=>setAuthPassword(e.target.value)} autoComplete="current-password"
            onKeyDown={e=>{ if(e.key==='Enter') doLogin(); }} />
          {authError && <div style={{color:'#dc2626',fontSize:13,padding:'8px 12px',background:'#fff1f2',borderRadius:8}}>{authError}</div>}
          <Btn onClick={doLogin} disabled={authBusy} className="btn-primary"
            style={{padding:'13px 0',fontSize:16,width:'100%',opacity:authBusy?0.6:1}}>
            {authBusy ? '登入中…' : '登入'}
          </Btn>
        </div>

        <div style={{textAlign:'center',marginTop:20,display:'flex',flexDirection:'column',gap:10}}>
          <span
            style={{color:'var(--accent)', cursor:'pointer', fontSize:13, textAlign:'center' as const}}
            onClick={() => setPage('forgot-pw')}
          >忘記密碼？</span>
          <Btn onClick={()=>{ setAuthError(''); setPage('register'); }}
            style={{background:'none',color:'var(--accent)',fontWeight:600,fontSize:14,padding:0}}>
            還沒有帳號？立即註冊
          </Btn>
          <Btn onClick={()=>{ setAuthError(''); setPage('guest-nick'); }}
            style={{background:'none',color:'var(--muted)',fontSize:13,padding:0}}>
            訪客模式（不儲存設定）
          </Btn>
        </div>
      </div>
    </div>
  );

  // ── Forgot password ───────────────────────────────────────────────────────
  if (page === 'forgot-pw') return (
    <div className="page-enter card" style={{maxWidth:400,margin:'60px auto',padding:28}}>
      <h2 style={{marginBottom:16}}>重設密碼</h2>
      <p style={{color:'var(--muted)',fontSize:14,marginBottom:16}}>
        輸入您的帳號 Email，我們將寄送重設密碼連結。
      </p>
      <input
        className="auth-input"
        type="email"
        placeholder="Email"
        value={fpEmail}
        onChange={e => setFpEmail(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && handleForgotPw()}
      />
      {fpMsg && <p style={{color: fpErr ? '#dc2626':'#16a34a', fontSize:13, marginTop:8}}>{fpMsg}</p>}
      <div style={{display:'flex',gap:10,marginTop:16}}>
        <Btn onClick={() => { setFpEmail(''); setFpMsg(''); setFpErr(false); setPage('login'); }} className="btn-ghost" style={{flex:1,padding:'12px 0'}}>返回</Btn>
        <Btn onClick={handleForgotPw} disabled={fpBusy} className="btn-primary" style={{flex:2,padding:'12px 0',opacity:fpBusy?0.6:1}}>
          {fpBusy ? '寄送中…' : '寄送重設連結'}
        </Btn>
      </div>
    </div>
  );

  // ── Guest nickname ─────────────────────────────────────────────────────────
  if (page === 'guest-nick') return (
    <div style={{minHeight:'100vh',background:'var(--bg)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'var(--card)',borderRadius:20,padding:36,width:'100%',maxWidth:400,boxShadow:'0 4px 20px rgba(0,0,0,.08)'}}>
        <div style={{textAlign:'center',marginBottom:28}}>
          <div style={{fontSize:48,marginBottom:8}}>👤</div>
          <h2 style={{fontSize:20,fontWeight:800,color:'var(--text)',margin:0}}>訪客模式</h2>
          <p style={{color:'var(--muted)',fontSize:14,marginTop:6,marginBottom:0}}>輸入暱稱，成績將顯示在排行榜</p>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <input className="auth-input" type="text" maxLength={12} placeholder="暱稱（最多 12 字）"
            value={nickname} onChange={e=>setNickname(e.target.value)}
            onKeyDown={e=>{ if(e.key==='Enter'){ setGuestMode(true); setPage('home'); } }}
            autoFocus />
          <Btn onClick={()=>{ setGuestMode(true); setPage('home'); }} className="btn-primary"
            style={{padding:'13px 0',fontSize:16,width:'100%'}}>
            開始遊戲
          </Btn>
          <Btn onClick={()=>{ setPage('login'); }}
            style={{background:'none',color:'var(--muted)',fontSize:13,padding:0,textAlign:'center' as const}}>
            ← 返回登入
          </Btn>
        </div>
      </div>
    </div>
  );

  // ── Register ──────────────────────────────────────────────────────────────
  if (page === 'register') return (
    <div style={{minHeight:'100vh',background:'var(--bg)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'var(--card)',borderRadius:20,padding:36,width:'100%',maxWidth:400,boxShadow:'0 4px 20px rgba(0,0,0,.08)'}}>
        <div style={{textAlign:'center',marginBottom:28}}>
          <div style={{fontSize:48,marginBottom:8}}>🎓</div>
          <h1 style={{fontSize:22,fontWeight:800,color:'var(--text)',margin:0}}>建立帳號</h1>
          <p style={{color:'var(--muted)',fontSize:14,marginTop:6,marginBottom:0}}>需要驗證電子郵件才能完成註冊</p>
        </div>

        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          <input className="auth-input" type="email" placeholder="電子郵件" value={authEmail}
            onChange={e=>setAuthEmail(e.target.value)} autoComplete="email" />
          <input className="auth-input" type="password" placeholder="密碼（至少 6 位）" value={authPassword}
            onChange={e=>setAuthPassword(e.target.value)} autoComplete="new-password" />
          <input className="auth-input" type="password" placeholder="確認密碼" value={authConfirm}
            onChange={e=>setAuthConfirm(e.target.value)} autoComplete="new-password"
            onKeyDown={e=>{ if(e.key==='Enter') doRegister(); }} />
          {authError && <div style={{color:'#dc2626',fontSize:13,padding:'8px 12px',background:'#fff1f2',borderRadius:8}}>{authError}</div>}
          <Btn onClick={doRegister} disabled={authBusy} className="btn-primary"
            style={{padding:'13px 0',fontSize:16,width:'100%',opacity:authBusy?0.6:1}}>
            {authBusy ? '建立中…' : '建立帳號'}
          </Btn>
        </div>

        <div style={{textAlign:'center',marginTop:20}}>
          <Btn onClick={()=>{ setAuthError(''); setPage('login'); }}
            style={{background:'none',color:'var(--accent)',fontWeight:600,fontSize:14,padding:0}}>
            已有帳號？登入
          </Btn>
        </div>
      </div>
    </div>
  );

  // ── 首頁 ──────────────────────────────────────────────────────────────────
  if (page==='home') return (
    <div className="page-enter" onClick={()=>startBgm()} style={{minHeight:'100vh',background:'var(--bg)',padding:'24px 16px',display:'flex',flexDirection:'column',alignItems:'center',position:'relative'}}>
      <div style={{maxWidth:460,width:'100%'}}>
        {/* 頂列：左側標題，右側深淺色、重新整理、個人 */}
        <div onClick={e=>e.stopPropagation()} style={{display:'flex',alignItems:'center',gap:2,marginBottom:20}}>
          <h1 style={{fontSize:20,fontWeight:800,color:'var(--text)',margin:0}}>考題怪</h1>
          {/* 版本編號與版本記錄在「個人設定」頁最下方 */}
          <span style={{flex:1}} />
          <Btn onClick={()=>{
            const want = !darkMode;
            const sibling = THEMES.find(t=>t.dark===want && t.accent===currentTheme.accent)
                          ?? THEMES.find(t=>t.dark===want)!;
            setThemeId(sibling.id);
          }} style={topBtn} aria-label={darkMode?'切換為淺色模式':'切換為深色模式'} title={darkMode?'淺色模式':'深色模式'}>{darkMode?'☀️':'🌙'}</Btn>
          <Btn onClick={forceRefresh} style={topBtn} aria-label="強制重新整理" title="強制重新整理（載入最新版本與資料）">🔄</Btn>
          {/* 後台管理（僅管理員）；待核准帳號數顯示在右上角 */}
          {authUser && ADMIN_UIDS.includes(authUser.uid) && (
            <Btn onClick={()=>setPage('admin')} aria-label="後台管理" title="後台管理" style={{...topBtn,position:'relative'}}>
              🛠️
              {pendingCount + reportCount > 0 && (
                <span title={[pendingCount > 0 && `${pendingCount} 個帳號待核准`, reportCount > 0 && `${reportCount} 筆回報`].filter(Boolean).join('、')} style={{position:'absolute',top:-2,right:-4,minWidth:16,height:16,borderRadius:8,background:'#dc2626',color:'#fff',fontSize:10,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 3px'}}>{pendingCount + reportCount}</span>
              )}
            </Btn>
          )}
          <Btn onClick={()=>setPage('profile')} title="個人設定" aria-label="個人設定"
            style={{background:'none',padding:2,borderRadius:99,display:'flex',alignItems:'center',position:'relative'}}>
            {authUser && avatarUrl
              ? <img src={avatarUrl} alt="" style={{width:34,height:34,borderRadius:99,objectFit:'cover',border:'2px solid var(--border)'}}/>
              : <span style={{fontSize:22,padding:'4px 6px'}}>{authUser ? '👤' : '⚙️'}</span>}
          </Btn>
        </div>

        {/* 快速功能：一排小按鈕（置頂） */}
        <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:14}}>
          <Btn onClick={()=>setStrokeText('')} className="card-hover" style={pillBtn}>✍️ 筆順</Btn>
          <Btn onClick={()=>openDict()} className="card-hover" style={pillBtn}>📖 字典</Btn>
          <Btn onClick={()=>{setSub({id:ALL_SUB_ID,n:'綜合',i:'🎯',c:'#1e293b',b:'#f1f5f9'});setTopic('ALL');setUnitSel(null);quickStart('home');}}
            className="card-hover" style={pillBtn}>🎯 隨機</Btn>
          {(globalWrongs.length>0 || everWrongOf().length>0) && (
            <Btn onClick={()=>{ setWrongMode('now'); setPage('wrongs'); }} className="card-hover" style={{...pillBtn,color:'#dc2626'}}>🔁 錯題 {globalWrongs.length}</Btn>
          )}
          <Btn onClick={()=>{setPage('leaderboard');}} className="card-hover" style={pillBtn}>🏆 排行</Btn>
          <Btn onClick={()=>{ setAnalysisTarget(null); setPage('stats'); }} className="card-hover" style={pillBtn}>📊 分析</Btn>
        </div>

        {/* 電子郵件驗證提示 */}
        {authUser && !authUser.emailVerified && (
          <div style={{background:'#fef9c3',border:'1px solid #fde047',borderRadius:12,padding:'12px 16px',marginBottom:16,display:'flex',alignItems:'center',gap:12}}>
            <span style={{fontSize:20}}>✉️</span>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,color:'#854d0e',fontSize:14}}>請驗證您的電子郵件</div>
              <div style={{color:'#a16207',fontSize:12}}>{authUser.email}</div>
            </div>
            <Btn onClick={async()=>{
              try {
                await sendEmailVerification(authUser!);
                toast('驗證信已發送，請檢查您的信箱（含垃圾郵件）');
              } catch(e:any) {
                if (e?.code==='auth/too-many-requests') toast('發送次數過多，請稍後再試');
                else toast('發送失敗：' + (e?.message ?? e));
              }
            }} style={{background:'#854d0e',color:'white',borderRadius:8,padding:'6px 12px',fontWeight:700,fontSize:12}}>
              重新發送
            </Btn>
          </div>
        )}

        {/* 繼續進度橫幅 (功能四) */}
        {savedProg && (
          <div style={{background:'#fef9c3',border:'1px solid #fde047',borderRadius:12,padding:'12px 16px',marginBottom:16,display:'flex',alignItems:'center',gap:12}}>
            <span style={{fontSize:22}}>🔄</span>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,color:'#854d0e',fontSize:14}}>繼續上次的遊戲</div>
              <div style={{color:'#a16207',fontSize:12}}>{savedProg.sub?.n} · {topicNameOf(savedProg.sub?.id, savedProg.topic)} · 第{savedProg.qi+1}題</div>
            </div>
            <Btn onClick={resumeProgress} style={{background:'#854d0e',color:'white',borderRadius:8,padding:'6px 14px',fontWeight:700,fontSize:13}}>繼續</Btn>
            <Btn onClick={()=>{lsSet(LS_PROG,null);setSavedProg(null);}} style={{background:'none',color:'#a16207',fontWeight:600,fontSize:12,padding:'4px'}}>✕</Btn>
          </div>
        )}

        {/* 每日目標與今日複習任務 */}
        {(() => {
          const today = dateKey();
          const count = daily.date === today ? daily.count : 0;
          const done = daily.date === today && daily.taskDone;
          const reached = count >= daily.goal;
          const task = pickDailyTask(globalWrongs.flatMap(w => w.ref ? [w.ref] : []), answers, Date.now(), TASK_SIZE, s => !!progressTopics[s]);
          if (!answersReady) return null;
          return (
            <div onClick={e=>e.stopPropagation()} style={{background:'var(--card)',border:`1px solid ${reached?'#22c55e':'var(--border)'}`,borderRadius:12,padding:'12px 14px',marginBottom:14}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                <span style={{fontWeight:700,fontSize:14,color:'var(--text)'}}>{reached ? '🎯 今日目標達成' : '🎯 今日目標'}</span>
                <span style={{flex:1,textAlign:'right',fontSize:13,color:'var(--muted)'}}>{count} / {daily.goal} 題</span>
                <Btn onClick={()=>setGoalEdit(v=>!v)} aria-label="設定每日目標" title="設定每日目標" style={{background:'none',fontSize:14,padding:'0 2px'}}>⚙️</Btn>
              </div>
              <div style={{height:8,borderRadius:99,background:'var(--border)',overflow:'hidden'}}>
                <div style={{height:'100%',width:`${Math.min(100,count/daily.goal*100)}%`,background:reached?'#22c55e':'var(--accent)',transition:'width .4s'}}/>
              </div>
              {goalEdit && (
                <div style={{display:'flex',gap:6,marginTop:10,alignItems:'center'}}>
                  <span style={{fontSize:12,color:'var(--muted)'}}>每天</span>
                  {GOAL_OPTIONS.map(n => (
                    <Btn key={n} onClick={()=>{ setDaily(d=>({...d,goal:n})); saveGoal(authUser?.uid ?? null, n).catch(()=>{}); setGoalEdit(false); }}
                      style={{padding:'4px 12px',borderRadius:99,fontSize:13,fontWeight:600,
                              background:daily.goal===n?'var(--accent)':'var(--bg)',color:daily.goal===n?'#fff':'var(--text)',border:'1px solid var(--border)'}}>{n}</Btn>
                  ))}
                  <span style={{fontSize:12,color:'var(--muted)'}}>題</span>
                </div>
              )}
              {(task.refs.length > 0 || done) && (
                <div style={{display:'flex',alignItems:'center',gap:10,marginTop:12,paddingTop:10,borderTop:'1px solid var(--border)'}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:14,color:'var(--text)'}}>{done ? '✅ 今日複習完成' : `📌 今日複習 ${task.refs.length} 題`}</div>
                    {!done && <div style={{fontSize:12,color:'var(--muted)'}}>{[task.wrong>0&&`錯題 ${task.wrong}`, task.stale>0&&`久未練習 ${task.stale}`].filter(Boolean).join('、')}</div>}
                  </div>
                  {!done && <Btn onClick={()=>practiceRefs(task.refs, DAILY_TASK_LABEL)} style={{background:'var(--accent)',color:'#fff',borderRadius:8,padding:'7px 16px',fontWeight:700,fontSize:13}}>開始</Btn>}
                </div>
              )}
            </div>
          );
        })()}

        {/* 上次學習的主題：一鍵繼續 */}
        {(() => {
          const sb = lastTopic && allSubjects.find(x => x.id === lastTopic.s && (!isGuest || x.ga));
          if (savedProg) return null;   // 已有「繼續上次的遊戲」橫幅（會回到中斷的那一題）時不重複顯示
          if (!lastTopic || !sb || !(allTopics[sb.id] ?? []).includes(lastTopic.t)) return null;
          return (
            <div style={{display:'flex',alignItems:'center',gap:10,background:'var(--card)',border:'1px solid var(--border)',borderRadius:12,padding:'12px 14px',marginBottom:14}}>
              <span style={{fontSize:22,flexShrink:0}}>{sb.i}</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,color:'var(--muted)'}}>上次學習</div>
                <div style={{fontWeight:700,fontSize:14,color:'var(--text)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                  {sb.n} · {topicNameOf(sb.id, lastTopic.t)}{lastTopic.u ? ` › ${lastTopic.u}` : ''}
                </div>
              </div>
              <Btn onClick={resumeLast} style={{background:'var(--accent)',color:'#fff',borderRadius:8,padding:'7px 16px',fontWeight:700,fontSize:13,flexShrink:0}}>繼續</Btn>
            </div>
          );
        })()}

        {/* 類別篩選（複選）：依科目卡片短名自動產生 */}
        {(() => {
          const kinds = Array.from(new Set(subjectGroups.flatMap(g => g.subs.map(sb => sb.lb || sb.n))));
          if (kinds.length < 2) return null;
          return (
            <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:14}}>
              {kinds.map(k => {
                const on = homeKinds.includes(k);
                return (
                  <Btn key={k} onClick={()=>toggleHomeKind(k)}
                    style={{padding:'6px 12px',borderRadius:99,fontSize:13,fontWeight:600,
                            background:on?'var(--accent)':'var(--card)',color:on?'#fff':'var(--text)',
                            border:`1px solid ${on?'var(--accent)':'var(--border)'}`}}>
                    {on ? '✓ ' : ''}{k}
                  </Btn>
                );
              })}
              {homeKinds.length > 0 && (
                <Btn onClick={()=>{ setHomeKinds([]); try { localStorage.setItem('gsat_home_kinds','[]'); } catch {} }}
                  style={{padding:'6px 10px',borderRadius:99,fontSize:12,background:'none',color:'var(--muted)'}}>清除</Btn>
              )}
            </div>
          );
        })()}

        {subjectGroups
          .map(g => ({ ...g, subs: homeKinds.length ? g.subs.filter(sb => homeKinds.includes(sb.lb || sb.n)) : g.subs }))
          .filter(g => g.subs.length)
          .map(g=>(
        <div key={g.id} style={{marginBottom:14}}>
          <div style={{display:'flex',alignItems:'center',gap:8,fontWeight:800,fontSize:15,color:g.subs[0].c,margin:'0 0 8px 2px'}}>
            <span style={{width:4,height:16,borderRadius:2,background:g.subs[0].c}}/>{g.name}
          </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(96px,1fr))',gap:8}}>
          {g.subs.map(s=>(
            <Btn key={s.id} onClick={()=>{setSub(s);setTopic('');setTopicCatSel('');setTopicQuery('');setPage('topic');}} className="card-hover"
              style={{background:'var(--card)',border:`1px solid ${s.c}30`,borderTop:`4px solid ${s.c}`,borderRadius:12,padding:'14px 6px 10px',textAlign:'center',boxShadow:'0 1px 3px rgba(15,23,42,0.06)',transition:'transform 0.2s ease, box-shadow 0.2s ease'}}>
              <div style={{fontWeight:700,color:s.c,fontSize:14}}>{s.lb || s.n}</div>
              {progress[s.id] && (
                <>
                  <span style={{fontSize:10,opacity:0.65,display:'block',marginTop:3,color:s.c}}>
                    {progress[s.id].done}/{progress[s.id].total}
                    {subAvgPct[s.id] !== undefined && (
                      <span style={{marginLeft:6,fontWeight:700,color:pctClr(subAvgPct[s.id]),opacity:1}}>· 答對率 {subAvgPct[s.id]}%</span>
                    )}
                  </span>
                  <div style={{height:5,background:`${s.c}20`,borderRadius:99,overflow:'hidden',marginTop:5}}>
                    <div style={{height:'100%',borderRadius:99,background:s.c,transition:'width 0.4s ease',
                      width:`${progress[s.id].total ? Math.min(100, progress[s.id].done/progress[s.id].total*100) : 0}%`}}/>
                  </div>
                </>
              )}
            </Btn>
          ))}
        </div>
        </div>
        ))}
      </div>
      {strokeLayer}
    </div>
  );

  // ── 排行榜頁 (功能三) ─────────────────────────────────────────────────────
  // ── Profile ───────────────────────────────────────────────────────────────
  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !authUser) return;
    if (file.size > 5 * 1024 * 1024) { toast('圖片大小不能超過 5MB'); return; }
    setUploading(true);
    try {
      // 壓縮至 300x300，輸出 base64 JPEG（約 30-60KB），存入 Firestore
      const base64 = await new Promise<string>((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          const SIZE = 300;
          const canvas = document.createElement('canvas');
          canvas.width = SIZE; canvas.height = SIZE;
          const ctx = canvas.getContext('2d')!;
          const scale = Math.max(SIZE / img.width, SIZE / img.height);
          const w = img.width * scale, h = img.height * scale;
          ctx.drawImage(img, (SIZE - w) / 2, (SIZE - h) / 2, w, h);
          URL.revokeObjectURL(url);
          resolve(canvas.toDataURL('image/jpeg', 0.75));
        };
        img.onerror = reject;
        img.src = url;
      });
      await updateDoc(doc(db, 'users', authUser.uid), { avatarUrl: base64 });
      setAvatarUrl(base64);
    } catch(err) {
      console.error(err);
      toast('上傳失敗，請稍後再試');
    } finally {
      setUploading(false);
    }
  };

  if (page === 'profile') {
    return (
      <div style={{minHeight:'100vh',background:'var(--bg)',padding:16}}>
        <div style={{maxWidth:480,margin:'0 auto'}}>
          <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:24,paddingTop:8}}>
            <Btn onClick={()=>setPage('home')} style={{background:'var(--card)',borderRadius:10,padding:'8px 14px',fontSize:14,color:'var(--text)',fontWeight:600}}>
              ← 返回
            </Btn>
            <h2 style={{margin:0,fontSize:20,fontWeight:800,color:'var(--text)',flex:1}}>個人設定</h2>
            {/* 自動儲存狀態 */}
            <span style={{fontSize:12,color:profileSaveState==='error'?'#dc2626':'var(--muted)'}}>
              {!authUser ? '僅存在此裝置' : profileSaveState==='saving' ? '儲存中…' : profileSaveState==='saved' ? '✓ 已自動儲存' : profileSaveState==='error' ? '儲存失敗' : '變更會自動儲存'}
            </span>
          </div>

          <div style={{background:'var(--card)',borderRadius:20,padding:24,boxShadow:'0 1px 4px rgba(0,0,0,.06)',display:'flex',flexDirection:'column',gap:20}}>
            <div style={{textAlign:'center'}}>
              <div
                style={{position:'relative',display:'inline-block',cursor: authUser ? 'pointer' : 'default'}}
                onClick={() => authUser && !uploading && document.getElementById('avatar-input')?.click()}
              >
                {avatarUrl
                  ? <img src={avatarUrl} alt="avatar"
                         style={{width:72,height:72,borderRadius:'50%',objectFit:'cover',border:'3px solid var(--accent)'}} />
                  : <div style={{width:72,height:72,borderRadius:'50%',background:'var(--accent)',
                                 display:'flex',alignItems:'center',justifyContent:'center',
                                 fontSize:28,color:'#fff',fontWeight:700}}>
                      {nickname.charAt(0).toUpperCase() || '?'}
                    </div>
                }
                {authUser && (
                  <div style={{position:'absolute',bottom:0,right:0,background:'var(--accent)',borderRadius:'50%',
                               width:22,height:22,display:'flex',alignItems:'center',justifyContent:'center',
                               fontSize:12,color:'#fff',boxShadow:'0 0 0 2px var(--bg)'}}>
                    ✏️
                  </div>
                )}
              </div>
              {authUser && (
                <>
                  <input id="avatar-input" type="file" accept="image/*" style={{display:'none'}}
                         onChange={handleAvatarUpload} />
                  {uploading && <p style={{fontSize:12,color:'var(--muted)',marginTop:4}}>上傳中…</p>}
                </>
              )}
              {authUser && <div style={{color:'var(--muted)',fontSize:13,marginTop:8}}>{authUser.email}</div>}
            </div>

            <div>
              <label style={{fontSize:13,fontWeight:600,color:'var(--muted)',display:'block',marginBottom:6}}>暱稱</label>
              <input className="auth-input" type="text" maxLength={12} placeholder="最多 12 個字"
                value={nickname} onChange={e=>setNickname(e.target.value)} />
            </div>

            <div>
              <label style={{fontSize:13,fontWeight:600,color:'var(--muted)',display:'block',marginBottom:10}}>主題</label>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(128px,1fr))',gap:8}}>
                {THEMES.map(t=>(
                  <Btn key={t.id} onClick={()=>setThemeId(t.id)}
                    style={{
                      padding:'10px 8px', borderRadius:10, textAlign:'left' as const,
                      background: themeId===t.id ? 'var(--accent)' : 'var(--card)',
                      color:      themeId===t.id ? '#fff' : 'var(--text)',
                      border:     `2px solid ${themeId===t.id ? 'var(--accent)' : 'var(--border)'}`,
                      fontSize:13, display:'flex', alignItems:'center', gap:6,
                    }}>
                    <span style={{flex:1}}>{t.name}</span>
                    <span style={{width:10,height:10,borderRadius:'50%',background:t.accent,flexShrink:0,display:'inline-block'}}/>
                  </Btn>
                ))}
              </div>
            </div>

            <div>
              <label style={{fontSize:13,fontWeight:600,color:'var(--muted)',display:'block',marginBottom:10}}>作答與解說時間</label>
              {([
                ['normal','一般題','作答時間'],
                ['gsat','會考歷屆題',''],
                ['listen','聽力題',''],
                ['correctDelay','答對後','看解說停留（時間到自動下一題；選「手動繼續」則按鈕才跳）'],
                ['wrongDelay','答錯後',''],
              ] as [keyof Timing,string,string][]).map(([k,label,head])=>{
                const isLimit = k==='normal'||k==='gsat'||k==='listen';
                const opts = isLimit ? TIMING_OPTIONS : DELAY_OPTIONS;
                return (
                  <React.Fragment key={k}>
                  {head && <div style={{fontSize:12,color:'var(--muted)',margin:'8px 0 6px'}}>{head}</div>}
                  <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
                    <span style={{flex:1,minWidth:0,fontSize:14,color:'var(--text)'}}>{label}</span>
                    <select value={timing[k]} onChange={e=>setTiming(t=>({...t,[k]:Number(e.target.value)}))}
                      style={{width:'6.5em',flexShrink:0,padding:'6px 8px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg2)',color:'var(--text)',fontSize:14}}>
                      {opts.map(v=>(
                        <option key={v} value={v}>
                          {v===0 ? (isLimit ? '不限時' : '手動繼續') : `${v} 秒`}
                        </option>
                      ))}
                    </select>
                  </div>
                  </React.Fragment>
                );
              })}
              <p style={{fontSize:12,color:'var(--muted)',margin:'4px 0 6px',lineHeight:1.5}}>
                需要較多時間時可隨時暫停：作答中按左上角 ⏸，看解說時按「⏸ 暫停」停止自動跳題。
              </p>
              <Btn onClick={()=>{ const t=appCfg.timingDefault; setTiming({...DEFAULT_TIMING, normal:t, gsat:t, listen:t}); }}
                style={{fontSize:12,color:'var(--muted)',background:'none',padding:'4px 0',textDecoration:'underline'}}>
                恢復預設（作答 {appCfg.timingDefault || '不限'} 秒、答對 {DEFAULT_TIMING.correctDelay} 秒後跳題、答錯手動繼續）</Btn>
            </div>

            <div style={{marginBottom:16}}>
              <label style={{fontSize:13,fontWeight:600,color:'var(--muted)',display:'block',marginBottom:8}}>
                字型大小：{fontSize}px
              </label>
              <div style={{display:'flex', alignItems:'center', gap:12}}>
                <Btn onClick={() => setFontSize(s => Math.max(12, s-2))}
                     style={{width:36,height:36,borderRadius:8,background:'var(--border)',color:'var(--text)',fontSize:18,padding:0,display:'flex',alignItems:'center',justifyContent:'center'}}>A-</Btn>
                <input
                  type="range" min={12} max={24} step={2} value={fontSize}
                  onChange={e => setFontSize(Number(e.target.value))}
                  style={{flex:1,minWidth:0}}
                />
                <Btn onClick={() => setFontSize(s => Math.min(24, s+2))}
                     style={{width:36,height:36,borderRadius:8,background:'var(--border)',color:'var(--text)',fontSize:18,padding:0,display:'flex',alignItems:'center',justifyContent:'center'}}>A+</Btn>
              </div>
              <p style={{fontSize:13,color:'var(--muted)',marginTop:6}}>範例：這是題目文字的預覽效果。</p>
            </div>


            {/* 聲音：音效與背景音樂分開控制 */}
            <div style={{marginBottom:16}}>
              <label style={{fontSize:13,fontWeight:600,color:'var(--muted)',display:'block',marginBottom:8}}>🔊 聲音</label>
              <label style={soundRow}>
                <span>音效（答對、答錯提示音）</span>
                <input type="checkbox" checked={!muted} onChange={e=>setMuted(!e.target.checked)} style={{width:20,height:20}}/>
              </label>
              <label style={soundRow}>
                <span>背景音樂</span>
                <input type="checkbox" checked={bgmOn} onChange={e=>setBgmOn(e.target.checked)} style={{width:20,height:20}}/>
              </label>
              <select value={bgmTrack} disabled={!bgmOn}
                onChange={e=>{ const i=Number(e.target.value); setBgmTrack(i); setBgmTrackState(i); stopBgm(); setTimeout(startBgm, 10); }}
                style={{width:'100%',padding:'8px 10px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg2)',color:'var(--text)',fontSize:14,opacity:bgmOn?1:0.45}}>
                {BGM_TRACKS.map((track, i) => <option key={i} value={i}>{track.name}</option>)}
              </select>
              {ttsSupported() ? (<>
                <label style={{...soundRow, marginTop:12}}>
                  <span>朗讀速度（🔊 按鈕）</span>
                  <select value={ttsRate} onChange={e=>{ const v=Number(e.target.value); setTtsRate(v); try { localStorage.setItem(LS_TTS_RATE, String(v)); } catch {} }}
                    style={{padding:'6px 8px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg2)',color:'var(--text)',fontSize:14}}>
                    {TTS_RATES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </label>
                <label style={soundRow}>
                  <span>閃卡翻面自動朗讀</span>
                  <input type="checkbox" checked={ttsAuto} onChange={e=>{ setTtsAuto(e.target.checked); try { localStorage.setItem(LS_TTS_AUTO, String(e.target.checked)); } catch {} }} style={{width:20,height:20}}/>
                </label>
              </>) : (
                <p style={{fontSize:12,color:'var(--muted)',margin:'10px 0 0'}}>此裝置不支援朗讀功能</p>
              )}
            </div>


            {/* 其他：後台、版本 */}
            <div style={{marginTop:24,paddingTop:16,borderTop:'1px solid var(--border)',display:'flex',flexDirection:'column',gap:10}}>
              {authUser && ADMIN_UIDS.includes(authUser.uid) && (
                <Btn onClick={() => setPage('admin')}
                     style={{width:'100%',background:'#334155',color:'#fff',padding:'10px',borderRadius:8,fontSize:14}}>
                  ⚙️ 後台管理
                </Btn>
              )}
              <p onClick={()=>setPage('about')} style={{color:'var(--muted)',fontSize:12,margin:0,cursor:'pointer',textAlign:'center',textDecoration:'underline'}}>v{APP_VERSION}（{BUILD_INFO}）· 版本記錄</p>
            </div>


            {authUser && (
              <Btn onClick={async()=>{ await signOut(auth); setGuestMode(false); }}
                style={{background:'none',color:'#dc2626',fontWeight:600,fontSize:14,padding:'8px 0',width:'100%'}}>
                登出
              </Btn>
            )}
            {!authUser && guestMode && (
              <Btn onClick={()=>{ setGuestMode(false); }}
                style={{background:'none',color:'#dc2626',fontWeight:600,fontSize:14,padding:'8px 0',width:'100%'}}>
                離開訪客模式
              </Btn>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (page === 'admin') {
    const isAdmin = authUser && ADMIN_UIDS.includes(authUser.uid);
    if (!isAdmin) return (
      <div style={{textAlign:'center',marginTop:60}}>
        <p>權限不足</p>
        <Btn onClick={() => setPage('home')} style={{marginTop:16,background:'var(--accent)',color:'#fff',padding:'8px 16px',borderRadius:8}}>返回首頁</Btn>
      </div>
    );

    return (
      <>
      {/* Batch import preview modal */}
      {batchPreview !== null && createPortal(
        <div onClick={e => { if (e.target === e.currentTarget) setBatchPreview(null); }}
             style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:'var(--card)',borderRadius:16,padding:24,width:'100%',maxWidth:420,maxHeight:'85vh',overflowY:'auto'}}>
            <h3 style={{margin:'0 0 16px',fontSize:16,fontWeight:700,color:'var(--text)'}}>批次匯入預覽</h3>
            <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:20}}>
              <div style={{display:'flex',justifyContent:'space-between',padding:'10px 14px',background:'var(--bg2)',borderRadius:8}}>
                <span style={{color:'var(--text)'}}>✏️ 修改</span>
                <span style={{fontWeight:700,color:'var(--accent)'}}>{batchPreview.toUpdate.length} 題</span>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',padding:'10px 14px',background:'var(--bg2)',borderRadius:8}}>
                <span style={{color:'var(--text)'}}>➕ 新增</span>
                <span style={{fontWeight:700,color:'#22c55e'}}>{batchPreview.toAdd.length} 題</span>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',padding:'10px 14px',background:'var(--bg2)',borderRadius:8}}>
                <span style={{color:'var(--muted)'}}>⏭ 略過（內容相同）</span>
                <span style={{fontWeight:700,color:'var(--muted)'}}>{batchPreview.skipped} 題</span>
              </div>
              {batchPreview.invalid > 0 && (
                <div style={{padding:'8px 14px',background:'#fef3c7',borderRadius:8,fontSize:13,color:'#92400e'}}>
                  ⚠️ {batchPreview.invalid} 筆格式不完整，將略過
                </div>
              )}
              {(batchPreview.media.img > 0 || batchPreview.media.audio > 0) && (
                <div style={{fontSize:13,color:'var(--muted)'}}>含 🖼 圖片 {batchPreview.media.img} 題 · 🔊 音訊 {batchPreview.media.audio} 題</div>
              )}
              {batchPreview.uploads.length > 0 && (
                <div style={{maxHeight:100,overflowY:'auto',fontSize:12,color:'var(--text)',background:'var(--bg2)',borderRadius:8,padding:'8px 12px'}}>
                  📤 將上傳 {batchPreview.uploads.length} 個檔案：{batchPreview.uploads.map(u => u.path).join('、')}
                </div>
              )}
              {batchPreview.toUpdate.length > 0 && (
                <div style={{maxHeight:140,overflowY:'auto',fontSize:12,color:'var(--text)',background:'var(--bg2)',borderRadius:8,padding:'8px 12px'}}>
                  {batchPreview.toUpdate.map(u => (
                    <div key={u.id}>✏️ {u.id.slice(0,8)}：{u.changed.map(k => FIELD_LABEL[k] ?? k).join('、')}</div>
                  ))}
                </div>
              )}
              {batchPreview.warnings.length > 0 && (
                <div style={{maxHeight:100,overflowY:'auto',fontSize:12,color:'#dc2626',background:'#fef2f2',borderRadius:8,padding:'8px 12px'}}>
                  {batchPreview.warnings.map((w,i) => <div key={i}>⚠️ {w}</div>)}
                </div>
              )}
            </div>
            <div style={{display:'flex',gap:10}}>
              <Btn onClick={() => setBatchPreview(null)}
                   style={{flex:1,padding:'10px',borderRadius:8,background:'var(--bg2)',color:'var(--text)',fontWeight:600}}>
                取消
              </Btn>
              <Btn onClick={confirmBatchImport} disabled={batchImporting || batchPreview.blocked}
                   style={{flex:2,padding:'10px',borderRadius:8,background:'var(--accent)',color:'#fff',fontWeight:700,
                           opacity:batchImporting?0.6:1,cursor:batchImporting?'default':'pointer'}}>
                {batchPreview.blocked ? '請先修正上方警告' : batchImporting ? (zipBusy || '寫入中…') : `確認寫入（${batchPreview.toUpdate.length + batchPreview.toAdd.length} 題）`}
              </Btn>
            </div>
          </div>
        </div>,
        document.body
      )}
      {/* Edit modal — rendered via portal to document.body to bypass any ancestor CSS */}
      {adminEdit !== null && createPortal(
        <div onClick={e => { if (e.target === e.currentTarget) { setAdminEdit(null); setAdminForm({t:'q',q:'',o:['','','',''],a:0,e:'',img:''}); } }}
             style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:9999,overflowY:'auto',padding:'calc(16px + env(safe-area-inset-top, 0px)) 16px 16px'}}>
          <div style={{background:'var(--card)',borderRadius:16,padding:16,width:'100%',maxWidth:520,boxSizing:'border-box',
                       margin:'16px auto'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
              <h3 style={{margin:0,fontSize:16,fontWeight:700,color:'var(--text)'}}>{adminEdit >= 0 ? `編輯題目 #${adminEdit+1}` : '新增題目'}</h3>
              <Btn onClick={() => { setAdminEdit(null); setAdminForm({t:'q',q:'',o:['','','',''],a:0,e:'',img:''}); }}
                   style={{fontSize:18,background:'none',color:'var(--muted)',padding:'0 6px',lineHeight:1}}>✕</Btn>
            </div>
            <div style={{marginBottom:10}}>
              <label style={{fontSize:13,color:'var(--muted)',display:'block',marginBottom:4}}>題型</label>
              <select value={adminForm.t} onChange={e => setAdminForm(f=>({...f,t:e.target.value}))}
                      style={{width:'100%',padding:8,borderRadius:8,border:'1px solid var(--border)',background:'var(--bg2)',color:'var(--text)'}}>
                <option value="q">選擇題</option>
                <option value="m">配對題</option>
              </select>
            </div>
            <div style={{marginBottom:10}}>
              <label style={{fontSize:13,color:'var(--muted)',display:'block',marginBottom:4}}>題目</label>
              <textarea value={adminForm.q} onChange={e => setAdminForm(f=>({...f,q:e.target.value}))}
                        rows={3} style={{width:'100%',padding:8,borderRadius:8,border:'1px solid var(--border)',background:'var(--bg2)',color:'var(--text)',boxSizing:'border-box' as const,resize:'vertical' as const}} />
            </div>
            {renderImgField()}
            <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13,color:'var(--muted)',margin:'-2px 0 10px'}}>
              <input type="checkbox" checked={!!adminForm.noImg} onChange={e=>setAdminForm(f=>({...f,noImg:e.target.checked}))} />
              不需圖片（題目提到圖表但已用文字呈現，缺圖檢查略過）
            </label>
            {(() => {
              const orig = adminEdit !== null && adminEdit >= 0 ? adminItems[adminEdit] : null;
              const oo = [...adminForm.o]; while (oo.length && !oo[oo.length - 1]?.trim()) oo.pop();
              const changed = !!orig && isVerified(orig) && contentHash({ ...orig, t: adminForm.t, q: adminForm.q, o: oo, a: adminForm.a, e: adminForm.e, img: adminForm.img || undefined }) !== contentHash(orig);
              return (
                <div style={{margin:'-2px 0 10px'}}>
                  <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13,color:'var(--text)',fontWeight:600}}>
                    <input type="checkbox" checked={!!adminForm.ver} onChange={e=>setAdminForm(f=>({...f,ver:e.target.checked,verTouched:true}))} />
                    ✅ 已驗證通過（內容經人工確認無誤）
                  </label>
                  {changed && !adminForm.verTouched && <div style={{fontSize:12,color:'#b45309',marginTop:2}}>內容已修改，原本的驗證已失效；確認新內容無誤後請重新勾選。</div>}
                  {orig?.verifiedAt && isVerified(orig) && !changed && <div style={{fontSize:12,color:'var(--muted)',marginTop:2}}>驗證日期：{orig.verifiedAt}{orig.verifiedBy ? `（${orig.verifiedBy}）` : ''}</div>}
                </div>
              );
            })()}
            {adminForm.t === 'q' && [0,1,2,3].map(oi => (
              <div key={oi} style={{display:'flex',gap:6,marginBottom:6,alignItems:'center'}}>
                <span style={{fontSize:13,color:'var(--muted)',minWidth:20}}>{oi+1}.</span>
                <input value={adminForm.o[oi]??''} onChange={e => {
                  const o = [...adminForm.o]; o[oi] = e.target.value;
                  setAdminForm(f=>({...f,o}));
                }} placeholder={oi === 3 ? '（三選一可留空）' : ''} style={{flex:1,minWidth:0,padding:'8px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg2)',color:'var(--text)'}} />
                <Btn onClick={() => setAdminForm(f=>({...f,a:oi}))}
                     style={{padding:'6px 10px',borderRadius:6,
                             background: adminForm.a===oi ? '#16a34a':'var(--border)',
                             color: adminForm.a===oi ? '#fff':'var(--text)',fontSize:12}}>正解</Btn>
              </div>
            ))}
            <div style={{marginBottom:14}}>
              <label style={{fontSize:13,color:'var(--muted)',display:'block',marginBottom:4}}>解說</label>
              <textarea value={adminForm.e??''} onChange={e => setAdminForm(f=>({...f,e:e.target.value}))}
                        rows={2} style={{width:'100%',padding:8,borderRadius:8,border:'1px solid var(--border)',background:'var(--bg2)',color:'var(--text)',boxSizing:'border-box' as const,resize:'vertical' as const}} />
            </div>
            <div style={{display:'flex',gap:8}}>
              <Btn onClick={() => { setAdminEdit(null); setAdminForm({t:'q',q:'',o:['','','',''],a:0,e:'',img:''}); }}
                   style={{flex:1,background:'var(--border)',color:'var(--text)',padding:'10px',borderRadius:8}}>取消</Btn>
              <Btn onClick={saveAdminItem} style={{flex:2,background:'var(--accent)',color:'#fff',padding:'10px',borderRadius:8}}>{adminEdit >= 0 ? '儲存修改' : '新增題目'}</Btn>
            </div>
          </div>
        </div>
      , document.body)}
      <div className="page-enter" style={{maxWidth:640,margin:'0 auto',padding:'20px 16px'}}>
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16}}>
          <Btn onClick={() => setPage('home')} style={{background:'var(--border)',color:'var(--text)',padding:'6px 12px',borderRadius:8}}>← 返回</Btn>
          <h2 style={{margin:0}}>後台管理</h2>
        </div>

        {/* Tab switcher */}
        <div style={{display:'flex',gap:4,marginBottom:16,background:'var(--bg2)',borderRadius:10,padding:4,flexWrap:'wrap'}}>
          {(['subjects','questions','notes','reports','users','settings'] as const).map(t => (
            <Btn key={t} onClick={() => setAdminTab(t)}
                 style={{flex:'1 0 30%',padding:'8px',borderRadius:8,fontSize:12,minWidth:60,
                         background: adminTab===t ? 'var(--accent)':'transparent',
                         color: adminTab===t ? '#fff':'var(--text)'}}>
              {t==='questions'?'📚 題庫':t==='notes'?'📝 重點':t==='subjects'?'📂 科目':t==='users'?`👥 用戶${pendingCount ? `（${pendingCount}）` : ''}`:t==='settings'?'⚙️ 設定':`🚩 回報${reportCount ? `（${reportCount}）` : ''}`}
            </Btn>
          ))}
        </div>

        {/* 設定 tab（config/app）：變更後立即儲存 */}
        {adminTab === 'settings' && (() => {
          const rows: { k: keyof AppConfig; label: string; opts: number[]; fmt: (n:number)=>string; section?: string }[] = [
            { section:'錯題', k:'wrongClearStreak', label:'連續答對幾次才移出錯題本', opts:[1,2,3,4,5], fmt:n=>`${n} 次` },
            { section:'測驗', k:'quizSize', label:'每次測驗題數（歷屆考題整份出）', opts:[10,15,20,25,30,40,50], fmt:n=>`${n} 題` },
            { k:'timingDefault', label:'作答與停留時間預設（使用者未自訂時）', opts:[40,60,80,100,120,180,0], fmt:n=>n===0?'不限時／手動':`${n} 秒` },
            { k:'unitSize', label:'單元自動切分：每個子單元題數', opts:[10,15,20,25,30], fmt:n=>`${n} 題` },
            { section:'排行榜', k:'boardMinQ', label:'上榜最低題數', opts:[5,10,15,20,30,50], fmt:n=>`${n} 題` },
            { k:'boardTopN', label:'顯示名次數', opts:[10,20,30,50], fmt:n=>`前 ${n} 名` },
            { section:'介面與分析', k:'topicFilterMin', label:'主題數超過多少才顯示篩選列', opts:[0,5,10,15,20], fmt:n=>`${n} 個` },
            { k:'analysisMinTried', label:'錯題分析：主題至少做過幾題才排名', opts:[1,3,5,10,20], fmt:n=>`${n} 題` },
            { k:'analysisTopN', label:'錯題分析：列出前幾個主題', opts:[3,5,10], fmt:n=>`${n} 個` },
          ];
          return (
            <div style={{padding:16,background:'var(--card)',borderRadius:12,border:'1px solid var(--border)'}}>
              <div style={{fontWeight:700,fontSize:15,color:'var(--text)',marginBottom:4}}>⚙️ App 設定</div>
              {rows.map(r => (
                <React.Fragment key={r.k}>
                  {r.section && <div style={{fontSize:13,fontWeight:700,color:'var(--muted)',margin:'10px 0 6px'}}>{r.section}</div>}
                  <label style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,fontSize:14,color:'var(--text)',marginBottom:8}}>
                    <span style={{flex:1,minWidth:0}}>{r.label}</span>
                    <select value={appCfg[r.k]} onChange={e=>saveCfg({ [r.k]: Number(e.target.value) } as Partial<AppConfig>)}
                      style={{flexShrink:0,padding:'6px 8px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg2)',color:'var(--text)',fontSize:14}}>
                      {(r.opts.includes(appCfg[r.k]) ? r.opts : [...r.opts, appCfg[r.k]]).map(n=><option key={n} value={n}>{r.fmt(n)}</option>)}
                    </select>
                  </label>
                </React.Fragment>
              ))}
              <p style={{fontSize:12,color:'var(--muted)',margin:'8px 0 0'}}>變更後立即儲存；其他使用者下次開啟 App 時生效。</p>
            </div>
          );
        })()}

        {adminTab === 'settings' && <StorageCleanup topics={allTopics} />}

        {/* Reports tab */}
        {adminTab === 'reports' && (
          <ReportsPanel subNameOf={id=>subNameOf(id)} topicNameOf={topicNameOf} onOpen={openReportedQuestion} onCount={setReportCount} />
        )}

        {/* Questions tab */}
        {adminTab === 'questions' && (() => {
          const sel: React.CSSProperties = {width:'100%',minWidth:0,padding:8,borderRadius:8,border:'1px solid var(--border)',background:'var(--bg2)',color:'var(--text)',fontSize:14};
          const qx = adminQuery.trim().toLowerCase();
          // 篩選條件：有圖片、有音訊、有閱讀文章、缺圖片（題目提到圖表卻沒有附圖）
          const MENTION = /如圖|下圖|上圖|附圖|右圖|左圖|圖中|圖為|圖示|如表|下表|附表/;
          const FILTERS: [string, string, (q: QItem) => boolean][] = [
            ['img', '🖼 有圖片', q => !!q.img],
            ['audio', '🔊 有音訊', q => !!q.audio],
            ['passage', '📄 有閱讀文章', q => !!q.passage],
            ['ver', '✅ 已驗證', q => isVerified(q)],
            ['unver', '⬜ 未驗證', q => !isVerified(q)],
            ['noimg', '🚫 缺圖片', q => !q.img && !q.noImgNeeded && MENTION.test(`${q.q ?? ''}${q.passage ?? ''}`) && !hasTable(q.q) && !hasTable(q.passage)],
          ];
          const passF = (q: QItem) => adminQFilters.every(k => FILTERS.find(f => f[0] === k)![2](q));
          const list = adminItems.map((item, i) => ({ item, i })).filter(({ item }) => passF(item)).filter(({ item }) => !qx
            || (item.q ?? '').toLowerCase().includes(qx) || (item.e ?? '').toLowerCase().includes(qx)
            || (item.o ?? []).some(o => String(o).toLowerCase().includes(qx)));
          const text = (item: QItem) => item.q !== undefined ? tableToSpeech(item.q) : (Array.isArray(item.p) ? item.p : []).map((p:any)=>Array.isArray(p) ? p.join('↔') : p && typeof p==='object' ? Object.values(p).join('↔') : String(p)).join(' / ');
          return (
          <div>
            <div style={{display:'grid',gridTemplateColumns:'minmax(0,1fr) minmax(0,1.4fr)',gap:8,marginBottom:8}}>
              <select value={adminSub} aria-label="科目" onChange={e => { setAdminSub(e.target.value); setAdminTopic((allTopics[e.target.value]||[])[0]||''); setAdminQuery(''); setAdminQFilters([]); }} style={sel}>
                {allSubjects.map(s => <option key={s.id} value={s.id}>{s.n}</option>)}
              </select>
              <select value={adminTopic} aria-label="主題" onChange={e => { setAdminTopic(e.target.value); setAdminQuery(''); setAdminQFilters([]); }} style={sel}>
                {(allTopics[adminSub]||[]).map(t => <option key={t} value={t}>{topicNameOf(adminSub,t)}</option>)}
              </select>
            </div>
            {/* 匯出／匯入：針對上方選定的主題 */}
            <div style={{display:'flex',gap:6,alignItems:'center',marginBottom:6}}>
              <select value={expFmt} onChange={e => setExpFmt(e.target.value as 'json'|'csv'|'zip')} aria-label="匯出格式"
                      style={{...sel,width:'auto',flex:'0 1 auto',padding:'7px 6px',fontSize:13}}>
                <option value="json">JSON</option>
                <option value="csv">CSV</option>
                <option value="zip">ZIP</option>
              </select>
              <Btn disabled={exporting}
                onClick={async () => {
                  setExporting(true);
                  try {
                    if (expFmt === 'zip') {
                      const { exportZip } = await import('./utils/zipIO');
                      const r = await exportZip(adminSub, adminTopic, (d, t) => setZipBusy(`打包 ${d}/${t}`));
                      if (r.failed.length) toast(`已匯出，但有 ${r.failed.length} 個檔案下載失敗（清單在 ZIP 內）。`);
                    } else await exportQuestions(adminSub, adminTopic, expFmt);
                  }
                  catch { toast('匯出失敗，請稍後再試'); }
                  finally { setExporting(false); setZipBusy(''); }
                }}
                style={{background:'var(--bg2)',border:'1px solid var(--border)',color:'var(--text)',padding:'7px 10px',borderRadius:8,fontSize:13,fontWeight:600,whiteSpace:'nowrap',opacity:exporting?0.6:1}}>
                {exporting ? (zipBusy || '匯出中…') : '⬇️ 匯出'}
              </Btn>
              <label style={{background:'var(--bg2)',border:'1px solid var(--border)',color:'var(--text)',padding:'7px 10px',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer',whiteSpace:'nowrap'}}>
                ⬆️ 匯入
                <input type="file" accept=".json,.csv,.zip" style={{display:'none'}} onChange={handleBatchFileSelect}/>
              </label>
              <Btn disabled={recounting} title="重新計算所有科目的題數（用腳本直接寫入題庫後使用）"
                onClick={async () => {
                  setRecounting(true);
                  try { await recountSubjects(allSubjects.map(s => s.id)); toast('✅ 已重算所有科目的題數'); }
                  catch { toast('重算失敗，請稍後再試'); }
                  finally { setRecounting(false); }
                }}
                style={{background:'var(--bg2)',border:'1px solid var(--border)',color:'var(--text)',padding:'7px 10px',borderRadius:8,fontSize:13,fontWeight:600,whiteSpace:'nowrap',opacity:recounting?0.6:1}}>
                {recounting ? '重算中…' : '🔄 重算題數'}
              </Btn>
              <Btn onClick={()=>setIoHelp(h=>!h)} aria-label="匯出匯入說明" style={{background:'none',color:'var(--muted)',fontSize:16,padding:'4px 6px'}}>ⓘ</Btn>
            </div>
            {ioHelp && (
              <p style={{fontSize:12,color:'var(--muted)',margin:'0 0 8px',lineHeight:1.6,background:'var(--bg2)',borderRadius:8,padding:'8px 10px'}}>
                匯出與匯入都針對上方選定的主題。先匯出（含 id），編輯後再匯入：有 id 的只更新有改動的欄位，沒有 id 的視為新題。
                圖片、音訊以網址表示，留空代表移除；ZIP 會連同圖片音訊一起打包，匯入時可用 ZIP 內的檔名換圖。
              </p>
            )}

            <div style={{display:'flex',gap:6,alignItems:'center',margin:'10px 0'}}>
              <input value={adminQuery} onChange={e=>setAdminQuery(e.target.value)} placeholder={`🔍 搜尋（共 ${adminItems.length} 題）`}
                style={{...sel,flex:1}} />
              <Btn onClick={()=>{ setAdminForm({t:'q',q:'',o:['','','',''],a:0,e:'',img:''}); setAdminEdit(-1); }}
                style={{background:'var(--accent)',color:'#fff',padding:'8px 12px',borderRadius:8,fontWeight:700,fontSize:13,whiteSpace:'nowrap'}}>＋ 新增題目</Btn>
            </div>
            <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:10}}>
              {FILTERS.map(([k, l, fn]) => {
                const on = adminQFilters.includes(k);
                const n = adminItems.filter(fn).length;
                return (
                  <Btn key={k} onClick={()=>setAdminQFilters(fs => on ? fs.filter(x => x !== k) : [...fs, k])}
                    style={{padding:'5px 10px',borderRadius:99,fontSize:12,fontWeight:600,
                            background: on ? (k==='noimg'?'#dc2626':'var(--accent)') : 'var(--card)', color: on ? '#fff' : n ? 'var(--text)' : 'var(--muted)',
                            border:`1px solid ${on ? 'transparent' : 'var(--border)'}`}}>
                    {l} {n}
                  </Btn>
                );
              })}
              {adminQFilters.length > 0 && (
                <Btn onClick={()=>setAdminQFilters([])} style={{padding:'5px 8px',borderRadius:99,fontSize:12,background:'none',color:'var(--muted)'}}>清除（顯示 {list.length} 題）</Btn>
              )}
            </div>

            {adminItemsLoading ? <p style={{color:'var(--muted)',fontSize:13}}>載入中…</p>
              : list.length === 0 ? <p style={{color:'var(--muted)',fontSize:13}}>{adminItems.length ? '找不到符合的題目' : '此主題沒有題目'}</p>
              : list.map(({ item, i }) => (
              <div key={adminDocIds[i] ?? i} style={{display:'flex',gap:8,alignItems:'flex-start',padding:'10px 12px',background:'var(--card)',borderRadius:10,marginBottom:8,border:'1px solid var(--border)'}}>
                <button onClick={() => startEditItem(i)} style={{flex:1,minWidth:0,textAlign:'left',background:'none',border:'none',padding:0,cursor:'pointer',color:'var(--text)'}}>
                  <div style={{fontSize:12,color:'var(--muted)',marginBottom:3,display:'flex',gap:6,flexWrap:'wrap'}}>
                    <span>#{i+1}</span>
                    {item.t === 'm' && <span>配對題</span>}
                    {item.year && <span>{item.year} 年{item.num ? ` 第 ${item.num} 題` : ''}</span>}
                    {item.img && <span>🖼</span>}
                    {item.audio && <span>🔊</span>}
                    {isVerified(item) && <span title="已驗證" style={{color:'#16a34a',fontWeight:700}}>✅ 已驗證</span>}
                    {item.u && <span>{item.u}</span>}
                  </div>
                  <div style={{fontSize:14,lineHeight:1.5,overflow:'hidden',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical'}}>{text(item)}</div>
                </button>
                {adminQFilters.includes('noimg') && (
                  <Btn onClick={async () => {
                    await updateDoc(doc(db,'questions',adminSub,'topics',adminTopic,'items',adminDocIds[i]), { noImgNeeded: true });
                    setAdminItems(prev => prev.map((x, k) => k === i ? { ...x, noImgNeeded: true } : x));
                  }} title="確認不需要圖片，之後缺圖檢查不再列出"
                    style={{fontSize:12,padding:'0 8px',height:34,background:'var(--bg2)',color:'var(--text)',borderRadius:8,flexShrink:0,whiteSpace:'nowrap'}}>✓ 不需圖片</Btn>
                )}
                <Btn onClick={() => deleteAdminItem(i)} aria-label="刪除題目"
                  style={{minWidth:34,height:34,fontSize:14,background:'#fee2e2',color:'#dc2626',borderRadius:8,flexShrink:0}}>🗑</Btn>
              </div>
            ))}
          </div>
          );
        })()}

        {/* Notes tab */}
        {adminTab === 'notes' && <AdminNotes subjects={allSubjects} topics={allTopics} topicNameOf={topicNameOf} />}

        {/* Subjects tab */}
        {adminTab === 'subjects' && (() => {
          const reload = () => { try { localStorage.removeItem(CUSTOM_SUBS_CACHE_KEY); } catch {} loadCustomSubjects(); };
          const editing = customSubs.find(s => s.id === editingSubId);
          // 編輯中：整頁顯示編輯器
          if (editing) return (
            <SubjectEditor key={editing.id} subject={editing} groups={subjectGroupList} topicIds={customTopics[editing.id]||[]}
              topicNames={customTopicNames[editing.id]||{}} topicCats={customTopicCats[editing.id]||{}}
              onCancel={()=>setEditingSubId(null)}
              onSaved={()=>{ setEditingSubId(null); reload(); }}
              onDeleted={()=>{ setEditingSubId(null); reload(); }} />
          );
          const inp: React.CSSProperties = {width:'100%',padding:'9px 10px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg2)',color:'var(--text)',boxSizing:'border-box',fontSize:14,minWidth:0};
          return (
          <div>
            <GroupEditor key={subjectGroupList.map(g=>g.id+g.name).join()} groups={subjectGroupList}
              usage={Object.fromEntries(subjectGroupList.map(g=>[g.id, customSubs.filter(s=>s.g===g.id).length]))}
              onSaved={reload} />
            <div style={{display:'flex',alignItems:'center',margin:'0 0 10px'}}>
              <h3 style={{margin:0,fontSize:15,flex:1}}>科目（{customSubs.length}）</h3>
              <Btn onClick={()=>setNewSubOpen(o=>!o)} style={{fontSize:13,padding:'7px 12px',borderRadius:8,background:newSubOpen?'var(--bg2)':'var(--accent)',color:newSubOpen?'var(--text)':'#fff',fontWeight:700}}>
                {newSubOpen ? '取消新增' : '＋ 新增科目'}
              </Btn>
            </div>

            {newSubOpen && (
              <div style={{marginBottom:12,padding:14,background:'var(--card)',borderRadius:12,border:'1px solid var(--border)'}}>
                {[
                  {label:'科目 ID（唯一，英數字與底線，建立後不可修改）',key:'id',ph:'例：en_book4'},
                  {label:'顯示名稱',key:'name',ph:'例：英文-課本四'},
                  {label:'Emoji 圖示',key:'icon',ph:'例：📗'},
                ].map(({label,key,ph}) => (
                  <div key={key} style={{marginBottom:10}}>
                    <label style={{fontSize:12,color:'var(--muted)',display:'block',marginBottom:4}}>{label}</label>
                    <input value={(adminNewSub as any)[key]} placeholder={ph} onChange={e=>setAdminNewSub(f=>({...f,[key]:e.target.value}))} style={inp} />
                  </div>
                ))}
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:'0 10px'}}>
                  {([['主色','color'],['背景色','bg']] as const).map(([label,key]) => (
                    <div key={key} style={{marginBottom:10}}>
                      <label style={{fontSize:12,color:'var(--muted)',display:'block',marginBottom:4}}>{label}</label>
                      <div style={{display:'flex',gap:6,alignItems:'center'}}>
                        <input type="color" value={/^#[0-9a-f]{6}$/i.test(adminNewSub[key]) ? adminNewSub[key] : '#888888'} onChange={e=>setAdminNewSub(f=>({...f,[key]:e.target.value}))}
                          style={{width:38,height:36,padding:0,border:'none',background:'none',cursor:'pointer',flexShrink:0}} />
                        <input value={adminNewSub[key]} onChange={e=>setAdminNewSub(f=>({...f,[key]:e.target.value}))} style={inp} />
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{marginBottom:12}}>
                  <label style={{fontSize:12,color:'var(--muted)',display:'block',marginBottom:4}}>主題列表（每行一個，之後可在編輯頁調整）</label>
                  <textarea value={adminNewSub.topicsText} rows={5} placeholder={'L5 單字\nL5 文法\nL6 單字'}
                    onChange={e=>setAdminNewSub(f=>({...f,topicsText:e.target.value}))} style={{...inp,resize:'vertical'}} />
                </div>
                <p style={{fontSize:12,color:'var(--muted)',margin:'0 0 10px'}}>新科目預設不開放訪客、未設定群組（顯示在首頁「其他」），建立後請到 ✏️ 設定群組與順序。</p>
                <Btn onClick={async()=>{ if (await saveCustomSubject()) setNewSubOpen(false); }} disabled={adminSubSaving}
                  style={{width:'100%',background:'var(--accent)',color:'#fff',padding:'10px',borderRadius:8,fontWeight:700}}>
                  {adminSubSaving ? '儲存中…' : '建立科目'}
                </Btn>
              </div>
            )}

            {customSubs.length === 0 && <p style={{color:'var(--muted)',fontSize:14,marginBottom:16}}>尚無科目</p>}
            {[...allSubjects].map(s => (
              <Btn key={s.id} onClick={()=>setEditingSubId(s.id)} aria-label="編輯科目"
                style={{width:'100%',display:'flex',alignItems:'center',gap:10,padding:'10px 12px',background:'var(--card)',borderRadius:10,marginBottom:8,border:'1px solid var(--border)',textAlign:'left'}}>
                <span style={{fontSize:22,flexShrink:0}}>{s.i}</span>
                <span style={{flex:1,minWidth:0}}>
                  <span style={{display:'block',fontWeight:700,color:s.c,fontSize:14,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.n}</span>
                  <span style={{display:'block',fontSize:12,color:'var(--muted)',marginTop:2}}>
                    {subjectGroupList.find(g=>g.id===s.g)?.name ?? '未設群組'} · 第 {s.o ?? '-'} 位 · {(customTopics[s.id]||[]).length} 個主題 · {s.ga ? '👥 開放訪客' : '🔒 需登入'}
                  </span>
                </span>
                <span style={{fontSize:15,flexShrink:0,color:'var(--muted)'}}>✏️</span>
              </Btn>
            ))}
          </div>
          );
        })()}

        {/* Users tab */}
        {adminTab === 'users' && (() => {
          const ST: Record<string,[string,string]> = { pending:['待核准','#d97706'], approved:['已核准','#16a34a'], disabled:['已停用','#64748b'], deleted:['已刪除','#dc2626'] };
          const stOf = (u: typeof adminUsers[number]) => u.status ?? 'pending';
          const shown = adminUsers.filter(u => userFilter === 'all' ? stOf(u) !== 'deleted' : stOf(u) === userFilter)
            .sort((a, b) => (stOf(a) === 'pending' ? 0 : 1) - (stOf(b) === 'pending' ? 0 : 1));
          const btn: React.CSSProperties = {fontSize:12,padding:'6px 10px',borderRadius:8,fontWeight:600,whiteSpace:'nowrap'};
          return (
          <div>
            <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:12}}>
              {([['all','全部'],['pending','待核准'],['approved','已核准'],['disabled','已停用'],['deleted','已刪除']] as const).map(([k,l]) => {
                const n = k === 'all' ? adminUsers.filter(u => stOf(u) !== 'deleted').length : adminUsers.filter(u => stOf(u) === k).length;
                return (
                  <Btn key={k} onClick={()=>setUserFilter(k)}
                    style={{padding:'6px 12px',borderRadius:99,fontSize:12,fontWeight:600,
                            background:userFilter===k?'var(--accent)':'var(--card)',color:userFilter===k?'#fff':'var(--text)',
                            border:`1px solid ${userFilter===k?'var(--accent)':'var(--border)'}`}}>
                    {l} {n}
                  </Btn>
                );
              })}
            </div>
            {!adminUsersLoaded && <p style={{color:'var(--muted)',fontSize:13}}>載入中…</p>}
            {shown.map(u => {
              const st = stOf(u);
              const [label, color] = ST[st] ?? [st, 'var(--muted)'];
              const isAdminUser = ADMIN_UIDS.includes(u.uid);
              return (
                <div key={u.uid} style={{background:'var(--card)',border:`1px solid ${st==='pending'?'#d9770666':'var(--border)'}`,borderRadius:12,padding:'10px 12px',marginBottom:8}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    {u.avatarUrl
                      ? <img src={u.avatarUrl} alt="" style={{width:36,height:36,borderRadius:'50%',objectFit:'cover',flexShrink:0}} />
                      : <div style={{width:36,height:36,borderRadius:'50%',background:u.color||'#6366f1',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,color:'#fff',fontWeight:700,flexShrink:0}}>
                          {(u.nickname||'?').charAt(0).toUpperCase()}
                        </div>}
                    <div style={{flex:1,minWidth:0,fontWeight:700,fontSize:15,color:'var(--text)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      {u.nickname || '（無暱稱）'}{isAdminUser ? '（管理員）' : ''}
                    </div>
                    <span style={{fontSize:11,color,border:`1px solid ${color}`,borderRadius:99,padding:'2px 8px',whiteSpace:'nowrap',flexShrink:0}}>{label}</span>
                  </div>
                  <div style={{fontSize:12,color:'var(--muted)',margin:'6px 0 0',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {u.emailVerified ? '✅' : '⚠️'} {u.email || u.uid}{u.emailVerified ? '' : '（未驗證信箱）'}
                  </div>
                  {st !== 'deleted' && (
                    <div style={{display:'flex',gap:6,marginTop:8,flexWrap:'wrap'}}>
                      <Btn onClick={()=>{ setAnalysisTarget({uid:u.uid, name:u.nickname || '使用者', email:u.email || u.uid}); setPage('stats'); }}
                        style={{...btn,background:'var(--bg2)',color:'var(--text)'}}>📊 學習分析</Btn>
                      {!isAdminUser && st !== 'approved' && (
                        <Btn onClick={()=>setUserStatus(u.uid,'approved')} disabled={!u.emailVerified}
                          title={u.emailVerified ? '核准' : '需先完成電子郵件驗證'}
                          style={{...btn,background:u.emailVerified?'#16a34a':'var(--bg2)',color:u.emailVerified?'#fff':'var(--muted)'}}>
                          {u.emailVerified ? '✓ 核准' : '✓ 核准（需先驗證）'}
                        </Btn>
                      )}
                      {!isAdminUser && st === 'approved' && (
                        <Btn onClick={async()=>{ if (await confirmDialog('停用後此帳號將無法使用，確定？')) setUserStatus(u.uid,'disabled'); }}
                          style={{...btn,background:'var(--bg2)',color:'var(--text)'}}>停用</Btn>
                      )}
                      {!isAdminUser && (
                        <Btn onClick={()=>deleteUserData(u)} style={{...btn,background:'#fee2e2',color:'#dc2626',marginLeft:'auto'}}>🗑 刪除</Btn>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {adminUsersLoaded && shown.length === 0 && adminUsers.length > 0 && (
              <p style={{color:'var(--muted)',textAlign:'center',fontSize:13}}>沒有符合的帳號</p>
            )}
            {adminUsersLoaded && adminUsers.length === 0 && (
              <p style={{color:'var(--muted)',textAlign:'center'}}>尚無用戶資料</p>
            )}
          </div>
          );
        })()}
      </div>
      </>
    );
  }

  // ── 錯題本頁：依群組 › 科目 › 主題分塊，可分塊練習與清除 ────────────────
  if (page==='wrongs') {
    type Row = { key:string; label:string; list:typeof globalWrongs };
    const ever = wrongMode === 'ever';
    const source = ever ? everWrongOf() : globalWrongs;
    const inScope = (sid: string) => source.filter(w => w.ref?.s === sid);
    const sections: { name:string; color:string; rows:Row[] }[] = [];
    const used = new Set<string>();
    subjectGroups.forEach(g => {
      const rows: Row[] = [];
      g.subs.forEach(sb => {
        const tids = allTopics[sb.id] ?? [];
        const inSub = inScope(sb.id);
        tids.forEach(t => {
          const list = inSub.filter(w => w.ref?.t === t);
          if (list.length) rows.push({ key:`${sb.id}/${t}`, label:`${sb.lb || sb.n} › ${topicNameOf(sb.id, t)}`, list });
        });
        // 主題已不在清單中的錯題，歸在該科目下
        const other = inSub.filter(w => !tids.includes(w.ref?.t ?? ''));
        if (other.length) rows.push({ key:`${sb.id}/_`, label:`${sb.lb || sb.n} › 其他`, list: other });
        used.add(sb.id);
      });
      if (rows.length) sections.push({ name:g.name, color:g.subs[0]?.c ?? 'var(--text)', rows });
    });
    const rest = source.filter(w => !w.ref || !used.has(w.ref.s));
    if (rest.length) sections.push({ name:'未分類', color:'var(--muted)', rows:[{ key:'_rest', label:'未分類（舊資料或已刪除的科目）', list: rest }] });
    return (
      <div className="page-enter" style={{minHeight:'100vh',background:'var(--bg)',padding:'20px 16px',display:'flex',flexDirection:'column',alignItems:'center'}}>
        <div style={{maxWidth:480,width:'100%'}}>
          <Btn onClick={()=>setPage('home')} style={{background:'none',color:'var(--muted)',fontWeight:600,fontSize:14,padding:'0 0 16px'}}>← 返回</Btn>
          <h2 style={{fontSize:20,fontWeight:800,color:'var(--text)',margin:'0 0 12px'}}>🔁 錯題本</h2>
          <div style={{display:'flex',gap:4,background:'var(--bg2)',borderRadius:10,padding:4,marginBottom:8}}>
            {([['now',`目前錯題 ${globalWrongs.length}`],['ever',`曾錯過的題目 ${everWrongOf().length}`]] as const).map(([k,l]) => (
              <Btn key={k} onClick={()=>setWrongMode(k)}
                style={{flex:1,padding:'8px 4px',borderRadius:8,fontSize:13,fontWeight:700,
                        background: wrongMode===k ? '#dc2626' : 'transparent', color: wrongMode===k ? '#fff' : 'var(--text)'}}>{l}</Btn>
            ))}
          </div>
          <p style={{fontSize:12,color:'var(--muted)',margin:'0 0 14px'}}>
            {ever ? '曾經答錯過的題目（含已訂正），適合考前完整複習；只能練習，不能清除。'
                  : `連續答對 ${wrongClearStreak} 次才會移出；答錯會重新計算。`}
          </p>
          {source.length === 0 ? (
            <p style={{color:'var(--muted)',textAlign:'center',marginTop:40}}>{ever ? '還沒有答錯過的紀錄' : '目前沒有錯題 👍'}</p>
          ) : (<>
            <div style={{display:'flex',gap:8,marginBottom:18}}>
              <Btn onClick={()=>practiceWrongs(source, ever ? '曾錯過' : undefined)} className="card-hover"
                style={{flex:1,background:'#dc2626',color:'#fff',borderRadius:12,padding:'12px',fontWeight:700,fontSize:15}}>全部練習 {source.length}</Btn>
              {!ever && (
                <Btn onClick={()=>clearWrongScope(globalWrongs,'全部')} aria-label="全部清空" title="全部清空"
                  style={{background:'var(--card)',border:'1px solid var(--border)',borderRadius:12,padding:'0 14px',fontSize:16}}>🗑</Btn>
              )}
            </div>
            {sections.map(sec => (
              <div key={sec.name} style={{marginBottom:16}}>
                <div style={{display:'flex',alignItems:'center',gap:8,fontWeight:800,fontSize:15,color:sec.color,margin:'0 0 8px 2px'}}>
                  <span style={{width:4,height:16,borderRadius:2,background:sec.color}}/>{sec.name}
                </div>
                {sec.rows.map(r => (
                  <div key={r.key} style={{display:'flex',alignItems:'center',gap:8,background:'var(--card)',border:'1px solid var(--border)',borderRadius:12,padding:'8px 8px 8px 12px',marginBottom:6}}>
                    <span style={{flex:1,minWidth:0,fontSize:14,color:'var(--text)'}}>{r.label}</span>
                    <span style={{fontSize:13,fontWeight:700,color:'#dc2626',flexShrink:0}}>{r.list.length}</span>
                    <Btn onClick={()=>practiceWrongs(r.list, ever ? `曾錯過 · ${r.label}` : r.label)}
                      style={{background:'var(--accent)',color:'#fff',borderRadius:8,padding:'6px 12px',fontSize:13,fontWeight:700,flexShrink:0}}>練習</Btn>
                    {!ever && (
                      <Btn onClick={()=>clearWrongScope(r.list, r.label)} aria-label="清除這一塊" title="清除這一塊"
                        style={{background:'none',fontSize:15,padding:'4px 6px',flexShrink:0}}>🗑</Btn>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </>)}
        </div>
      </div>
    );
  }

  if (page==='leaderboard') return (
    <div className="page-enter" style={{minHeight:'100vh',background:'var(--bg2)',padding:'20px 16px',display:'flex',flexDirection:'column',alignItems:'center'}}>
      <div style={{maxWidth:500,width:'100%'}}>
        <Btn onClick={()=>setPage('home')} style={{background:'none',color:'#64748b',fontWeight:600,fontSize:14,padding:'0 0 16px'}}>← 返回</Btn>
        <div style={{textAlign:'center',marginBottom:20}}>
          <div style={{fontSize:44}}>🏆</div>
          <h2 style={{fontSize:20,fontWeight:700,color:'var(--text)',margin:'8px 0 4px'}}>排行榜</h2>
          <p style={{color:'var(--muted)',fontSize:13,margin:0}}>Top {appCfg.boardTopN}</p>
        </div>

        {/* Tab switcher */}
        <div style={{display:'flex',gap:4,marginBottom:16,background:'var(--bg2)',borderRadius:10,padding:4}}>
          {(['global','mine'] as const).map(t => (
            <Btn key={t} onClick={() => setLbTab(t)}
                 style={{flex:1,padding:'8px',borderRadius:8,
                         background: lbTab===t ? 'var(--accent)' : 'transparent',
                         color: lbTab===t ? '#fff' : 'var(--text)'}}>
              {t==='global' ? '排行榜' : '我的歷史'}
            </Btn>
          ))}
        </div>

        {/* 排行榜：多種面向 × 期間 × 科目，只計登入帳號 */}
        {lbTab === 'global' && (
          <Leaderboard myUid={authUser?.uid ?? null} subjects={subjectGroups.flatMap(g => g.subs)} minQ={appCfg.boardMinQ} topN={appCfg.boardTopN}
            totals={Object.fromEntries(allSubjects.map(sb => [sb.id, progress[sb.id]?.total ?? 0]))} wrongsSubId={WRONGS_SUB_ID} />
        )}

        {/* Personal history */}
        {lbTab === 'mine' && (
          <div>
            {(!authUser && !guestMode) ? (
              <p style={{color:'var(--muted)',textAlign:'center',marginTop:24}}>請先登入以查看個人成績</p>
            ) : localRecords.length === 0 ? (
              <p style={{color:'var(--muted)',textAlign:'center',marginTop:24}}>還沒有成績紀錄</p>
            ) : (
              localRecords.map((r, i) => (
                <div key={i} className="lb-row" style={{
                  display:'flex',justifyContent:'space-between',alignItems:'center',
                  padding:'12px 16px',background:'var(--card)',borderRadius:10,marginBottom:8,
                  border:'1px solid var(--border)'
                }}>
                  <div>
                    <div style={{fontWeight:600}}>{subNameOf(r.sub, r.subName)} — {topicNameOf(r.sub, r.topic)}</div>
                    <div style={{fontSize:12,color:'var(--muted)'}}>{r.mode} · {r.date}{(r as any).partial ? ` · 中途離開（共 ${(r as any).planned} 題）` : ''}</div>
                  </div>
                  <div style={{fontSize:18,fontWeight:700,color:'var(--accent)'}}>
                    {r.pct}%
                    <span style={{fontSize:12,fontWeight:400,color:'var(--muted)',marginLeft:4}}>
                      ({r.score}/{r.total})
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );

  // ── 學習統計頁 ────────────────────────────────────────────────────────────
  // ── 學習分析：本人或後台查看的使用者 ────────────────────────────────────
  if (page==='stats') {
    const totals = Object.fromEntries(Object.entries(progress).map(([k, v]) => [k, v.total]));
    const target = analysisTarget;
    return (
      <Analysis
        minTried={appCfg.analysisMinTried}
        topN={appCfg.analysisTopN}
        uid={target ? target.uid : (authUser?.uid ?? null)}
        title={target ? `${target.name} 的學習分析` : '學習分析'}
        subtitle={target?.email}
        groups={subjectGroups}
        topics={allTopics}
        topicNameOf={topicNameOf}
        totals={totals}
        liveAnswers={target ? undefined : answers}
        onOpenTopic={target ? undefined : (sid, tid) => {
          const sb = allSubjects.find(x => x.id === sid); if (!sb) return;
          setSub(sb); setTopic(''); setTopicCatSel(''); setTopicQuery(''); setPage('topic');
        }}
        onPractice={target ? undefined : practiceRefs}
        onBack={() => { const back = target ? 'admin' : 'home'; setAnalysisTarget(null); setPage(back); }}
      />
    );
  }

  if (page === 'about') return (
    <div className="page-enter" style={{minHeight:'100vh',background:'var(--bg)',padding:'24px 16px',display:'flex',flexDirection:'column',alignItems:'center'}}>
      <div style={{maxWidth:460,width:'100%'}}>
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:24}}>
          <Btn onClick={()=>setPage('home')} style={{background:'var(--card)',borderRadius:10,padding:'8px 14px',fontSize:14,color:'var(--text)',fontWeight:600}}>← 返回</Btn>
          <h2 style={{margin:0,fontSize:20,fontWeight:800,color:'var(--text)'}}>關於考題怪</h2>
        </div>
        <div style={{textAlign:'center',marginBottom:32}}>
          <div style={{fontSize:52,marginBottom:8}}>🎓</div>
          <h1 style={{fontSize:24,fontWeight:800,color:'var(--text)',margin:'0 0 4px'}}>考題怪</h1>
          <p style={{color:'var(--muted)',fontSize:13,margin:'0 0 12px'}}>用遊戲方式備考學測！</p>
          <span style={{background:'var(--accent)',color:'#fff',borderRadius:99,padding:'3px 14px',fontSize:12,fontWeight:700}}>v{APP_VERSION}</span>
          <div style={{fontSize:11,color:'var(--muted)',marginTop:6}}>建置 {BUILD_INFO}</div>
        </div>
        <div>
          <h3 style={{fontSize:15,fontWeight:700,color:'var(--text)',marginBottom:12,marginTop:0}}>版本記錄</h3>
          {RELEASE_NOTES.map((r,i)=>(
            <div key={r.version} style={{marginBottom:16,paddingLeft:16,borderLeft:`3px solid ${i===0?'var(--accent)':'var(--border)'}`}}>
              <div style={{display:'flex',alignItems:'baseline',gap:8,marginBottom:4}}>
                <span style={{fontWeight:700,color:i===0?'var(--accent)':'var(--text)',fontSize:14}}>{r.version}</span>
                <span style={{color:'var(--muted)',fontSize:12}}>{r.date}</span>
              </div>
              <ul style={{margin:0,paddingLeft:16}}>
                {r.items.map((item,j)=>(
                  <li key={j} style={{color:'var(--muted)',fontSize:13,marginBottom:2}}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (!sub) return null;

  // ── 主題頁 ────────────────────────────────────────────────────────────────
  if (page==='topic') return (
    <div className="page-enter" style={{minHeight:'100vh',background:sbg,padding:'20px 16px',display:'flex',flexDirection:'column',alignItems:'center'}}>
      <div style={{maxWidth:440,width:'100%'}}>
        <Btn onClick={()=>setPage('home')} style={{background:'none',color:clr,fontWeight:600,fontSize:14,padding:'0 0 16px'}}>← 返回</Btn>
        <div style={{textAlign:'center',marginBottom:22}}>
          <div style={{fontSize:40}}>{sub.i}</div>
          <h2 style={{fontSize:20,fontWeight:700,color:clr,margin:'8px 0 4px'}}>{sub.n}</h2>
          <p style={{color:'var(--muted)',fontSize:14,margin:0}}>選擇學習主題</p>
        </div>
        {wrongsOf(sub.id).length > 0 && (
          <Btn onClick={()=>practiceWrongs(wrongsOf(sub.id), sub.lb || sub.n)} className="card-hover"
            style={{width:'100%',background:'var(--card)',border:'1px solid #dc262640',color:'#dc2626',borderRadius:12,padding:'10px',fontWeight:700,fontSize:14,marginBottom:12}}>
            🔁 本科目錯題 {wrongsOf(sub.id).length}
          </Btn>
        )}
        {everWrongOf(sub.id).length > wrongsOf(sub.id).length && (
          <Btn onClick={()=>practiceWrongs(everWrongOf(sub.id), `曾錯過 · ${sub.lb || sub.n}`)} className="card-hover"
            style={{width:'100%',background:'none',border:'1px dashed #dc262660',color:'#dc2626',borderRadius:12,padding:'8px',fontWeight:600,fontSize:13,marginBottom:12}}>
            📚 本科目曾錯過的題目 {everWrongOf(sub.id).length}
          </Btn>
        )}
        {/* 篩選列：主題超過 10 個才顯示；分類來自 topicCats，關鍵字比對主題名稱 */}
        {(allTopics[sub.id]||[]).length > appCfg.topicFilterMin && (() => {
          const cats = Array.from(new Set((allTopics[sub.id]||[]).map(t => customTopicCats[sub.id]?.[t]).filter(Boolean))) as string[];
          return (
            <div style={{marginBottom:12}}>
              {cats.length > 0 && (
                <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:8}}>
                  {['', ...cats].map(c => (
                    <Btn key={c||'all'} onClick={()=>setTopicCatSel(c)}
                      style={{padding:'6px 14px',borderRadius:99,fontSize:13,fontWeight:600,
                              background: topicCatSel===c ? clr : 'var(--card)',
                              color: topicCatSel===c ? '#fff' : 'var(--text)',
                              border:`1px solid ${topicCatSel===c ? clr : 'var(--border)'}`}}>
                      {c || '全部'}
                    </Btn>
                  ))}
                </div>
              )}
              <input value={topicQuery} onChange={e=>setTopicQuery(e.target.value)} placeholder="🔍 搜尋主題…"
                style={{width:'100%',padding:'9px 12px',borderRadius:10,border:'1px solid var(--border)',background:'var(--card)',color:'var(--text)',fontSize:14,boxSizing:'border-box'}}/>
            </div>
          );
        })()}
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {(() => {
            // 範圍：目前分類篩選下的主題
            const scope = (allTopics[sub.id]||[]).filter(t => !topicCatSel || customTopicCats[sub.id]?.[t] === topicCatSel);
            const total = scope.reduce((a, t) => a + (progress[`${sub.id}/${t}`]?.total ?? 0), 0);
            return (
              <div style={{background:clr,borderRadius:14,padding:'12px 14px',color:'white'}}>
                <Btn onClick={()=>{setAllScope(scope);setTopic('ALL');setUnitSel(null);quickStart('topic');}} className="card-hover"
                  style={{width:'100%',background:'none',textAlign:'center',fontWeight:700,color:'white',fontSize:15,padding:'4px 0 10px'}}>
                  📚 全部{topicCatSel}主題{total ? `（共 ${total} 題）` : ''}
                </Btn>
                <div style={{display:'flex',gap:6,justifyContent:'center'}}>
                  {([['50','50 題'],['75','75 題'],['100','100 題'],['all','全部']] as const).map(([v,l]) => (
                    <Btn key={v} onClick={()=>{ setAllCount(v); try { localStorage.setItem('gsat_all_count', v); } catch {} }}
                      style={{flex:1,padding:'6px 4px',borderRadius:99,fontSize:12,fontWeight:700,
                              background: allCount===v ? 'white' : 'rgba(255,255,255,0.18)', color: allCount===v ? clr : 'white'}}>{l}</Btn>
                  ))}
                </div>
                <div style={{fontSize:11,opacity:0.85,textAlign:'center',marginTop:6}}>各主題平均出題，優先還沒答對過的題目</div>
              </div>
            );
          })()}
          {(allTopics[sub.id]||[]).filter(t =>
              (!topicCatSel || customTopicCats[sub.id]?.[t] === topicCatSel) &&
              (!topicQuery.trim() || topicNameOf(sub.id, t).toLowerCase().includes(topicQuery.trim().toLowerCase()))
            ).map(t=>(
            <div key={t} style={{display:'flex',gap:8,alignItems:'stretch'}}>
              <Btn
                title="重點整理"
                onClick={async ()=>{
                  setNotesTopic(t); setNotesUnit(null); setNotesTab('article');
                  setCardIndex(0); setCardFlipped(false); setNotesSearch('');
                  setNotesLoading(true); setPage('notes');
                  const items = await fetchNotes(sub.id, t, null);
                  setNotesItems(items); setNotesLoading(false);
                  if (authUser) addDoc(collection(db,'users',authUser.uid,'records'),{type:'notes',mode:'article',sub:sub.id,topic:t,unit:null,ts:serverTimestamp()}).catch(()=>{});
                }}
                style={{background:`${clr}15`,border:`2px solid ${clr}30`,borderRadius:14,padding:'0 14px',fontWeight:700,fontSize:18,color:clr,flexShrink:0,transition:'background 0.15s'}}>
                📋
              </Btn>
              <Btn onClick={async ()=>{
                  setTopic(t); setUnitSel(null); setPage('unit');
                  setUnitItems(null);
                  try { setUnitItems(await loadTopic(sub.id, t)); } catch { setUnitItems([]); }
                }}
                className="card-hover"
                style={{flex:1,background:'var(--card)',border:`2px solid ${clr}30`,borderRadius:14,padding:'14px 18px',textAlign:'left',fontWeight:600,color:'var(--text)',fontSize:15,transition:'transform 0.2s ease, box-shadow 0.2s ease'}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                  <span>{topicNameOf(sub.id, t)}</span>
                  <div style={{display:'flex',alignItems:'center',gap:6}}>
                    {topicAvgPct[`${sub.id}__${t}`] !== undefined && (progress[`${sub.id}/${t}`]?.done ?? 0) > 0 && (
                      <span style={{fontSize:12,fontWeight:700,color:pctClr(topicAvgPct[`${sub.id}__${t}`])}}>
                        {topicAvgPct[`${sub.id}__${t}`]}%
                      </span>
                    )}
                    {progress[`${sub.id}/${t}`] && (
                      <span className="badge">{progress[`${sub.id}/${t}`].done}/{progress[`${sub.id}/${t}`].total}</span>
                    )}
                  </div>
                </div>
                {progress[`${sub.id}/${t}`] && (
                  <div style={{height:5,background:`${clr}18`,borderRadius:99,overflow:'hidden',marginTop:8}}>
                    <div style={{height:'100%',borderRadius:99,background:clr,transition:'width 0.4s ease',
                      width:`${progress[`${sub.id}/${t}`].total ? Math.min(100, progress[`${sub.id}/${t}`].done/progress[`${sub.id}/${t}`].total*100) : 0}%`}}/>
                  </div>
                )}
              </Btn>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ── 單元選擇頁（依概念分組）──────────────────────────────────────────────
  if (page==='unit') {
    // 依 u 欄位分組；無 u 歸「綜合練習」；過大的組切成（一）（二）… 子單元
    const groups = new Map<string,QItem[]>();
    (unitItems ?? []).filter(q=>q.t==='q').forEach(it => {
      const k = it.u ?? '綜合練習';
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k)!.push(it);
    });
    const units: {label:string, concept:string, items:QItem[]}[] = [];
    groups.forEach((items, concept) => {
      const UNIT_SIZE = appCfg.unitSize;
      if (items.length <= UNIT_SIZE + 5) {
        units.push({ label: concept, concept, items });
      } else {
        const parts = Math.ceil(items.length / UNIT_SIZE);
        for (let i = 0; i < parts; i++) {
          units.push({
            label: `${concept}（${CN_NUM[i] ?? i+1}）`,
            concept,
            items: items.slice(i*UNIT_SIZE, (i+1)*UNIT_SIZE),
          });
        }
      }
    });
    const total = (unitItems ?? []).filter(q=>q.t==='q').length;
    return (
      <div className="page-enter" style={{minHeight:'100vh',background:'var(--bg2)',padding:'20px 16px',display:'flex',flexDirection:'column',alignItems:'center'}}>
        <div style={{maxWidth:480,width:'100%'}}>
          <Btn onClick={() => setPage('topic')} style={{background:'none',color:'var(--accent)',fontWeight:600,fontSize:14,padding:'0 0 16px'}}>← 返回</Btn>
          <div style={{textAlign:'center',marginBottom:22}}>
            <div style={{fontSize:40}}>{sub.i}</div>
            <h2 style={{fontSize:20,fontWeight:700,color:'var(--text)',margin:'8px 0 4px'}}>{topicLabel}</h2>
            <p style={{color:'var(--muted)',fontSize:14,margin:0}}>選擇學習單元</p>
          </div>
          {wrongsOf(sub.id, topic).length > 0 && (
            <Btn onClick={()=>practiceWrongs(wrongsOf(sub.id, topic), topicLabel)} className="card-hover"
              style={{width:'100%',background:'var(--card)',border:'1px solid #dc262640',color:'#dc2626',borderRadius:12,padding:'10px',fontWeight:700,fontSize:14,marginBottom:12}}>
              🔁 本主題錯題 {wrongsOf(sub.id, topic).length}
            </Btn>
          )}
          {everWrongOf(sub.id, topic).length > wrongsOf(sub.id, topic).length && (
            <Btn onClick={()=>practiceWrongs(everWrongOf(sub.id, topic), `曾錯過 · ${topicLabel}`)} className="card-hover"
              style={{width:'100%',background:'none',border:'1px dashed #dc262660',color:'#dc2626',borderRadius:12,padding:'8px',fontWeight:600,fontSize:13,marginBottom:12}}>
              📚 本主題曾錯過的題目 {everWrongOf(sub.id, topic).length}
            </Btn>
          )}
          {unitItems === null ? (
            <SkeletonList n={5} h={64} />
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              <Btn
                className="btn-primary card-hover"
                onClick={() => { setUnitSel(null); quickStart('unit'); }}
                style={{padding:'16px 18px',fontSize:15,textAlign:'left',display:'flex',alignItems:'center',gap:12}}
              >
                <span style={{fontSize:24}}>🎯</span>
                <span style={{flex:1}}>全部單元</span>
                <span style={{fontSize:13,opacity:0.85}}>{total} 題</span>
              </Btn>
              {units.map(u => (
                <div key={u.label} style={{display:'flex',gap:8,alignItems:'stretch'}}>
                  <Btn
                    title="重點整理"
                    onClick={async ()=>{
                      setNotesTopic(topic); setNotesUnit(u.concept); setNotesTab('article');
                      setCardIndex(0); setCardFlipped(false); setNotesSearch('');
                      setNotesLoading(true); setPage('notes');
                      const items = await fetchNotes(sub.id, topic, u.concept);
                      setNotesItems(items); setNotesLoading(false);
                      if (authUser) addDoc(collection(db,'users',authUser.uid,'records'),{type:'notes',mode:'article',sub:sub.id,topic,unit:u.concept,ts:serverTimestamp()}).catch(()=>{});
                    }}
                    style={{background:'var(--bg2)',border:'2px solid var(--border)',borderRadius:12,padding:'0 10px',fontWeight:700,fontSize:16,color:'var(--accent)',flexShrink:0}}>
                    📋
                  </Btn>
                  <Btn
                    className="card card-hover"
                    onClick={() => { setUnitSel({label:u.label, items:u.items}); quickStart('unit'); }}
                    style={{flex:1,minWidth:0,padding:'12px 12px',textAlign:'left',display:'flex',alignItems:'center',gap:10,color:'var(--text)',fontSize:15,fontWeight:600}}
                  >
                    <span style={{fontSize:22,flexShrink:0}}>{CONCEPT_EMOJI[u.concept] ?? '📚'}</span>
                    <span style={{flex:1,minWidth:0}}>
                      {u.label}
                      <span style={{display:'block',fontSize:12,fontWeight:500,color:'var(--muted)',marginTop:2}}>{u.items.length} 題</span>
                    </span>
                  </Btn>
                  {(() => {
                    // 本單元錯題：以題目 ID 比對
                    const uw = wrongsOf(sub.id, topic, new Set(u.items.map(it => it.ref?.id).filter(Boolean) as string[]));
                    return uw.length > 0 && (
                      <Btn onClick={()=>practiceWrongs(uw, `${topicLabel} › ${u.label}`)} title="本單元錯題" aria-label="本單元錯題"
                        style={{background:'var(--bg2)',border:'2px solid #dc262640',borderRadius:12,padding:'0 8px',fontWeight:700,fontSize:13,color:'#dc2626',flexShrink:0}}>
                        🔁{uw.length}
                      </Btn>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── 重點整理頁 ────────────────────────────────────────────────────────────
  if (page==='notes') {
    const sq = notesSearch.trim().toLowerCase();
    const articles = (notesItems.filter(n=>n.t==='article') as Extract<NoteItem,{t:'article'}>[])
      .filter(a=>!sq || a.title.toLowerCase().includes(sq) || a.points.some(p=>p.toLowerCase().includes(sq)))
      .sort((a, b) => {
        const aScore = (a.imgs && a.imgs.length > 0 ? 2 : 0) + (a.title.includes('第一次段考') ? 1 : 0);
        const bScore = (b.imgs && b.imgs.length > 0 ? 2 : 0) + (b.title.includes('第一次段考') ? 1 : 0);
        return bScore - aScore;
      });
    const allCards  = (notesItems.filter(n=>n.t==='card') as Extract<NoteItem,{t:'card'}>[])
      .filter(c=>!sq || c.front.toLowerCase().includes(sq) || c.back.toLowerCase().includes(sq));
    const maps = (notesItems.filter(n=>n.t==='map') as Extract<NoteItem,{t:'map'}>[])
      .filter(m=>!sq || m.title.toLowerCase().includes(sq) || m.text.toLowerCase().includes(sq));
    const mapTotal = notesItems.filter(n=>n.t==='map').length;
    const dueCount  = allCards.filter(c=>{ const d=srData[srCardId(c.front)]; return !d || d.due<=Date.now(); }).length;
    const cards     = srDueOnly ? allCards.filter(c=>{ const d=srData[srCardId(c.front)]; return !d || d.due<=Date.now(); }) : allCards;
    const card      = cards[cardIndex] ?? cards[0];
    // 分頁標籤上的筆數：搜尋時顯示「符合/總數」
    const articleTotal = notesItems.filter(n=>n.t==='article').length;
    const cardTotal    = notesItems.filter(n=>n.t==='card').length;
    const countLabel   = (shown:number, total:number) => sq ? `${shown}/${total}` : `${total}`;

    const rateCard = (front: string, rating: 0|3|5) => {
      const id = srCardId(front);
      const updated = { ...srData, [id]: sm2Update(srData[id], rating) };
      setSrData(updated);
      try { localStorage.setItem(SR_KEY, JSON.stringify(updated)); } catch {}
      setCardFlipped(false);
      setCardIndex(i => Math.min(cards.length - 1, i + 1));
    };
    return (
      <div className="page-enter" style={{minHeight:'100vh',background:'var(--bg2)',padding:'20px 16px',display:'flex',flexDirection:'column',alignItems:'center'}}>
        <div style={{maxWidth:480,width:'100%'}}>
          {strokeLayer}
          {/* Header */}
          <Btn onClick={()=>setPage('topic')} style={{background:'none',color:'var(--accent)',fontWeight:600,fontSize:14,padding:'0 0 16px'}}>← 返回</Btn>
          <div style={{textAlign:'center',marginBottom:20}}>
            <div style={{fontSize:36}}>{sub?.i}</div>
            <h2 style={{fontSize:18,fontWeight:700,color:'var(--text)',margin:'6px 0 2px'}}>{topicNameOf(sub?.id, notesTopic)}{notesUnit ? ` › ${notesUnit}` : ''}</h2>
            <p style={{color:'var(--muted)',fontSize:13,margin:0}}>重點整理</p>
          </div>

          {/* Tab 切換 */}
          <div style={{display:'flex',gap:4,marginBottom:12,background:'var(--card)',borderRadius:12,padding:4,boxShadow:'0 1px 4px rgba(0,0,0,.06)'}}>
            {(['article','card','map', ...(notesMatchCount > 0 ? ['match'] as const : [])] as ('article'|'card'|'map'|'match')[]).map(tab=>(
              <Btn key={tab} onClick={()=>{setNotesTab(tab);setCardIndex(0);setCardFlipped(false);setNotesSearch('');setSrDueOnly(false);
                if (tab==='map' && sub && maps.length>0) logStudy(authUser?.uid ?? null, sub.id, 'map').catch(()=>{});
                if (authUser && tab==='map' && maps.length>0) addDoc(collection(db,'users',authUser.uid,'records'),{type:'notes',mode:'map',sub:sub!.id,topic:notesTopic,unit:notesUnit??null,total:maps.length,ts:serverTimestamp()}).catch(()=>{});
                if (authUser && tab==='card' && allCards.length>0) addDoc(collection(db,'users',authUser.uid,'records'),{type:'notes',mode:'card',sub:sub!.id,topic:notesTopic,unit:notesUnit??null,total:allCards.length,ts:serverTimestamp()}).catch(()=>{});
              }}
                style={{flex:1,padding:'9px 2px',borderRadius:9,fontSize:notesMatchCount > 0 ? 12 : 13,fontWeight:600,whiteSpace:'nowrap',
                  background:notesTab===tab?'var(--accent)':'transparent',
                  color:notesTab===tab?'#fff':'var(--muted)'}}>
                {tab==='article' ? `📄 條列（${countLabel(articles.length, articleTotal)}）`
                  : tab==='map' ? `🧠 心智圖（${countLabel(maps.length, mapTotal)}）`
                  : tab==='match' ? `🔗 配對（${notesMatchCount}）` : (
                  <span style={{position:'relative',display:'inline-flex',alignItems:'center',gap:4}}>
                    🃏 閃卡（{countLabel(allCards.length, cardTotal)}）
                    {tab===notesTab && dueCount>0 && (
                      <span style={{background:'#f97316',color:'#fff',borderRadius:99,fontSize:10,padding:'1px 5px',fontWeight:700,lineHeight:1.4}}>{dueCount}</span>
                    )}
                  </span>
                )}
              </Btn>
            ))}
          </div>

          {/* 搜尋 */}
          {notesTab !== 'match' && <div style={{position:'relative',marginBottom:16}}>
            <span style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',fontSize:15,pointerEvents:'none',color:'var(--muted)'}}>🔍</span>
            <input
              value={notesSearch}
              onChange={e=>{setNotesSearch(e.target.value);setCardIndex(0);setCardFlipped(false);}}
              placeholder="搜尋重點…"
              style={{width:'100%',boxSizing:'border-box',padding:'10px 12px 10px 36px',borderRadius:10,border:'1.5px solid var(--border)',background:'var(--card)',color:'var(--text)',fontSize:14,outline:'none'}}
            />
            {notesSearch && (
              <Btn onClick={()=>{setNotesSearch('');setCardIndex(0);setCardFlipped(false);}}
                style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',color:'var(--muted)',fontSize:16,padding:'2px 4px'}}>
                ✕
              </Btn>
            )}
          </div>}

          {notesTab==='match' ? (
            /* ── 配對：入口（沿用原本的配對練習流程與計分） ── */
            <div style={{background:'var(--card)',borderRadius:14,padding:'28px 20px',textAlign:'center',boxShadow:'0 1px 6px rgba(0,0,0,.06)'}}>
              <div style={{fontSize:44,marginBottom:8}}>🔗</div>
              <p style={{fontSize:16,fontWeight:700,color:'var(--text)',margin:'0 0 6px'}}>配對練習</p>
              <p style={{fontSize:13,color:'var(--muted)',margin:'0 0 18px',lineHeight:1.6}}>把概念和說明連起來。<br/>{topicNameOf(sub?.id, notesTopic)} 共 {notesMatchCount} 組{notesUnit ? '（範圍為整個主題，不分單元）' : ''}</p>
              <Btn onClick={()=>{ setTopic(notesTopic); setUnitSel(null); quickStart('notes','match'); }}
                style={{background:'var(--accent)',color:'#fff',borderRadius:12,padding:'12px 28px',fontSize:15,fontWeight:700}}>開始配對</Btn>
            </div>
          ) : notesLoading ? (
            <SkeletonList n={4} h={110} />
          ) : notesItems.length===0 ? (
            <div style={{textAlign:'center',padding:'60px 0',color:'var(--muted)'}}>
              <div style={{fontSize:48,marginBottom:12}}>📭</div>
              <p style={{fontSize:16,fontWeight:600}}>尚無重點整理</p>
              <p style={{fontSize:13}}>管理員尚未新增此主題的重點內容</p>
            </div>
          ) : notesTab==='map' ? (
            /* ── 心智圖 ── */
            <div style={{display:'flex',flexDirection:'column',gap:14}}>
              {maps.length===0 ? (
                <p style={{textAlign:'center',color:'var(--muted)',fontSize:14}}>{sq ? `找不到「${notesSearch}」的相關心智圖` : '此主題暫無心智圖。'}</p>
              ) : maps.map((m,i)=>(
                <MindMap key={m._id ?? i} title={m.title} text={m.text} color={clr} renderText={inlineMd}
                  speak={t => <SpeakBtn active={speakingKey===`map-${i}`} onClick={()=>toggleSpeak(`map-${i}`, plainMd(t))} />} />
              ))}
            </div>
          ) : notesTab==='article' ? (
            /* ── 條列模式 ── */
            <div style={{display:'flex',flexDirection:'column',gap:14}}>
              {articles.length===0 ? (
                <p style={{textAlign:'center',color:'var(--muted)',fontSize:14}}>{sq ? `找不到「${notesSearch}」的相關重點` : '此主題暫無條列式重點，請切換至閃卡模式。'}</p>
              ) : articles.map((art,i)=>(
                <div key={i} style={{background:'var(--card)',borderRadius:14,padding:'18px 20px',boxShadow:'0 1px 6px rgba(0,0,0,.06)'}}>
                  <div style={{display:'flex',alignItems:'flex-start',gap:6,margin:'0 0 12px'}}>
                    <h3 style={{margin:0,flex:1,fontSize:15,fontWeight:700,color:'var(--accent)'}}>{inlineMd(art.title)}</h3>
                    <SpeakBtn active={speakingKey===`art-${i}`} onClick={()=>toggleSpeak(`art-${i}`, [art.title, ...art.points].map(plainMd).join('。\n'))} />
                  </div>
                  {art.imgs && art.imgs.length>0 && (
                    <div style={{display:'flex',flexDirection:'column',gap:12,margin:'0 0 16px'}}>
                      {art.imgs.map((url,j)=>(
                        <img key={j} src={url} alt={`圖${j+1}`} title="點一下放大"
                          onClick={()=>openImage(url)}
                          style={{width:'100%',borderRadius:10,objectFit:'contain',maxHeight:420,background:'#f8fafc',boxShadow:'0 1px 6px rgba(0,0,0,.08)',cursor:'zoom-in'}} loading="lazy"/>
                      ))}
                    </div>
                  )}
                  <ul style={{margin:0,paddingLeft:20,display:'flex',flexDirection:'column',gap:7}}>
                    {art.points.map((pt,j)=>(
                      // 自帶編號（1. 2.）的重點不再顯示圓點
                      <li key={j} style={{fontSize:14,color:'var(--text)',lineHeight:1.6,
                        ...(/^\s*\d+[.、)]\s*/.test(pt) ? {listStyle:'none',marginLeft:-18} : {})}}>{inlineMd(stripBullet(pt))}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            /* ── 閃卡模式 ── */
            <>
              {/* 到期篩選切換 */}
              {allCards.length>0 && (
                <div style={{display:'flex',gap:8,marginBottom:12,alignItems:'center'}}>
                  <Btn onClick={()=>{setSrDueOnly(d=>!d);setCardIndex(0);setCardFlipped(false);}}
                    style={{padding:'7px 14px',borderRadius:99,fontSize:13,fontWeight:600,
                      background:srDueOnly?'#f97316':'var(--card)',
                      color:srDueOnly?'#fff':'var(--muted)',
                      border:`1.5px solid ${srDueOnly?'#f97316':'var(--border)'}`,boxShadow:'0 1px 4px rgba(0,0,0,.06)'}}>
                    📅 到期複習 {dueCount>0?`(${dueCount})`:''}
                  </Btn>
                  <span style={{fontSize:12,color:'var(--muted)'}}>
                    {srDueOnly?`顯示 ${cards.length} 張到期`:`全部 ${allCards.length} 張`}
                  </span>
                </div>
              )}
              {cards.length===0 ? (
                <div style={{textAlign:'center',padding:'40px 0',color:'var(--muted)'}}>
                  {srDueOnly
                    ? <><div style={{fontSize:48,marginBottom:12}}>🎉</div><p style={{fontWeight:700,fontSize:16,color:'#22c55e'}}>今日到期卡片已全部複習完畢！</p><p style={{fontSize:13}}>明天再來繼續吧</p><Btn onClick={()=>{setSrDueOnly(false);setCardIndex(0);}} style={{marginTop:12,padding:'10px 24px',borderRadius:12,background:'var(--accent)',color:'#fff',fontWeight:700,fontSize:14}}>顯示全部卡片</Btn></>
                    : <p style={{fontSize:14}}>{sq ? `找不到「${notesSearch}」的相關閃卡` : '此主題暫無閃卡，請切換至條列模式。'}</p>
                  }
                </div>
              ) : (
                <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:16}}>
                  {/* 進度 + SR 狀態 */}
                  <div style={{display:'flex',justifyContent:'space-between',width:'100%',alignItems:'center'}}>
                    <span style={{color:'var(--muted)',fontSize:13}}>{cardIndex+1} / {cards.length}</span>
                    {card && (()=>{
                      const d = srData[srCardId(card.front)];
                      if (!d) return <span style={{fontSize:11,color:'var(--muted)'}}>🆕 新卡片</span>;
                      const days = Math.round((d.due - Date.now()) / 86400000);
                      return <span style={{fontSize:11,color:days<=0?'#f97316':'#22c55e'}}>{days<=0?`⏰ 到期`:`✅ ${days}天後複習`}</span>;
                    })()}
                  </div>
                  {/* 閃卡 */}
                  {(() => { cardSpeechRef.current = card ? { front: plainMd(card.front), back: plainMd(card.back) } : null; return null; })()}
                  <div onClick={()=>{ if (!cardFlipped && sub) logStudy(authUser?.uid ?? null, sub.id, 'card').catch(()=>{}); setCardFlipped(f=>!f); }}
                    style={{width:'100%',minHeight:220,background:'var(--card)',borderRadius:18,
                      boxShadow:'0 4px 20px rgba(0,0,0,.10)',cursor:'pointer',display:'flex',
                      flexDirection:'column',alignItems:'center',justifyContent:'center',
                      padding:'28px 24px',textAlign:'center',userSelect:'none',
                      border:`2px solid ${cardFlipped?'var(--accent)':'var(--border)'}`,
                      transition:'border-color 0.2s'}}>
                    {!cardFlipped ? (
                      <>
                        <p style={{fontSize:13,color:'var(--muted)',marginBottom:12,display:'flex',alignItems:'center',gap:6}}>正面（點擊翻面）<SpeakBtn active={speakingKey==='card-front'} onClick={()=>toggleSpeak('card-front', plainMd(card.front))} />{strokeOn(sub?.id) && strokeBtn(card.front)}</p>
                        <p style={{fontSize:16,fontWeight:700,color:'var(--text)',lineHeight:1.6,margin:0,whiteSpace:'pre-line',textAlign:'left'}}>{inlineMd(card.front)}</p>
                      </>
                    ) : (
                      <>
                        <p style={{fontSize:13,color:'var(--accent)',marginBottom:12,fontWeight:600,display:'flex',alignItems:'center',gap:6}}>背面<SpeakBtn active={speakingKey==='card-back'} onClick={()=>toggleSpeak('card-back', plainMd(card.back))} />{strokeOn(sub?.id) && strokeBtn(card.back)}</p>
                        <p style={{fontSize:15,color:'var(--text)',lineHeight:1.6,margin:0,whiteSpace:'pre-line',textAlign:'left'}}>{inlineMd(card.back)}</p>
                        {card.img && <img src={card.img} alt="說明圖" style={{marginTop:16,width:'100%',borderRadius:10,maxHeight:220,objectFit:'contain'}} loading="lazy"/>}
                      </>
                    )}
                  </div>
                  {/* 評分按鈕（翻面後顯示）*/}
                  {cardFlipped && card && (
                    <div style={{width:'100%'}}>
                      <p style={{textAlign:'center',fontSize:12,color:'var(--muted)',marginBottom:8}}>記憶程度？</p>
                      <div style={{display:'flex',gap:8}}>
                        <Btn onClick={()=>rateCard(card.front,0)} style={{flex:1,padding:'11px 4px',borderRadius:12,background:'#fee2e2',border:'2px solid #fca5a5',color:'#dc2626',fontWeight:700,fontSize:13}}>😰<br/><span style={{fontSize:11}}>忘了</span></Btn>
                        <Btn onClick={()=>rateCard(card.front,3)} style={{flex:1,padding:'11px 4px',borderRadius:12,background:'#fef9c3',border:'2px solid #fde047',color:'#a16207',fontWeight:700,fontSize:13}}>😐<br/><span style={{fontSize:11}}>普通</span></Btn>
                        <Btn onClick={()=>rateCard(card.front,5)} style={{flex:1,padding:'11px 4px',borderRadius:12,background:'#dcfce7',border:'2px solid #86efac',color:'#15803d',fontWeight:700,fontSize:13}}>😊<br/><span style={{fontSize:11}}>熟悉</span></Btn>
                      </div>
                    </div>
                  )}
                  {/* 上一張 / 下一張（未翻面時顯示）*/}
                  {!cardFlipped && (
                    <div style={{display:'flex',gap:12,width:'100%'}}>
                      <Btn onClick={()=>{setCardIndex(i=>Math.max(0,i-1));setCardFlipped(false);}}
                        disabled={cardIndex===0}
                        style={{flex:1,padding:'12px',borderRadius:12,background:'var(--card)',border:'2px solid var(--border)',color:'var(--text)',fontWeight:600,fontSize:15}}>
                        ← 上一張
                      </Btn>
                      <Btn onClick={()=>{setCardIndex(i=>Math.min(cards.length-1,i+1));setCardFlipped(false);}}
                        disabled={cardIndex===cards.length-1}
                        style={{flex:1,padding:'12px',borderRadius:12,background:'var(--accent)',border:'none',color:'#fff',fontWeight:600,fontSize:15}}>
                        下一張 →
                      </Btn>
                    </div>
                  )}
                  {cardIndex===cards.length-1 && !cardFlipped && (
                    <Btn onClick={()=>{setCardIndex(0);setCardFlipped(false);}}
                      style={{padding:'10px 28px',borderRadius:12,background:'#f0fdf4',border:'2px solid #16a34a',color:'#16a34a',fontWeight:700,fontSize:14}}>
                      🔁 重新開始
                    </Btn>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // ── 設定頁 ────────────────────────────────────────────────────────────────
  if (page==='config') return (
    <div className="page-enter" style={{minHeight:'100vh',background:'var(--bg2)',padding:'20px 16px',display:'flex',flexDirection:'column',alignItems:'center'}}>
      <div style={{maxWidth:440,width:'100%'}}>
        <Btn onClick={()=>setPage(sub.id===ALL_SUB_ID||sub.id===WRONGS_SUB_ID?'home':'topic')} style={{background:'none',color:'#64748b',fontWeight:600,fontSize:14,padding:'0 0 16px'}}>← 返回</Btn>
        <div style={{textAlign:'center',marginBottom:20}}>
          <span style={{background:sbg,border:`1px solid ${clr}30`,borderRadius:20,padding:'5px 14px',fontSize:13,fontWeight:600,color:clr}}>
            {sub.i} {sub.n}{topic?` › ${topicLabel}`:''}
          </span>
          <h2 style={{fontSize:18,fontWeight:700,color:'var(--text)',margin:'12px 0 0'}}>遊戲設定</h2>
        </div>
        {err && <div style={{background:'#fef2f2',border:'1px solid #fca5a5',borderRadius:10,padding:'10px 14px',color:'#dc2626',fontSize:13,marginBottom:16}}>{err}</div>}

        <p style={{fontSize:12,color:'var(--muted)',margin:'0 0 16px',textAlign:'center' as const}}>暱稱與介面主題請到首頁右上角的個人設定修改</p>

        <p style={{fontWeight:700,color:'#374151',fontSize:14,margin:'0 0 8px'}}>遊戲模式</p>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:20}}>
          {[['quiz','🙋','問答模式','四選一選擇題'],['match','🔗','配對模式','概念與答案配對']].map(([v,ic,lbl,desc])=>(
            <Btn key={v} onClick={()=>setMode(v)} className="card-hover"
              style={{background:mode===v?clr:'white',border:`2px solid ${mode===v?clr:'#e2e8f0'}`,borderRadius:14,padding:14,textAlign:'center',transition:'transform 0.2s ease, box-shadow 0.2s ease',boxShadow:mode===v?`0 4px 14px ${clr}40`:'none'}}>
              <div style={{fontSize:22,marginBottom:4}}>{ic}</div>
              <div style={{fontWeight:700,fontSize:14,color:mode===v?'white':'#1e293b'}}>{lbl}</div>
              <div style={{fontSize:12,color:mode===v?'rgba(255,255,255,.75)':'#64748b'}}>{desc}</div>
            </Btn>
          ))}
        </div>
        <Btn onClick={()=>{ setStartFrom(null); startGame(); }} className="card-hover"
          style={{width:'100%',background:`linear-gradient(135deg, ${clr}, ${clr}cc)`,color:'white',borderRadius:14,padding:16,fontWeight:700,fontSize:16,minHeight:54,transition:'transform 0.2s ease, box-shadow 0.2s ease',boxShadow:`0 4px 14px ${clr}40`}}>
          開始遊戲 🚀
        </Btn>
      </div>
    </div>
  );

  // ── 載入頁 ────────────────────────────────────────────────────────────────
  if (page === 'loading') return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',
                 minHeight:'60vh',gap:8}}>
      <div className="loading-spinner" />
      <div className="loading-dots">
        <span/><span/><span/>
      </div>
      <p style={{color:'var(--muted)',fontSize:14,marginTop:8}}>載入題目中…</p>
    </div>
  );

  // ── 問答頁 ────────────────────────────────────────────────────────────────
  if (page==='quiz') {
    const q = qList[qi];
    const answered = qi + (sel!==null?1:0);
    const isWrong = sel!==null && sel!==q.a;
    const isTimeout = sel===-1;
    const RADIUS = 16;
    const CIRC = 2 * Math.PI * RADIUS;
    const limit = timeLimitFor(qList[qi]);
    const timerDash = limit ? (timeLeft / limit) * CIRC : 0;
    const quitQuiz = () => {
      clearAdvance();
      setTimerActive(false);
      if(timerRef.current) clearInterval(timerRef.current);
      lsSet(LS_PROG,null); setSavedProg(null);
      setPaused(false);
      setPage('home'); setSub(null); setTopic('');
    };
    return (
      <React.Fragment>
      <div className="page-enter" style={{minHeight:'100vh',background:'var(--bg2)',padding:'18px 16px',display:'flex',flexDirection:'column',alignItems:'center'}}>
        <div style={{maxWidth:520,width:'100%'}}>
          {strokeLayer}
          {/* Header */}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:8,marginBottom:10}}>
            <div style={{display:'flex',alignItems:'center',gap:4,flex:1,minWidth:0}}>
              <Btn onClick={()=>{
                setTimerActive(false);
                if(timerRef.current) clearInterval(timerRef.current);
                setPaused(p=>!p);
              }} disabled={sel!==null} title="暫停 / 繼續" style={{background:'none',fontSize:18,padding:'2px 6px',color:'var(--muted)',opacity:sel!==null?0.35:1}}>
                {paused?'▶️':'⏸️'}
              </Btn>
              <Btn onClick={async()=>{ if (await confirmDialog('放棄這次作答並回到首頁？')) quitQuiz(); }}
                title="離開作答" aria-label="離開作答" style={{background:'none',fontSize:16,padding:'2px 6px',color:'var(--muted)'}}>
                ✕
              </Btn>
              <Btn onClick={() => handleReport(qList[qi])} title="回報這題的錯誤" aria-label="回報這題的錯誤"
                style={{background:'none',fontSize:16,padding:'2px 6px',color:'var(--muted)'}}>🚩</Btn>
            </div>
            <div style={{display:'flex',gap:6,alignItems:'center',flexShrink:0}}>
              {combo>=2 && <span key={combo} className="combo-badge" style={{background:'#fef3c7',borderRadius:20,padding:'3px 10px',fontSize:12,fontWeight:700,color:'#b45309'}}>🔥{combo}連擊</span>}
              {/* 計時器 SVG (功能二) */}
              {sel===null && limit>0 && (
                <div style={{display:'flex',alignItems:'center',gap:6}}>
                  <svg width="44" height="44" style={{transform:'rotate(-90deg)',flexShrink:0}}>
                    <circle cx="22" cy="22" r={RADIUS} fill="none" stroke="var(--border)" strokeWidth="3"/>
                    <circle cx="22" cy="22" r={RADIUS} fill="none"
                      stroke={timeLeft<=10?'#dc2626':'var(--text)'}
                      strokeWidth="3"
                      strokeDasharray={`${CIRC}`}
                      strokeDashoffset={`${CIRC - timerDash}`}
                      style={{transition:'stroke-dashoffset 1s linear'}}
                    />
                  </svg>
                  <span style={{fontWeight:700,fontSize:13,color:timeLeft<=10?'#dc2626':'var(--muted)',minWidth:24,textAlign:'center'}}>
                    {timeLeft}s
                  </span>
                </div>
              )}
              <span style={{fontWeight:700,color:'var(--text)',fontSize:14,whiteSpace:'nowrap'}}>{qi+1}/{qList.length}</span>
            </div>
          </div>
          {/* 科目與主題（獨立一行，窄螢幕不擠壓標題列） */}
          <div style={{fontSize:12,fontWeight:600,color:clr,marginBottom:8,lineHeight:1.4}}>{sub.n} · {topicLabel}</div>
          {/* Progress bar */}
          <div style={{height:6,background:'var(--border)',borderRadius:3,marginBottom:18,overflow:'hidden'}}>
            <div style={{height:'100%',background:clr,width:`${(qi/qList.length)*100}%`,borderRadius:3,transition:'width .3s'}}/>
          </div>
          {/* Question */}
          <div style={{background:'var(--card)',borderRadius:16,padding:'18px',marginBottom:14,boxShadow:'0 2px 8px rgba(0,0,0,.06)'}}>
            {q.passage && (
              <div style={{fontSize:14,color:'var(--text)',lineHeight:1.7,background:'var(--bg2)',borderLeft:`3px solid ${clr}`,borderRadius:8,padding:'10px 12px',marginBottom:12,maxHeight:260,overflowY:'auto'}}>
                <div style={{fontSize:12,fontWeight:700,color:'var(--muted)',marginBottom:4}}>閱讀文章</div>
                <RichText text={q.passage} />
              </div>
            )}
            <div style={{display:'flex',alignItems:'flex-start',gap:6}}>
              <RichText text={q.q} style={{fontSize:15,fontWeight:600,color:'var(--text)',lineHeight:1.6,margin:0,flex:1,minWidth:0}} />
              {!q.audio && (
                <SpeakBtn active={speakingKey==='quiz'} onClick={()=>toggleSpeak('quiz', [tableToSpeech(q.q ?? ''), ...shuffledOpts.map((o,i)=>`${'ABCD'[i]}. ${o.text}`)].join('。\n'))} />
              )}
            </div>
            {q.img && <img key={q.img} src={q.img} alt="題目圖片" style={{display:'block',maxWidth:'100%',marginTop:12,borderRadius:8,background:'#fff'}} />}
            {q.audio && <audio key={q.audio} src={q.audio} controls autoPlay preload="auto" style={{display:'block',width:'100%',marginTop:12}} />}
          </div>
          {/* Options */}
          <div style={{display:'flex',flexDirection:'column',gap:9,marginBottom:14}}>
            {shuffledOpts.map((opt,i)=>{
              const isCorrect = opt.origIdx===q.a;
              const isSelected = sel===opt.origIdx;
              let bg2='white',border2='#e2e8f0',c2='#1e293b';
              if (sel!==null) {
                if (isCorrect){bg2='#f0fdf4';border2='#16a34a';c2='#15803d';}
                else if (isSelected&&sel!==-1){bg2='#fef2f2';border2='#dc2626';c2='#dc2626';}
              }
              return (
                <Btn key={opt.origIdx} onClick={()=>handleQuizSel(opt.origIdx)} disabled={sel!==null}
                  style={{background:bg2,border:`2px solid ${border2}`,borderRadius:12,padding:'13px 16px',textAlign:'left',color:c2,fontWeight:500,fontSize:14,transition:'all .15s',display:'flex',justifyContent:'space-between',alignItems:'center',opacity:sel!==null&&!isCorrect&&!isSelected?.5:1}}
                  className={sel===null?'ans-btn':(isCorrect?'flash-correct':(isSelected&&sel!==-1?'flash-wrong':undefined))}>
                  <span><span style={{fontWeight:700,marginRight:8}}>{'ABCD'[i]}.</span>{opt.text}</span>
                  {sel!==null&&isCorrect&&<span style={{color:'#16a34a'}}>✓</span>}
                  {sel!==null&&isSelected&&!isCorrect&&sel!==-1&&<span style={{color:'#dc2626'}}>✗</span>}
                </Btn>
              );
            })}
          </div>
          {/* 解析 + 繼續按鈕 (功能一) */}
          {showExp && (
            <div style={{background:isTimeout?'#fef3c7':isWrong?'#fef2f2':'#f0fdf4',border:`1px solid ${isTimeout?'#fde047':isWrong?'#fca5a5':'#86efac'}`,borderRadius:12,padding:'12px 16px',marginBottom:14}}>
              <p style={{fontWeight:700,color:isTimeout?'#92400e':isWrong?'#dc2626':'#15803d',margin:'0 0 4px',fontSize:13}}>
                {isTimeout?'⏰ 時間到！':isWrong?'✗ 答錯了':'✓ 答對了！'}
              </p>
              <div style={{color:'#475569',fontSize:13,margin:'0 0 12px',lineHeight:1.5,display:'flex',gap:4}}><span>📖</span><RichText text={explanationOf(q)} style={{flex:1,minWidth:0}} /></div>
              {/* 自動跳題倒數：需要多看一下時可暫停，暫停後改為手動按繼續 */}
              {advanceIn !== null && (
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10,fontSize:13,color:'#475569'}}>
                  <span style={{flex:1}}>⏳ {advanceIn} 秒後自動下一題</span>
                  <Btn onClick={clearAdvance}
                    style={{fontSize:13,padding:'5px 12px',borderRadius:8,background:'white',border:'1px solid #cbd5e1',color:'#1e293b',fontWeight:600}}>
                    ⏸ 暫停
                  </Btn>
                </div>
              )}
              {sel !== null && (
                <Btn onClick={()=>nextQuestion(qi)}
                  style={{width:'100%',background:'#1e293b',color:'white',borderRadius:10,padding:'11px',fontWeight:700,fontSize:14}}>
                  ✅ 我已了解，繼續下一題
                </Btn>
              )}
            </div>
          )}
          {showExp && (
            <div style={{marginTop:8,textAlign:'right'}}>
              {strokeOn(q.ref?.s ?? sub?.id) && (
                <Btn onClick={() => { clearAdvance(); setStrokeText([q.q, ...(q.o ?? []), q.e].join('')); }}
                  style={{fontSize:12,color:'var(--muted)',background:'none',padding:'4px 8px',border:'1px solid var(--border)',borderRadius:6,marginRight:6}}
                  title="看這題文字的筆順">✍️ 筆順</Btn>
              )}
              <Btn
                onClick={() => handleReport(qList[qi])}
                style={{fontSize:12,color:'var(--muted)',background:'none',padding:'4px 8px',border:'1px solid var(--border)',borderRadius:6}}
                title="回報此題目的錯誤"
              >🚩 回報錯誤</Btn>
            </div>
          )}
          <div style={{display:'flex',justifyContent:'center',gap:20,marginTop:4,color:'#64748b',fontSize:12}}>
            <span>✅ 答對 {score} 題</span><span>❌ 答錯 {Math.max(0,answered-score)} 題</span>
          </div>
        </div>
      </div>

      {/* 暫停 Overlay */}
      {paused && sel===null && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.65)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:16,zIndex:999}}>
          <div style={{fontSize:52}}>⏸️</div>
          <p style={{color:'white',fontWeight:700,fontSize:20,margin:0}}>遊戲暫停中</p>
          <Btn onClick={()=>{
            setPaused(false);
            setTimerActive(true);
          }} style={{background:clr,color:'white',borderRadius:14,padding:'14px 36px',fontWeight:700,fontSize:16}}>
            ▶️ 繼續作答
          </Btn>
          <Btn onClick={quitQuiz} style={{background:'#fef2f2',color:'#dc2626',border:'1px solid #fca5a5',borderRadius:14,padding:'12px 32px',fontWeight:700,fontSize:15}}>
            🏠 放棄並回首頁
          </Btn>
        </div>
      )}
      </React.Fragment>
    );
  }

  // ── 配對頁 ────────────────────────────────────────────────────────────────
  if (page==='match') {
    const total=lItems.length, done=matched.size;
    return (
      <div className="page-enter" style={{minHeight:'100vh',background:'var(--bg2)',padding:'18px 16px',display:'flex',flexDirection:'column',alignItems:'center'}}>
        <div style={{maxWidth:600,width:'100%'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <span style={{background:sbg,border:`1px solid ${clr}30`,borderRadius:20,padding:'3px 12px',fontSize:12,fontWeight:600,color:clr}}>{sub.i} {sub.n} · {topicLabel}</span>
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              <span style={{fontWeight:700,color:'#1e293b',fontSize:14}}>{done}/{total} 已配對</span>
              <Btn onClick={()=>{
                lsSet(LS_PROG,null);
                setPage('home'); setSub(null); setTopic('');
              }} title="放棄並回首頁" style={{background:'none',fontSize:14,color:'var(--muted)',padding:'2px 6px'}}>🏠</Btn>
            </div>
          </div>
          <div style={{height:6,background:'#e2e8f0',borderRadius:3,marginBottom:12,overflow:'hidden'}}>
            <div style={{height:'100%',background:clr,width:`${(done/total)*100}%`,borderRadius:3,transition:'width .3s'}}/>
          </div>
          <p style={{textAlign:'center',color:'#64748b',fontSize:13,marginBottom:14}}>點左側概念 → 點右側對應答案</p>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:isMobile ? 8 : 12}}>
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {lItems.map((item,i)=>{
                const isDone=matched.has(item.idx),isSel=selL===i,isFlash=flashL===i;
                return (
                  <Btn key={i} onClick={()=>handleMatchL(i)} disabled={isDone}
                    style={{background:isDone?'#f0fdf4':isFlash?'#fef2f2':isSel?clr:'white',border:`2px solid ${isDone?'#86efac':isFlash?'#dc2626':isSel?clr:'#e2e8f0'}`,borderRadius:10,padding:isMobile?'8px 6px':'10px 8px',textAlign:'center',fontSize:isMobile?12:13,fontWeight:600,color:isDone?'#16a34a':isSel?'white':'#1e293b',minHeight:isMobile?44:48,display:'flex',alignItems:'center',justifyContent:'center',wordBreak:'break-word',lineHeight:1.35,transition:'all .15s'}}>
                    {isDone&&'✓ '}{item.txt}
                  </Btn>
                );
              })}
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {rItems.map((item,i)=>{
                const isDone=matched.has(item.idx),isFlash=flashR===i,canSel=selL!==null&&!isDone;
                return (
                  <Btn key={i} onClick={()=>handleMatchR(i)} disabled={isDone}
                    style={{background:isDone?'#f0fdf4':isFlash?'#fef2f2':canSel?'#eff6ff':'white',border:`2px solid ${isDone?'#86efac':isFlash?'#dc2626':canSel?'#93c5fd':'#e2e8f0'}`,borderRadius:10,padding:isMobile?'8px 6px':'10px 8px',textAlign:'center',fontSize:isMobile?12:13,fontWeight:500,color:isDone?'#16a34a':isFlash?'#dc2626':'#1e293b',minHeight:isMobile?44:48,display:'flex',alignItems:'center',justifyContent:'center',wordBreak:'break-word',lineHeight:1.35,transition:'all .15s'}}>
                    {isDone&&'✓ '}{item.txt}
                  </Btn>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── 結果頁 ────────────────────────────────────────────────────────────────
  if (page==='result') {
    const total = mode==='quiz'?qList.length:lItems.length;
    const sc    = mode==='quiz'?score:lItems.length;
    const pct   = Math.round((sc/total)*100);
    const grade = pct>=90?'🏆':pct>=75?'🥇':pct>=60?'👍':'💪';
    const msg   = pct>=90?'非常優秀！':pct>=75?'答得不錯！':pct>=60?'繼續加油！':'再接再厲！';
    const scoreClr = pct>=75?'#16a34a':pct>=60?'#d97706':'#dc2626';

    const shareScore = async () => {
      const W = 600, H = 360;
      const canvas = document.createElement('canvas');
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext('2d')!;

      // Background
      const bg = ctx.createLinearGradient(0, 0, W, H);
      bg.addColorStop(0, '#f8fafc');
      bg.addColorStop(1, pct>=75?'#dcfce7':pct>=60?'#fef9c3':'#fee2e2');
      ctx.fillStyle = bg;
      ctx.roundRect(0, 0, W, H, 20);
      ctx.fill();

      // Top accent bar
      ctx.fillStyle = scoreClr;
      ctx.roundRect(0, 0, W, 6, [20, 20, 0, 0]);
      ctx.fill();

      // App name
      ctx.fillStyle = '#94a3b8';
      ctx.font = '500 14px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('🎮 GSAT 題庫練習', W/2, 36);

      // Grade emoji + pct
      ctx.font = '52px system-ui, sans-serif';
      ctx.fillText(grade, W/2, 110);
      ctx.fillStyle = scoreClr;
      ctx.font = '800 72px system-ui, sans-serif';
      ctx.fillText(`${pct}%`, W/2, 190);

      // Message
      ctx.fillStyle = '#1e293b';
      ctx.font = '700 22px system-ui, sans-serif';
      ctx.fillText(msg, W/2, 225);

      // Subject · topic
      ctx.fillStyle = '#64748b';
      ctx.font = '500 15px system-ui, sans-serif';
      ctx.fillText(`${sub.i} ${sub.n}  ·  ${topicLabel}`, W/2, 258);

      // Stats
      const stats: string[] = [];
      if (mode==='quiz') stats.push(`答對 ${sc}/${total} 題`);
      if (mode==='quiz' && maxC>1) stats.push(`🔥 最高連擊 ${maxC}`);
      if (mode==='match') stats.push(`錯誤 ${mWrong} 次`);
      ctx.fillStyle = '#475569';
      ctx.font = '600 15px system-ui, sans-serif';
      ctx.fillText(stats.join('   '), W/2, 290);

      // Bottom divider + branding
      ctx.strokeStyle = '#e2e8f0';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(40, 316); ctx.lineTo(W-40, 316); ctx.stroke();
      ctx.fillStyle = '#cbd5e1';
      ctx.font = '400 12px system-ui, sans-serif';
      ctx.fillText('gsat-game.pages.dev', W/2, 340);

      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const file = new File([blob], 'gsat-score.png', { type: 'image/png' });
        if (navigator.canShare?.({ files: [file] })) {
          try { await navigator.share({ files: [file], title: `GSAT 成績 ${pct}%`, text: `${sub.n} ${topicLabel} — ${pct}% ${msg}` }); return; } catch {}
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'gsat-score.png';
        a.click(); setTimeout(() => URL.revokeObjectURL(url), 5000);
      }, 'image/png');
    };

    return (
      <div className="page-enter" style={{minHeight:'100vh',background:'var(--bg2)',padding:'22px 16px',display:'flex',flexDirection:'column',alignItems:'center'}}>
        <div style={{maxWidth:460,width:'100%'}}>
          <div style={{background:'var(--card)',borderRadius:20,padding:'26px 22px',textAlign:'center',boxShadow:'0 4px 20px rgba(0,0,0,.08)',marginBottom:18}}>
            <div style={{fontSize:52,marginBottom:8}}>{grade}</div>
            <h2 style={{fontSize:20,fontWeight:700,color:'var(--text)',margin:'0 0 4px'}}>{msg}</h2>
            <p style={{color:'#64748b',fontSize:13,margin:'0 0 20px'}}>{sub.i} {sub.n} · {topicLabel}</p>
            <div style={{display:'flex',justifyContent:'center',alignItems:'center',gap:28,marginBottom:6,flexWrap:'wrap'}}>
              {/* 正確率圓環 */}
              <svg width="120" height="120" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="48" fill="none" stroke="var(--border)" strokeWidth="11"/>
                <circle cx="60" cy="60" r="48" fill="none"
                  stroke={pct>=75?'#16a34a':pct>=60?'#d97706':'#dc2626'} strokeWidth="11"
                  strokeLinecap="round"
                  strokeDasharray={`${2*Math.PI*48*displayPct/100} ${2*Math.PI*48}`}
                  transform="rotate(-90 60 60)"
                  style={{transition:'stroke-dasharray 0.3s ease'}}/>
                <text x="60" y="68" textAnchor="middle" fontSize="24" fontWeight="800" fill="var(--text)">{displayPct}%</text>
              </svg>
              <div style={{display:'flex',flexDirection:'column',gap:10,textAlign:'left'}}>
                {mode==='quiz'&&<div><div style={{fontSize:26,fontWeight:800,color:clr}}>{sc}/{total}</div><div style={{color:'#64748b',fontSize:12}}>答對題數</div></div>}
                {mode==='quiz'&&maxC>1&&<div><div style={{fontSize:26,fontWeight:800,color:'#b45309'}}>🔥{maxC}</div><div style={{color:'#64748b',fontSize:12}}>最高連擊</div></div>}
                {mode==='match'&&<div><div style={{fontSize:26,fontWeight:800,color:mWrong>0?'#dc2626':'#16a34a'}}>{mWrong}</div><div style={{color:'#64748b',fontSize:12}}>錯誤次數</div></div>}
              </div>
            </div>
          </div>
          {/* 全部主題：各主題答對率 */}
          {mode==='quiz' && topic==='ALL' && sub.id!==ALL_SUB_ID && sub.id!==WRONGS_SUB_ID && (() => {
            // 題目 ID 只在主題內唯一（各主題都有 q01…），以「主題/ID」比對
            const wrongIds = new Set(wrongs.map(w => `${(w as QItem).ref?.t}/${(w as QItem).ref?.id}`));
            const rows = (allTopics[sub.id]||[]).map(t => {
              const qs = qList.filter(q => q.ref?.t === t);
              const ok = qs.filter(q => !wrongIds.has(`${q.ref?.t}/${q.ref?.id}`)).length;
              return { t, n: qs.length, ok, pct: qs.length ? Math.round(ok / qs.length * 100) : 0 };
            }).filter(r => r.n > 0).sort((a, b) => a.pct - b.pct);
            if (rows.length < 2) return null;
            return (
              <div style={{background:'var(--card)',borderRadius:14,padding:'14px 16px',marginBottom:14,border:'1px solid var(--border)'}}>
                <div style={{fontWeight:700,fontSize:14,color:'var(--text)',marginBottom:8}}>📊 各主題答對率（低 → 高）</div>
                {rows.map(r => (
                  <div key={r.t} style={{display:'flex',alignItems:'center',gap:8,fontSize:13,padding:'5px 0',borderTop:'1px solid var(--border)'}}>
                    <span style={{flex:1,minWidth:0,color:'var(--text)'}}>{topicNameOf(sub.id, r.t)}</span>
                    <span style={{color:'var(--muted)',flexShrink:0}}>{r.ok}/{r.n}</span>
                    <span style={{width:40,textAlign:'right',fontWeight:700,flexShrink:0,color:r.pct>=80?'#16a34a':r.pct>=60?'#ca8a04':'#dc2626'}}>{r.pct}%</span>
                  </div>
                ))}
              </div>
            );
          })()}
          {/* 答錯題目回顧 */}
          {mode==='quiz'&&wrongs.length>0&&(
            <div style={{background:'white',borderRadius:16,padding:'16px',marginBottom:16,boxShadow:'0 2px 8px rgba(0,0,0,.05)'}}>
              <h3 style={{fontWeight:700,color:'#dc2626',fontSize:14,margin:'0 0 12px'}}>❌ 答錯的題目（{wrongs.length} 題）</h3>
              {wrongs.map((q,i)=>(
                <div key={i} style={{borderTop:i>0?'1px solid #f1f5f9':'none',paddingTop:i>0?10:0,marginTop:i>0?10:0}}>
                  {q.passage && (
                    <details style={{fontSize:12,color:'#475569',margin:'0 0 4px'}}>
                      <summary style={{cursor:'pointer'}}>閱讀文章</summary>
                      <RichText text={q.passage} style={{lineHeight:1.6,marginTop:4}} />
                    </details>
                  )}
                  <RichText text={q.q} style={{fontWeight:600,color:'#1e293b',fontSize:13,margin:'0 0 3px'}} />
                  {q.img && <img key={q.img} src={q.img} alt="題目圖片" style={{display:'block',maxWidth:'100%',margin:'4px 0',borderRadius:6}} />}
                  {q.audio && <audio src={q.audio} controls preload="none" style={{display:'block',width:'100%',margin:'4px 0'}} />}
                  <p style={{color:'#16a34a',fontSize:12,margin:'0 0 2px'}}>✓ {(q.o||[])[q.a??0]}</p>
                  <div style={{color:'#64748b',fontSize:12,margin:0,display:'flex',gap:4}}><span>💡</span><RichText text={explanationOf(q)} style={{flex:1,minWidth:0}} /></div>
                </div>
              ))}
            </div>
          )}
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            <Btn onClick={()=>sub.id===WRONGS_SUB_ID ? startGame(qList) : startGame()} className="card-hover" style={{background:`linear-gradient(135deg, ${clr}, ${clr}cc)`,color:'white',borderRadius:12,padding:14,fontWeight:700,fontSize:15,transition:'transform 0.2s ease, box-shadow 0.2s ease'}}>🔄 再玩一次</Btn>
            <Btn onClick={shareScore} style={{background:'white',color:'#0369a1',border:'2px solid #bae6fd',borderRadius:12,padding:14,fontWeight:700,fontSize:14}}>📸 分享成績圖片</Btn>
            {/* 複習錯題 (功能五) */}
            {mode==='quiz'&&wrongs.length>0&&(
              <Btn onClick={()=>{
                setMode('quiz');
                startGame(wrongs);
              }} style={{background:'#fff1f2',color:'#dc2626',border:'2px solid #fca5a5',borderRadius:12,padding:14,fontWeight:700,fontSize:14}}>
                🔁 複習這次答錯的題目（{wrongs.length} 題）
              </Btn>
            )}
            <Btn onClick={()=>setPage('config')} style={{background:'white',color:'#1e293b',border:'2px solid #e2e8f0',borderRadius:12,padding:14,fontWeight:600,fontSize:14}}>⚙️ 更換設定</Btn>
            <Btn onClick={()=>{setPage('home');setSub(null);setTopic('');}} style={{background:'none',color:'#64748b',borderRadius:12,padding:10,fontWeight:600,fontSize:13}}>🏠 回到主頁</Btn>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
